import React, { useState, useEffect, useRef, useCallback } from 'react';
import logo from './filezensvg.svg';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { downloadEnhancedFile } from './enhanced-download';
import jsPDF from 'jspdf';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import SEOHead from './components/SEOHead';
import {
  ChevronRightIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CloudArrowUpIcon, // v2: CloudUploadIcon renamed to CloudArrowUpIcon
  CreditCardIcon,
  UserIcon,
  Cog6ToothIcon, // v2: CogIcon renamed to Cog6ToothIcon
  ArrowRightOnRectangleIcon, // v2: LogoutIcon renamed to ArrowRightOnRectangleIcon
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ShareIcon,
  GiftIcon,
  CurrencyDollarIcon,
  PlayIcon,
  SparklesIcon,
  PresentationChartBarIcon,
  DocumentDuplicateIcon,
  PencilSquareIcon, // v2: PencilAltIcon renamed to PencilSquareIcon
  ArrowLeftIcon,
  HomeIcon,
  QuestionMarkCircleIcon,
  ChatBubbleLeftEllipsisIcon, // v2: ChatAltIcon renamed to ChatBubbleLeftEllipsisIcon
  ShieldCheckIcon,
  DocumentCheckIcon, // v2: DocumentIcon renamed to DocumentCheckIcon
  InformationCircleIcon,
  PhoneIcon,
  EnvelopeIcon, // v2: MailIcon renamed to EnvelopeIcon
  ChevronDownIcon,
  ArrowDownTrayIcon, // v2: ArrowDownTrayIcon renamed to ArrowDownTrayIcon
  PlusIcon,
  PlusCircleIcon,
  CalendarIcon,
  ArrowRightIcon,
  ArrowPathIcon, // v2: ArrowPathIcon renamed to ArrowPathIcon
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import './App.css';
import { sendEmailVerification, onAuthStateChanged } from 'firebase/auth';
import { claimSignupBonus } from './firebase/functions';
import { createSimpleStripePayment, checkPaymentAndAddCredits } from './firebase/simplePayment';
import { ALL_LANG_OPTIONS, getRelevantUpsells, getDefaultUserProfile, calculateUpsellStats } from './utils/upsell';
import { detectUserRegion } from './utils/geo';
import { applyUpsellToJob } from './firebase/firestore';

// Import Firebase services
import {
  auth,
  registerUser,
  loginUser,
  logoutUser,
  getCurrentUser,
  uploadFile,
  createAIJob,
  getJobStatus,
  getUserJobs,
  getUserData,
  getUserReferrals,
  getUserTransactions,
  createStripeCheckoutSession,
  createRazorpayOrder,
  verifyRazorpayPayment,
  getCreditPackages,
  getJobTypes,
  getStorage,
  ref,
  getDownloadURL,
  uploadBytes,
  updateDoc,
  doc,
  serverTimestamp,
  db,
  processExcelWithGemini,
  processDocumentWithGemini,
  processFileWithAI,
  addDoc,
  collection,
  getUserHistory,
  FieldValue,
  getDoc,
  getDocs,
  query,
  where,
  functions,
  httpsCallable,
  cancelPayment,
  calculateCreditsRequired
} from './firebase';

// Import API keys
import { RAZORPAY_KEY_ID } from './config/keys';
import { createPromoCode, listPromoCodes, redeemPromoCode, grantCreditsToUserByEmail } from './firebase/promoCodes';
import { createManagedUser, listManagedUsers, deleteManagedUser, findUserByEmail, grantAdminAccess, revokeAdminAccess, adminCreateUserWithPassword } from './firebase/userManagement';
import { getTopUsersByJobs, getTopReferrers, getGlobalAnalytics, logPageView, logUserLogin, logUserSignup, logFileAction, logJobAction, logPurchaseEvent } from './firebase/analytics';
import { createLandingContent, listLandingContent, deleteLandingContent, updateLandingContent, listApprovedFeedback, approveFeedback, deleteFeedback, createFeedback, listAllFeedback, updateFeedback } from './firebase/landing';

// API URL
const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// EU country codes for GDPR consent handling
const EU_COUNTRY_CODES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
]);

async function detectCountryCode() {
  try {
    // Try multiple IP detection services with fallback
    const services = [
      'https://ipapi.co/json/',
      'https://api.ipify.org?format=json',
      'https://ipinfo.io/json'
    ];

    for (const service of services) {
      try {
        console.log(`Trying IP detection service: ${service}`);
        const res = await fetch(service, {
          timeout: 3000,
          mode: 'cors'
        });

        if (res.ok) {
          const data = await res.json();
          console.log(`IP detection successful with ${service}:`, data);

          // Handle different response formats
          if (data.country_code) {
            return data.country_code.toUpperCase();
          } else if (data.country) {
            return data.country.toUpperCase();
          } else if (data.ip) {
            // If we only get IP, use a fallback
            console.log('Only IP received, using fallback country detection');
            return 'US';
          }
        }
      } catch (serviceError) {
        console.log(`Service ${service} failed:`, serviceError.message);
        continue;
      }
    }

    console.log('All IP detection services failed, using fallback');

    // Use default fallback for all environments
    console.log('Using default country detection');
    return 'US';

    return 'US';
  } catch (e) {
    console.error('IP detection completely failed:', e);
    return 'US';
  }
}

// Initialize Stripe
let stripePromise = null;
try {
  if (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  }
} catch (error) {
  console.warn('Stripe initialization failed:', error);
}

// Auth Context
const AuthContext = React.createContext();

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log("BOARDING: Setting up persistent auth listener");
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          console.log("👤 Auth State: User detected:", firebaseUser.email);
          // Reuse getCurrentUser to handle profile fetching, creation, and referral logic consistency
          const userData = await getCurrentUser();
          console.log("✅ Auth State: User data loaded via helper");
          setUser(userData);
        } else {
          console.log("👋 Auth State: No user signed in");
          setUser(null);
        }
      } catch (error) {
        console.error("❌ Auth State Error:", error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      console.log("🚀 Login attempt:", email);
      const response = await loginUser(email, password);
      console.log("✅ Login successful:", response.user.email);
      logUserLogin('email');
      setUser(response.user);
      toast.success('Welcome back!');
      return response.user;
    } catch (error) {
      console.error("❌ Login failed:", error.message);
      toast.error(error.message);
      throw new Error(error.message);
    }
  };

  const register = async (email, display_name, password, referral_code) => {
    try {
      console.log("🚀 Registration attempt:", email);
      const response = await registerUser(email, display_name, password, referral_code);
      console.log("✅ Registration successful:", response.user.email);
      logUserSignup('email');
      setUser(response.user);
      toast.success('Welcome to FileZen!');
      return response.user;
    } catch (error) {
      console.error("❌ Registration failed:", error.message);
      toast.error(error.message);
      throw new Error(error.message);
    }
  };

  const logout = async () => {
    try {
      await logoutUser();
      setUser(null);
      toast.success('Logged out successfully');
      // Redirect to homepage after logout
      window.location.href = '/';
    } catch (error) {
      console.error('❌ Error logging out:', error);
      toast.error('Failed to logout');
    }
  };

  const refreshUser = async () => {
    try {
      console.log("🔄 Refreshing user data...");
      const userData = await getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('❌ Error refreshing user:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

const useAuth = () => React.useContext(AuthContext);

// Navigation Component with Back Button
function NavigationBreadcrumb() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const getPageInfo = (pathname) => {
    switch (pathname) {
      case '/':
        return { title: 'Home', showBack: false };
      case '/login':
        return { title: 'Sign In', showBack: true, backTo: '/' };
      case '/dashboard':
        return { title: 'Dashboard', showBack: false };
      case '/history':
        return { title: 'History', showBack: true, backTo: '/dashboard' };
      case '/referrals':
        return { title: 'Referrals', showBack: true, backTo: '/dashboard' };
      case '/pricing':
        return { title: 'Pricing', showBack: user ? true : false, backTo: user ? '/dashboard' : '/' };
      case '/help':
        return { title: 'Help Center', showBack: true, backTo: user ? '/dashboard' : '/' };
      case '/privacy':
        return { title: 'Privacy Policy', showBack: true, backTo: '/' };
      case '/terms':
        return { title: 'Terms of Service', showBack: true, backTo: '/' };
      case '/contact':
        return { title: 'Contact Us', showBack: true, backTo: user ? '/dashboard' : '/' };
      case '/refund':
        return { title: 'Refund Policy', showBack: true, backTo: '/' };
      case '/cookies':
        return { title: 'Cookie Policy', showBack: true, backTo: '/' };
      case '/payment-success':
        return { title: 'Payment Success', showBack: true, backTo: '/dashboard' };
      case '/payment-cancel':
        return { title: 'Payment Canceled', showBack: true, backTo: '/dashboard' };
      default:
        return { title: 'FileZen', showBack: false };
    }
  };

  const pageInfo = getPageInfo(location.pathname);

  if (!pageInfo.showBack && location.pathname === '/') {
    return null;
  }

  return (
    <div className="bg-gray-50 border-b px-4 py-2">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {pageInfo.showBack && (
            <button
              onClick={() => navigate(pageInfo.backTo)}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back
            </button>
          )}

          {user && location.pathname !== '/dashboard' && (
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              <HomeIcon className="h-4 w-4 mr-1" />
              Dashboard
            </button>
          )}
        </div>

        <h1 className="text-lg font-semibold text-gray-900">{pageInfo.title}</h1>

        <div className="flex items-center space-x-2">
          <Link
            to="/help"
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <QuestionMarkCircleIcon className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Help</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

// Chatbot Component
function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      message: 'Hi! I\'m here to help you with FileZen. What would you like to know?',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');

  const quickQuestions = [
    'How do credits work?',
    'What file types are supported?',
    'How does AI processing work?',
    'What are the pricing options?',
    'How do referrals work?'
  ];

  const getResponse = (message) => {
    const lowerMessage = message.toLowerCase();

    if (lowerMessage.includes('credit')) {
      return 'Credits are used to pay for AI processing. You start with 100 free credits. Different processing types cost different amounts: Excel formatting (5 credits), Data analysis (10 credits), Dashboard creation (15 credits), Document enhancements (8-12 credits).';
    } else if (lowerMessage.includes('file') || lowerMessage.includes('support')) {
      return 'Accepted formats are MS Word, PDF documents and MS excel files. Maximum file size is 50MB.';
    } else if (lowerMessage.includes('ai') || lowerMessage.includes('processing')) {
      return 'Our AI analyzes your documents using Google Gemini. For Excel files, we offer formatting, analysis, and dashboard creation. For documents, we provide proofreading, business enhancement, blog optimization, and academic improvements.';
    } else if (lowerMessage.includes('price') || lowerMessage.includes('cost')) {
      return 'We offer flexible pricing: pay-as-you-go with credits, preset packages from 50-2500 credits, or guest processing. Prices are region-based (₹ for India, $ for others).';
    } else if (lowerMessage.includes('referral')) {
      return 'Refer friends and earn 25 credits for each signup! Your friends get 50 bonus credits too. Find your referral link in the Referrals page.';
    } else if (lowerMessage.includes('help') || lowerMessage.includes('how')) {
      return 'Visit our Help Center for detailed guides, or check these quick topics: credits, file uploads, AI processing, payments, referrals. What specific area would you like help with?';
    } else {
      return 'I can help you with credits, file uploads, AI processing, pricing, referrals, and general usage. Try asking about any of these topics, or visit our Help Center for detailed guides.';
    }
  };

  const sendMessage = (messageText = inputMessage) => {
    if (!messageText.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      message: messageText,
      timestamp: new Date()
    };

    const botResponse = {
      id: Date.now() + 1,
      type: 'bot',
      message: getResponse(messageText),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage, botResponse]);
    setInputMessage('');
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 bg-indigo-600 text-white p-4 rounded-full shadow-lg hover:bg-indigo-700 transition-colors z-50"
      >
        <ChatBubbleLeftEllipsisIcon className="h-6 w-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 w-80 bg-white rounded-lg shadow-xl border z-50">
      <div className="flex items-center justify-between p-4 bg-indigo-600 text-white rounded-t-lg">
        <h3 className="font-semibold">FileZen Assistant</h3>
        <button
          onClick={() => setIsOpen(false)}
          className="text-white hover:text-gray-200"
        >
          <XCircleIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="h-64 overflow-y-auto p-4 space-y-3">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`${msg.type === 'user'
              ? 'ml-auto bg-indigo-600 text-white'
              : 'bg-gray-100 text-gray-800'
              } p-2 rounded-lg max-w-xs text-sm`}
          >
            {msg.message}
          </div>
        ))}
      </div>

      <div className="p-3 border-t">
        <div className="flex flex-wrap gap-1 mb-2">
          {quickQuestions.map((question, index) => (
            <button
              key={index}
              onClick={() => sendMessage(question)}
              className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded hover:bg-gray-200 transition-colors"
            >
              {question}
            </button>
          ))}
        </div>
        <div className="flex">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Ask me anything..."
            className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
          <button
            onClick={() => sendMessage()}
            className="bg-indigo-600 text-white px-4 py-2 rounded-r-md hover:bg-indigo-700 transition-colors"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal Component
function Modal({ isOpen, onClose, children }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-end mb-4">
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircleIcon className="h-6 w-6" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Credit Purchase Modal
function CreditPurchaseModal({ isOpen, onClose, onPurchase }) {
  const [packages, setPackages] = useState([]);
  const [customCredits, setCustomCredits] = useState('');
  const [customPrice, setCustomPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const { user, refreshUser } = useAuth();
  const [userCountry, setUserCountry] = useState('US');
  const [currency, setCurrency] = useState('USD');
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const [paymentProvider, setPaymentProvider] = useState('stripe');

  useEffect(() => {
    if (isOpen) {
      fetchPackages();
    }
  }, [isOpen]);

  const fetchPackages = async () => {
    try {
      console.log("Fetching credit packages for modal");

      // Enhanced country detection using ipapi.co
      let detectedCountry = 'US';
      let detectedCurrency = 'USD';
      let detectedSymbol = '$';
      let detectedProvider = 'stripe';

      try {
        console.log("Detecting user location using ipapi.co for modal...");
        const geoResponse = await axios.get('https://ipapi.co/json/', {
          timeout: 5000
        });

        const geoData = geoResponse.data;
        console.log("Modal GeoIP data received:", geoData);

        detectedCountry = geoData.country_code || 'US';
        detectedCurrency = geoData.currency || 'USD';
        detectedSymbol = geoData.currency_symbol || '$';

        console.log(`🌍 Modal - Detected country: ${detectedCountry}`);
        console.log(`💱 Modal - Currency: ${detectedCurrency} (${detectedSymbol})`);
        console.log(`💳 Modal - Payment provider: ${detectedProvider}`);

        setUserCountry(detectedCountry);
        setCurrency(detectedCurrency);
        setCurrencySymbol(detectedSymbol);
        setPaymentProvider(detectedProvider);

      } catch (geoError) {
        console.error("Error detecting location in modal:", geoError);
        console.log("Using fallback country detection in modal...");
      }

      // Use frontend country detection as primary source for modal
      console.log("Using frontend country detection as primary source for modal");

      // Create packages based on detected country
      const isIndia = detectedCountry === 'IN';
      const finalCurrency = isIndia ? 'INR' : 'USD';
      const finalSymbol = isIndia ? '₹' : '$';
      const finalProvider = isIndia ? 'razorpay' : 'stripe';

      const packageData = {
        packages: [
          {
            package_id: "starter",
            credits: 50,
            price: isIndia ? 249 : 5.00,
            currency: finalCurrency,
            symbol: finalSymbol,
            description: "Starter Pack",
            validity: "3 months",
            recommended: false
          },
          {
            package_id: "small",
            credits: 100,
            price: isIndia ? 500 : 10.00,
            currency: finalCurrency,
            symbol: finalSymbol,
            description: "Small Pack",
            validity: "3 months",
            recommended: false
          },
          {
            package_id: "medium",
            credits: 250,
            price: isIndia ? 1000 : 22.00,
            currency: finalCurrency,
            symbol: finalSymbol,
            description: "Popular Pack",
            validity: "3 months",
            recommended: true
          },
          {
            package_id: "large",
            credits: 500,
            price: isIndia ? 1800 : 40.00,
            currency: finalCurrency,
            symbol: finalSymbol,
            description: "Pro Pack",
            validity: "6 months",
            recommended: false
          },
          {
            package_id: "enterprise",
            credits: 1000,
            price: isIndia ? 3200 : 75.00,
            currency: finalCurrency,
            symbol: finalSymbol,
            description: "Enterprise Pack",
            validity: "6 months",
            recommended: false
          },
          {
            package_id: "mega",
            credits: 2500,
            price: isIndia ? 7499 : 150.00,
            currency: finalCurrency,
            symbol: finalSymbol,
            description: "Mega Pack",
            validity: "6 months",
            recommended: false
          }
        ]
      };

      console.log("Created packages for modal based on frontend detection:", packageData);

      setPackages(packageData.packages);
      setPaymentProvider(finalProvider);
      setUserCountry(detectedCountry);
      setCurrency(finalCurrency);
      setCurrencySymbol(finalSymbol);
    } catch (error) {
      console.error("Error fetching packages for modal:", error);
      toast.error('Failed to load pricing packages');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomCreditsChange = async (credits) => {
    setCustomCredits(credits);

    if (credits && credits >= 10 && credits <= 10000) {
      try {
        // Calculate price client-side based on detected currency
        const price = calculateCustomCreditPrice(parseInt(credits));

        // Validate minimum price requirements
        const minCreditsForUSD = Math.ceil((0.50 * 100) / 10.00); // Minimum $0.50 USD
        const minCreditsForINR = Math.ceil((10 * 100) / 500); // Minimum ₹10 INR

        const minCredits = userCountry === 'IN' ? minCreditsForINR : minCreditsForUSD;

        if (parseInt(credits) < minCredits) {
          setCustomPrice(null);
          return;
        }

        setCustomPrice({
          credits: parseInt(credits),
          price: price,
          currency: currency,
          symbol: currencySymbol
        });
      } catch (error) {
        console.error("Error calculating custom price:", error);
        setCustomPrice(null);
      }
    } else {
      setCustomPrice(null);
    }
  };

  const calculateCustomCreditPrice = (credits) => {
    // Base rate: 100 credits = $5.99 USD or ₹500 INR
    if (userCountry === 'IN') {
      return Math.round((credits * 500) / 100); // Round to whole number for INR
    } else {
      return Math.round((credits * 10.00) / 100 * 100) / 100; // Round to 2 decimal places for USD
    }
  };

  const handlePurchase = async (packageId = null, credits = null) => {
    if (!user) {
      toast.error('You must be logged in to purchase credits');
      return;
    }

    setPaymentLoading(true);

    try {
      let price, currency;

      if (packageId) {
        // Package purchase
        const selectedPackage = packages.find(pkg => pkg.package_id === packageId);
        if (!selectedPackage) {
          throw new Error('Package not found');
        }
        price = selectedPackage.price;
        currency = selectedPackage.currency || 'USD';
        credits = selectedPackage.credits;
      } else if (credits) {
        // Custom credits purchase
        price = calculateCustomCreditPrice(credits);
        const selectedCurrency = currency; // Use detected currency from state
        currency = selectedCurrency;
      } else {
        throw new Error('Either package ID or credits amount is required');
      }

      console.log(`Creating checkout session for ${credits} credits at ${price} ${currency}`);

      // Ensure user is properly authenticated
      if (!user || !user.id) {
        throw new Error('User not properly authenticated');
      }

      console.log('Creating Stripe session with user ID:', user.id);

      // Get the user's IP address
      let ip = '';
      try {
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        ip = ipData.ip;
        console.log("User IP detected:", ip);
      } catch (ipError) {
        console.error("Error detecting IP:", ipError);
      }

      // Create simplified checkout session
      const sessionData = await createSimpleStripePayment(
        price,
        currency,
        user.id,
        credits,
        packageId,
        user.email
      );

      // Check if we got a Razorpay order instead of Stripe session
      if (sessionData.provider === 'razorpay' || sessionData.paymentProvider === 'razorpay') {
        console.log("Initializing Razorpay payment:", sessionData);

        // Make sure Razorpay script is loaded
        if (!window.Razorpay) {
          toast.error('Payment gateway not available. Please try again later.');
          return;
        }

        // Initialize Razorpay
        const options = {
          key: sessionData.key || RAZORPAY_KEY_ID,
          amount: sessionData.amount,
          currency: sessionData.currency,
          name: "FileZen",
          description: `Purchase ${sessionData.credits} credits`,
          order_id: sessionData.orderId,
          handler: async function (response) {
            try {
              console.log("Razorpay payment successful, verifying:", response);
              // Verify the payment
              const result = await verifyRazorpayPayment(
                response.razorpay_payment_id,
                response.razorpay_order_id,
                response.razorpay_signature,
                user.id
              );

              if (result.success) {
                toast.success(`Payment successful! ${result.credits} credits added to your account.`);
                logPurchaseEvent(sessionData.currency, sessionData.amount / 100, [{ item_id: 'credits', item_name: 'FileZen Credits', price: sessionData.amount / 100 }]);
                refreshUser();
                onClose();
              } else {
                toast.error('Payment verification failed');
              }
            } catch (error) {
              console.error("Razorpay verification error:", error);
              toast.error('Payment verification failed');
            }
          },
          prefill: {
            name: user.display_name || '',
            email: user.email || ''
          },
          theme: {
            color: "#4F46E5"
          }
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else if (sessionData && sessionData.sessionUrl) {
        // Redirect to Stripe checkout
        try {
          const url = new URL(sessionData.sessionUrl);
          if (!/^https:\/\/checkout\.stripe\.com\//.test(url.href)) {
            throw new Error('Invalid Stripe URL');
          }
          window.location.href = url.href;
        } catch (e) {
          toast.error('Received invalid session URL');
        }
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error("Purchase error:", error);
      toast.error('Payment setup failed. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6">Purchase Credits</h2>

        {loading ? (
          <div className="text-center py-8">Loading packages...</div>
        ) : (
          <>
            {/* Preset Packages */}
            <div className="mb-8">
              <h3 className="text-lg font-medium mb-4">Choose a Package</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {packages.map((pkg) => (
                  <div
                    key={pkg.package_id}
                    className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => handlePurchase(pkg.package_id)}
                  >
                    <h4 className="font-medium">{pkg.description}</h4>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-lg font-bold">{pkg.symbol || '$'}{pkg.price}</span>
                      <div className="text-right">
                        <span className="text-gray-600 block">{pkg.credits} Credits</span>
                        {pkg.validity && (
                          <span className="text-xs text-green-600 block">Valid for {pkg.validity}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-2 text-center">
                      Taxes as applicable will be charged subject to jurisdiction and applicable Tax laws
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Credits */}
            <div>
              <h3 className="text-lg font-medium mb-4">Custom Amount</h3>
              <div className="mb-4">
                <input
                  type="number"
                  min={userCountry === 'IN' ? '2' : '5'}
                  max="10000"
                  value={customCredits}
                  onChange={(e) => handleCustomCreditsChange(e.target.value)}
                  placeholder={`Enter credit amount (min ${userCountry === 'IN' ? '2' : '5'} credits)`}
                  className="w-full border rounded-lg px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum: {userCountry === 'IN' ? '₹10 INR' : '$0.50 USD'} ({userCountry === 'IN' ? '2' : '5'} credits)
                </p>
              </div>

              {customPrice && (
                <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg mb-4">
                  <div>
                    <span className="block font-medium">{customPrice.credits} Credits</span>
                    <span className="text-gray-600 text-sm">Custom package</span>
                  </div>
                  <div className="text-right">
                    <span className="block font-bold">{customPrice.symbol}{customPrice.price}</span>
                  </div>
                </div>
              )}

              <button
                onClick={() => handlePurchase(null, parseInt(customCredits))}
                disabled={!customPrice || paymentLoading}
                className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {paymentLoading ? 'Processing...' : 'Purchase Custom Credits'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal >
  );
}

// Enhanced Navbar with compliance links
function Navbar() {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  // Close menus when clicking outside
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) &&
        event.target.id !== 'mobile-menu-button') {
        setMobileMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Function to close mobile menu
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center relative">
            {/* Logo and Title */}
            <div className="flex-shrink-0">
              <button
                onClick={() => navigate('/')}
                className="flex items-center hover:opacity-80 transition-opacity"
              >
                <img src={logo} alt="FileZen Logo" className="h-8 w-8 mr-2" />
                <h1 className="text-xl sm:text-2xl font-bold text-indigo-600">FileZen</h1>
                <span className="hidden sm:inline ml-2 text-sm text-gray-500">AI-Powered Document Enhancer</span>
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/output"
                    className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Output
                  </Link>
                  <Link
                    to="/history"
                    className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    History
                  </Link>
                  <Link
                    to="/pricing"
                    className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Pricing
                  </Link>
                  {user.isAdmin && (
                    <Link
                      to="/admin"
                      className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                      Admin
                    </Link>
                  )}

                  {/* Credit Badge with Quick Buy */}
                  <button
                    onClick={() => setShowCreditModal(true)}
                    className="flex items-center space-x-2 bg-green-50 hover:bg-green-100 px-3 py-1 rounded-full transition-colors"
                  >
                    <CreditCardIcon className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700">{user.credits} Credits</span>
                    <CurrencyDollarIcon className="h-4 w-4 text-green-600" />
                  </button>

                  <div className="relative" ref={userMenuRef}>
                    <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className="flex items-center space-x-2 text-gray-700 hover:text-gray-900"
                    >
                      <UserIcon className="h-5 w-5" />
                      <span className="text-sm font-medium">{user.display_name}</span>
                    </button>

                    {showUserMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                        <Link
                          to="/referrals"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <GiftIcon className="h-4 w-4 mr-2" />
                          Referrals
                        </Link>
                        <Link
                          to="/help"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <QuestionMarkCircleIcon className="h-4 w-4 mr-2" />
                          Help Center
                        </Link>
                        <Link
                          to="/contact"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <PhoneIcon className="h-4 w-4 mr-2" />
                          Contact Us
                        </Link>
                        <button
                          onClick={logout}
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                        >
                          <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                          Sign out
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Link
                    to="/help"
                    className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Help
                  </Link>
                  <Link
                    to="/login"
                    className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/pricing"
                    className="text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Pricing
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                id="mobile-menu-button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t" ref={mobileMenuRef}>
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {user ? (
                <>
                  <div className="flex items-center px-3 py-2">
                    <UserIcon className="h-5 w-5 mr-2 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">{user.display_name}</span>
                  </div>
                  {user.isAdmin && (
                    <Link
                      to="/admin"
                      onClick={closeMobileMenu}
                      className="block text-gray-700 hover:text-indigo-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                      Admin
                    </Link>
                  )}
                  <div className="flex items-center justify-between px-3 py-2 bg-green-50 rounded-md">
                    <div className="flex items-center">
                      <CreditCardIcon className="h-5 w-5 mr-2 text-green-600" />
                      <span className="text-sm font-medium text-gray-700">Credits: {user.credits}</span>
                    </div>
                    <button
                      onClick={() => {
                        setShowCreditModal(true);
                        closeMobileMenu();
                      }}
                      className="text-xs text-green-600 font-medium"
                    >
                      + Add More
                    </button>
                  </div>
                  <Link
                    to="/dashboard"
                    className="block text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-md text-base font-medium"
                    onClick={closeMobileMenu}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/output"
                    className="block text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-md text-base font-medium"
                    onClick={closeMobileMenu}
                  >
                    Output
                  </Link>
                  <Link
                    to="/history"
                    className="block text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-md text-base font-medium"
                    onClick={closeMobileMenu}
                  >
                    History
                  </Link>
                  <Link
                    to="/pricing"
                    className="block text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-md text-base font-medium"
                    onClick={closeMobileMenu}
                  >
                    Pricing
                  </Link>
                  <Link
                    to="/referrals"
                    className="block text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-md text-base font-medium"
                    onClick={closeMobileMenu}
                  >
                    Referrals
                  </Link>
                  <Link
                    to="/help"
                    className="block text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-md text-base font-medium"
                    onClick={closeMobileMenu}
                  >
                    Help Center
                  </Link>
                  <Link
                    to="/contact"
                    className="block text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-md text-base font-medium"
                    onClick={closeMobileMenu}
                  >
                    Contact Us
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      closeMobileMenu();
                    }}
                    className="block w-full text-left text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-md text-base font-medium"
                  >
                    Sign out
                  </button>
                  <button
                    onClick={closeMobileMenu}
                    className="block w-full text-left text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-md text-base font-medium"
                  >
                    Close
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/help"
                    className="block text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-md text-base font-medium"
                    onClick={closeMobileMenu}
                  >
                    Help
                  </Link>
                  <Link
                    to="/login"
                    className="block text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-md text-base font-medium"
                    onClick={closeMobileMenu}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/pricing"
                    className="block text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-md text-base font-medium"
                    onClick={closeMobileMenu}
                  >
                    Pricing
                  </Link>
                  <button
                    onClick={closeMobileMenu}
                    className="block w-full text-left text-gray-700 hover:bg-gray-50 px-3 py-2 rounded-md text-base font-medium"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      <CreditPurchaseModal
        isOpen={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        onPurchase={refreshUser}
      />
    </>
  );
}

// Footer with compliance links
function Footer() {
  const [userCountry, setUserCountry] = useState('US');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const detectCountry = async () => {
      try {
        // Get user's IP address
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        const ip = ipData.ip;

        // Call our backend to get country
        const response = await fetch('https://us-central1-filezenai.cloudfunctions.net/getCreditPackagesApi');
        if (response.ok) {
          const data = await response.json();
          setUserCountry(data.country || 'US');
        }
      } catch (error) {
        console.error('Error detecting country:', error);
        setUserCountry('US'); // Default to US
      } finally {
        setIsLoading(false);
      }
    };

    detectCountry();
  }, []);

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center mb-4">
              <img src={logo} alt="FileZen Logo" className="h-6 w-6 mr-2" />
              <h3 className="text-lg font-semibold">FileZen</h3>
            </div>
            <p className="text-gray-400 text-sm">
              AI-powered document processing platform. Transform your files with intelligent analysis and enhancement.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Products</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/help#excel" className="hover:text-white">Excel Processing</Link></li>
              <li><Link to="/help#documents" className="hover:text-white">Document Enhancement</Link></li>
              <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
              <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Support</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact Us</Link></li>
              <li><Link to="/help#faq" className="hover:text-white">FAQ</Link></li>
              <li><a href="mailto:filezen@smartbusinessai.cloud" className="hover:text-white">Contact Us: filezen@smartbusinessai.cloud</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
              <li><Link to="/refund" className="hover:text-white">Refund Policy</Link></li>
              <li><Link to="/cookies" className="hover:text-white">Cookie Policy</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
          <p>&copy; 2025 FileZen. All rights reserved. | Built with AI-powered technology</p>
          <p className="mt-2 text-xs text-gray-500">
            FileZen - product of Halfpace Consultants LLP.
          </p>
        </div>
      </div>
    </footer>
  );
}

// Help Center Page
function HelpPage() {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = {
    overview: {
      title: 'Getting Started',
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-3">Welcome to FileZen</h3>
            <p className="text-gray-600 mb-4">
              FileZen is an AI-powered document processing platform that helps you analyze, enhance, and transform your files with intelligent automation.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">Quick Start Guide:</h4>
              <ol className="list-decimal list-inside text-blue-800 space-y-1">
                <li>Create an account and get 100 free credits</li>
                <li>Upload your file (Excel, Word, or PDF)</li>
                <li>Choose your processing type</li>
                <li>Review and confirm the credit cost</li>
                <li>Get your AI-enhanced results</li>
              </ol>
            </div>
          </div>
        </div>
      )
    },
    credits: {
      title: 'Credits System',
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-3">How Credits Work</h3>
            <p className="text-gray-600 mb-4">
              Credits are the currency used to pay for AI processing services. Each type of processing costs different amounts of credits.
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="border p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Excel Processing</h4>
                <ul className="text-sm space-y-1">
                  <li>• Plain Formatting: 5 credits</li>
                  <li>• Data Analysis: 10 credits</li>
                  <li>• Dashboard Creation: 15 credits</li>
                </ul>
              </div>
              <div className="border p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Document Enhancement</h4>
                <ul className="text-sm space-y-1">
                  <li>• General Proofreading: 8 credits</li>
                  <li>• Business Enhancement: 8 credits</li>
                  <li>• Blog Optimization: 10 credits</li>
                  <li>• Academic Enhancement: 12 credits</li>
                </ul>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-green-900 mb-2">Ways to Get Credits:</h4>
              <ul className="text-green-800 space-y-1">
                <li>• 100 free credits when you sign up</li>
                <li>• Purchase credit packages or custom amounts</li>
                <li>• Earn 25 credits for each successful referral</li>
                <li>• Get 50 bonus credits when using a referral code</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    excel: {
      title: 'Excel Processing',
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-3">Excel Processing Options</h3>

            <div className="space-y-4">
              <div className="border p-4 rounded-lg">
                <h4 className="font-semibold text-blue-600 mb-2">Plain Formatting (5 credits)</h4>
                <p className="text-gray-600 mb-2">Basic structure and formatting improvements for your spreadsheet.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Column header improvements</li>
                  <li>• Data type suggestions</li>
                  <li>• Cell formatting recommendations</li>
                  <li>• Structure optimization</li>
                </ul>
              </div>

              <div className="border p-4 rounded-lg">
                <h4 className="font-semibold text-blue-600 mb-2">Data Analysis (10 credits)</h4>
                <p className="text-gray-600 mb-2">Comprehensive data insights with bullet-point analysis.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Key insights and trends</li>
                  <li>• Important statistics</li>
                  <li>• Data quality observations</li>
                  <li>• Action recommendations</li>
                </ul>
              </div>

              <div className="border p-4 rounded-lg">
                <h4 className="font-semibold text-blue-600 mb-2">Dashboard Creation (15 credits)</h4>
                <p className="text-gray-600 mb-2">Auto-generated charts, KPIs, and visualization recommendations.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Key Performance Indicators</li>
                  <li>• Suggested chart types</li>
                  <li>• Trend analysis</li>
                  <li>• Comparative insights</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    documents: {
      title: 'Document Enhancement',
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-3">Document Enhancement Types</h3>

            <div className="space-y-4">
              <div className="border p-4 rounded-lg">
                <h4 className="font-semibold text-green-600 mb-2">General Proofreading (8 credits)</h4>
                <p className="text-gray-600 mb-2">Basic grammar, spelling, and readability improvements.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Grammar and spelling corrections</li>
                  <li>• Style improvements</li>
                  <li>• Clarity enhancements</li>
                  <li>• Basic structure suggestions</li>
                </ul>
              </div>

              <div className="border p-4 rounded-lg">
                <h4 className="font-semibold text-green-600 mb-2">Business Enhancement (8 credits)</h4>
                <p className="text-gray-600 mb-2">Professional business communication optimization.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Professional tone adjustments</li>
                  <li>• Business formatting standards</li>
                  <li>• Executive summary suggestions</li>
                  <li>• Action items identification</li>
                </ul>
              </div>

              <div className="border p-4 rounded-lg">
                <h4 className="font-semibold text-green-600 mb-2">Blog Optimization (10 credits)</h4>
                <p className="text-gray-600 mb-2">SEO and engagement improvements for blog content.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• SEO optimization</li>
                  <li>• Readability improvements</li>
                  <li>• Engaging headlines</li>
                  <li>• Call-to-action suggestions</li>
                </ul>
              </div>

              <div className="border p-4 rounded-lg">
                <h4 className="font-semibold text-green-600 mb-2">Academic Enhancement (12 credits)</h4>
                <p className="text-gray-600 mb-2">Scholarly writing and research paper improvements.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Academic writing standards</li>
                  <li>• Citation recommendations</li>
                  <li>• Argument structure</li>
                  <li>• Research methodology suggestions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    faq: {
      title: 'Frequently Asked Questions',
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">What file types do you support?</h4>
              <p className="text-gray-600">Accepted formats are MS Word, PDF documents and MS excel files. Maximum file size is 50MB.</p>
            </div>

            <div className="border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">How long does AI processing take?</h4>
              <p className="text-gray-600">Most files are processed within 1-3 minutes. Processing time depends on file size and complexity.</p>
            </div>

            <div className="border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Can I process files without an account?</h4>
              <p className="text-gray-600">Yes! Guest users can upload files, pay per job, and receive results via email without creating an account.</p>
            </div>

            <div className="border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">What payment methods do you accept?</h4>
              <p className="text-gray-600">We accept major credit cards through Stripe (international) and Razorpay (India). Payment method is automatically selected based on your location.</p>
            </div>

            <div className="border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">How does the referral system work?</h4>
              <p className="text-gray-600">Share your referral link with friends. When they sign up, they get 50 bonus credits and you earn 25 credits. Win-win!</p>
            </div>

            <div className="border p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Is my data secure?</h4>
              <p className="text-gray-600">Yes, all files are encrypted during upload and processing. Files are automatically deleted after 48 hours for security.</p>
            </div>
          </div>
        </div>
      )
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Help Center</h1>
        <p className="text-gray-600">Everything you need to know about using FileZen</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-8">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <nav className="space-y-1">
            {Object.entries(sections).map(([key, section]) => (
              <button
                key={key}
                onClick={() => setActiveSection(key)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeSection === key
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
              >
                {section.title}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg shadow border p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {sections[activeSection].title}
            </h2>
            {sections[activeSection].content}
          </div>
        </div>
      </div>
    </div>
  );
}

// Privacy Policy Page
function PrivacyPage() {
  const [country, setCountry] = useState('US');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const detect = async () => {
      try {
        const data = await detectUserRegion();
        setCountry(data.country);
      } catch (e) {
        console.error("Failed to detect region for privacy policy:", e);
      } finally {
        setLoading(false);
      }
    };
    detect();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // India Specific Policy
  if (country === 'IN' || country === 'IND') {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow border p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy – Halfpace consultants LLP, India</h1>
          <div className="prose prose-lg max-w-none space-y-6">
            <p className="text-gray-600">Last updated: 12/27/2025</p>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Legal Entities</h2>
              <p className="text-gray-600">
                Refunds are processed by Halfpace Consultants LLP for India transactions. This policy applies to all transactions.
                The entity reserves the right to refund or refuse refund based on the activity done and evaluation.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Information We Collect</h2>
              <p className="text-gray-600">
                We collect information you provide directly to us, including when you create an account, upload files, make payments, or contact us for support.
                This may include your name, email address, payment information, and the content of files you upload for processing.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. How We Use Your Information</h2>
              <p className="text-gray-600">
                We use the information we collect to provide, maintain, and improve our services, process payments, send you technical notices and support messages, and respond to your requests and questions.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. File Processing and AI Analysis</h2>
              <p className="text-gray-600">
                Files you upload are processed using AI services to provide analysis and enhancements. Files are automatically deleted from our servers after 48 hours for security and privacy.
                We do not use your file content to train AI models or for any purpose other than providing the requested service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Data Security</h2>
              <p className="text-gray-600">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
                All data transmissions are encrypted using industry-standard protocols.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. International Data Transfers</h2>
              <p className="text-gray-600">
                Your information may be processed and stored in countries other than your own. We ensure appropriate safeguards are in place for international data transfers in compliance with applicable laws.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Your Rights</h2>
              <p className="text-gray-600">
                You have the right to access, update, or delete your personal information. You can manage your account settings or contact us to exercise these rights.
                EU residents have additional rights under GDPR, including data portability and the right to object to processing.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Contact Us</h2>
              <p className="text-gray-600">
                If you have any questions about this Privacy Policy, please contact us at filezen@smartbusinessai.cloud or through our contact page.
              </p>
            </section>
          </div>
        </div>
      </div>
    );
  }

  // Default Policy
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow border p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy – Phoenix SSA Sp.z.o.o</h1>
        <div className="prose prose-lg max-w-none space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Privacy Policy</h2>
          <p className="text-gray-600">Last updated: 12/27/2025</p>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Legal Entities</h2>
            <p className="text-gray-600">
              Refunds are processed by Phoenix SSA Sp.z.o.o. This policy applies to all transactions regardless of the operating entity.
              The entity reserves the right to refund or refuse refund based on the activity done and evaluation.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Information We Collect</h2>
            <p className="text-gray-600">
              We collect information you provide directly to us, including when you create an account, upload files,
              make payments, or contact us for support. This may include your name, email address, payment information,
              and the content of files you upload for processing.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. How We Use Your Information</h2>
            <p className="text-gray-600">
              We use the information we collect to provide, maintain, and improve our services, process payments,
              send you technical notices and support messages, and respond to your requests and questions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3. File Processing and AI Analysis</h2>
            <p className="text-gray-600">
              Files you upload are processed using AI services to provide analysis and enhancements.
              Files are automatically deleted from our servers after 48 hours for security and privacy.
              We do not use your file content to train AI models or for any purpose other than providing the requested service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Data Security</h2>
            <p className="text-gray-600">
              We implement appropriate technical and organizational security measures to protect your personal information
              against unauthorized access, alteration, disclosure, or destruction. All data transmissions are encrypted using industry-standard protocols.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">5. International Data Transfers</h2>
            <p className="text-gray-600">
              Your information may be processed and stored in countries other than your own.
              We ensure appropriate safeguards are in place for international data transfers in compliance with applicable laws.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Your Rights</h2>
            <p className="text-gray-600">
              You have the right to access, update, or delete your personal information.
              You can manage your account settings or contact us to exercise these rights.
              EU residents have additional rights under GDPR, including data portability and the right to object to processing.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Contact Us</h2>
            <p className="text-gray-600">
              If you have any questions about this Privacy Policy, please contact us at Filezen@smartbusinessai.cloud
              or through our contact page.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

// Terms of Service Page
function TermsPage() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow border p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms of Service</h1>
        <div className="prose prose-lg max-w-none space-y-6">
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h2>
            <p className="text-gray-600">
              By accessing and using FileZen, you accept and agree to be bound by the terms and provision of this agreement.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Description of Service</h2>
            <p className="text-gray-600">
              FileZen provides AI-powered document processing services, including but not limited to Excel analysis,
              document enhancement, and file formatting. Services are provided on a credit-based payment system.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3. User Accounts and Responsibilities</h2>
            <p className="text-gray-600">
              You are responsible for maintaining the confidentiality of your account and password.
              You agree to accept responsibility for all activities that occur under your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Credits and Payments</h2>
            <p className="text-gray-600">
              Credits are required to use AI processing services. Credits are non-refundable except as required by law.
              Pricing is subject to change with notice. Unused credits do not expire but may be subject to inactivity policies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">5. File Upload and Processing</h2>
            <p className="text-gray-600">
              You retain ownership of all files you upload. By uploading files, you grant us permission to process them
              using AI services to provide the requested analysis or enhancement. Files are automatically deleted after 48 hours.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Prohibited Uses</h2>
            <p className="text-gray-600">
              You may not use our service for any unlawful purpose or to upload content that is illegal, harmful,
              threatening, abusive, or violates any third party's rights.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Limitation of Liability</h2>
            <p className="text-gray-600">
              FileZen shall not be liable for any indirect, incidental, special, consequential, or punitive damages
              resulting from your use of the service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Contact Information</h2>
            <p className="text-gray-600">
              Questions about the Terms of Service should be sent to us at contact@smartbusinessai.cloud.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

// Contact Page
function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));

    toast.success(`Message sent! We'll get back to you within 24 hours.`);
    setFormData({ name: '', email: '', subject: '', message: '' });
    setLoading(false);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Contact Us</h1>
        <p className="text-gray-600">We're here to help! Get in touch with our support team.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Contact Form */}
        <div className="bg-white rounded-lg shadow border p-6">
          <h2 className="text-xl font-semibold mb-4">Send us a message</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Name</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Email</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Subject</label>
              <input
                type="text"
                required
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Message</label>
              <textarea
                required
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="form-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary"
            >
              {loading ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>

        {/* Contact Info */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow border p-6">
            <h3 className="font-semibold mb-4">Other ways to reach us</h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <EnvelopeIcon className="h-5 w-5 text-gray-600 mr-3" />
                <div>
                  <p className="font-medium">Email Support</p>
                  <p className="text-gray-600">filezen@smartbusinessai.cloud</p>
                </div>
              </div>
              <div className="flex items-center">
                <ChatBubbleLeftEllipsisIcon className="h-5 w-5 text-gray-600 mr-3" />
                <div>
                  <p className="font-medium">Live Chat</p>
                  <p className="text-gray-600">Use the chat button in the bottom right</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-2">Quick Help</h3>
            <p className="text-blue-800 text-sm mb-3">
              For faster support, try our Help Center or chat with our AI assistant first.
            </p>
            <Link
              to="/help"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
            >
              Visit Help Center
              <ChevronRightIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// Keep all existing components (Dashboard, PricingPage, etc.) but update to include the new navigation...
// [Rest of the code remains the same - Dashboard, FileUploadZone, AIProcessingCard, JobStatusCard, HistoryPage, ReferralsPage, PaymentSuccess, etc.]

// Enhanced HomePage with compliance links
function HomePage() {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [dynamicContent, setDynamicContent] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [averageRating, setAverageRating] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const landing = await import('./firebase/landing');
        const items = await landing.listLandingContent();
        setDynamicContent(items.filter(i => i.published));
        const fb = await landing.listApprovedFeedback(6);
        console.log('Loaded feedback:', fb);
        setFeedback(fb);

        // Calculate average rating
        if (fb.length > 0) {
          const totalRating = fb.reduce((sum, item) => sum + (item.rating || 0), 0);
          const avg = totalRating / fb.length;
          setAverageRating(Math.round(avg * 10) / 10); // Round to 1 decimal place
        } else {
          setAverageRating(0);
        }
      } catch (error) {
        console.error('Error loading feedback:', error);
      }
    })();
  }, []);

  const openImageModal = (imageSrc, imageAlt) => {
    setSelectedImage({ src: imageSrc, alt: imageAlt });
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  return (
    <>
      <SEOHead
        title="FileZen | AI-Powered Document Enhancer"
        canonicalPath=""
        domain="https://filezen.smartbusinessai.cloud"
      />
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          {/* Enhanced Hero Section with Pain Points */}
          <div className="text-center mb-20">
            <div className="mb-6">
              <span className="bg-indigo-100 text-indigo-800 text-sm font-medium px-3 py-1 rounded-full">
                Try Free • No Credit Card Required
              </span>
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Just Upload your document (PDF, WORD OR EXCEL).
              <span className="text-indigo-600 block">FileZen automatically fixes formatting & Structure.</span>
            </h1>
            <p className="text-xl text-gray-600 mb-6 max-w-3xl mx-auto">
              No prompts. No instructions. No templates. FileZen understands your file and improves the formatting on its own.
            </p>

            {/* Pain Points vs Solution */}
            <div className="flex justify-center mb-8">
              <div className="grid md:grid-cols-1 gap-3 text-lg text-gray-700 max-w-2xl text-left">
                <div className="flex items-center">
                  <span className="mr-3">❌</span> Writing prompts to explain what you want
                </div>
                <div className="flex items-center">
                  <span className="mr-3">❌</span> Manually fixing styles, layouts & formulas
                </div>
                <div className="flex items-center">
                  <span className="mr-3">❌</span> Guessing how AI tools should behave
                </div>
                <div className="flex items-center font-semibold text-indigo-900 mt-2">
                  <span className="mr-3">✅</span> Just upload. FileZen figures it out.
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="mb-6">
              <p className="text-gray-700 font-medium mb-4">
                Built for people who hate formatting — and don’t want to waste time on prompts.
              </p>

              <div className="flex justify-center space-x-4 mb-4">
                <button
                  onClick={() => navigate('/login')}
                  className="bg-indigo-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                >
                  Fix My Document Instantly
                </button>
                <button
                  onClick={() => navigate('/pricing')}
                  className="border border-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  See Pricing
                </button>
              </div>

              <p className="text-sm font-medium text-gray-600 mb-6">
                No prompts • No setup • No credit card
              </p>
            </div>

            <p className="text-sm text-gray-500">
              100 free credits to start – No subscription required – Pay per use or buy credit packs
            </p>
          </div>

          {/* Before & After Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">See the Difference</h2>
            <div className="grid md:grid-cols-2 gap-12">
              {/* Excel Example */}
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-gray-800">Excel Transformation</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                    <div className="text-sm font-medium text-red-600 mb-2">BEFORE</div>
                    <div className="text-sm text-gray-600 mb-3">Messy data, inconsistent formatting, broken formulas</div>
                    <img
                      src="/images/before.png"
                      alt="Before: Unformatted Excel data"
                      className="w-full h-auto rounded border border-gray-300 shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => openImageModal("/images/before.png", "Before: Unformatted Excel data")}
                    />
                  </div>
                  <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                    <div className="text-sm font-medium text-green-600 mb-2">AFTER</div>
                    <div className="text-sm text-gray-600 mb-3">Clean data, proper formatting, working formulas</div>
                    <img
                      src="/images/After.png"
                      alt="After: Formatted Excel data with analysis"
                      className="w-full h-auto rounded border border-gray-300 shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => openImageModal("/images/After.png", "After: Formatted Excel data with analysis")}
                    />
                  </div>
                </div>
                <div className="mt-4 text-sm text-gray-500 italic">
                  "FileZen fixed my messy sales spreadsheet in minutes. My manager was impressed with the clean charts and formatting!" - Sarah K.
                </div>
              </div>

              {/* Document Example */}
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-gray-800">Document Enhancement</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                    <div className="text-sm font-medium text-red-600 mb-2">BEFORE</div>
                    <div className="text-sm text-gray-600 mb-3">Inconsistent formatting, grammar issues, poor structure</div>
                    <img
                      src="/images/beforedoc.png"
                      alt="Before: Unformatted document with errors"
                      className="w-full h-auto rounded border border-gray-300 shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => openImageModal("/images/beforedoc.png", "Before: Unformatted document with errors")}
                    />
                  </div>
                  <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                    <div className="text-sm font-medium text-green-600 mb-2">AFTER</div>
                    <div className="text-sm text-gray-600 mb-3">Professional formatting, error-free, well-structured</div>
                    <img
                      src="/images/Afterdoc.png"
                      alt="After: Formatted document with improvements"
                      className="w-full h-auto rounded border border-gray-300 shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => openImageModal("/images/Afterdoc.png", "After: Formatted document with improvements")}
                    />
                  </div>
                </div>
                <div className="mt-4 text-sm text-gray-500 italic">
                  "My business proposal looked amateur until FileZen fixed it. The client was impressed with the professional formatting!" - Michael T.
                </div>
              </div>
            </div>
          </div>

          {/* User Feedback (moderated) */}
          {feedback.length > 0 && (
            <div className="mb-20">
              <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">What Users Say</h2>
              {averageRating > 0 && (
                <div className="text-center mb-8">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <div className="flex text-yellow-400">
                      {[1, 2, 3, 4, 5].map(star => {
                        const diff = averageRating - star;
                        if (diff >= 1) return <span key={star} className="text-yellow-400">★</span>;
                        if (diff > 0) return <span key={star} className="text-yellow-400">★</span>; // Full star for now
                        return <span key={star} className="text-gray-300">★</span>;
                      })}
                    </div>
                    <span className="text-lg font-semibold text-gray-900">{averageRating > 0 ? averageRating.toFixed(1) : '0'}/5</span>
                  </div>
                  <p className="text-sm text-gray-600">Average rating from {feedback.length} reviews</p>
                </div>
              )}
              <div className="grid md:grid-cols-3 gap-6">
                {feedback.map(fb => (
                  <div key={fb.id} className="bg-white p-6 rounded-xl shadow-sm border">
                    <p className="text-gray-700 italic mb-4">"{fb.text || fb.body}"</p>
                    <div className="text-sm text-gray-500 mb-3">- {fb.author || 'Anonymous'}</div>
                    <div className="flex items-center justify-end">
                      <div className="flex text-yellow-400">
                        {[1, 2, 3, 4, 5].map(star => {
                          const rating = fb.rating || 0;
                          const diff = rating - star;
                          if (diff >= 1) return <span key={star} className="text-yellow-400">★</span>;
                          if (diff > 0) return <span key={star} className="text-yellow-400">★</span>; // Full star for now
                          return <span key={star} className="text-gray-300">★</span>;
                        })}
                      </div>
                      <span className="text-sm text-gray-600 ml-2">{(fb.rating || 0) > 0 ? (fb.rating || 0).toFixed(1) : '0'}/5</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Social Proof Section */}
          <div className="text-center mb-20">
            <p className="text-sm text-gray-500 mb-8">Trusted by users worldwide</p>
            <div className="grid md:grid-cols-4 gap-8 max-w-3xl mx-auto">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">1,000+</div>
                <div className="text-sm text-gray-500">Documents Processed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">500+</div>
                <div className="text-sm text-gray-500">Happy Users</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">5 mins</div>
                <div className="text-sm text-gray-500">Average Processing Time</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">3 formats</div>
                <div className="text-sm text-gray-500">Supported File Types</div>
              </div>
            </div>
          </div>

          {/* Dynamic Sections from Firestore */}
          {dynamicContent.length > 0 && (
            <div className="space-y-12 mb-20">
              {dynamicContent.map(block => (
                <div key={block.id} className="bg-white p-6 rounded-xl shadow-sm border">
                  <h3 className="text-2xl font-semibold mb-3 text-gray-900">{block.title}</h3>
                  {block.type === 'image' && block.mediaUrl && (
                    <div className="mb-4">
                      <img
                        src={block.mediaUrl}
                        alt={block.title}
                        className="w-full h-auto rounded-lg shadow-sm"
                        style={{ maxHeight: '400px', objectFit: 'contain' }}
                      />
                    </div>
                  )}

                  {block.type === 'video' && block.mediaUrl && (
                    <div className="mb-4">
                      <video
                        controls
                        className="w-full h-auto rounded-lg shadow-sm"
                        style={{ maxHeight: '400px' }}
                      >
                        <source src={block.mediaUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  )}

                  {block.type === 'video-embed' && block.mediaUrl && (
                    <div className="aspect-w-16 aspect-h-9 mb-4">
                      <iframe
                        title={block.title}
                        className="w-full h-64 rounded-lg"
                        src={block.mediaUrl}
                        allow="autoplay; fullscreen"
                      />
                    </div>
                  )}

                  {block.type === 'text' && block.sections && block.sections.length > 0 && (
                    <div className="space-y-6">
                      {block.sections.map((section, sectionIndex) => (
                        <div key={sectionIndex} className="space-y-4">
                          {section.type === 'text' && section.content && (
                            <p className="text-gray-700 whitespace-pre-wrap">{section.content}</p>
                          )}

                          {section.type === 'image' && section.mediaUrl && (
                            <div className="text-center">
                              <img
                                src={section.mediaUrl}
                                alt={`${block.title} - Section ${sectionIndex + 1}`}
                                className="max-w-full h-auto rounded-lg shadow-sm mx-auto"
                                style={{ maxHeight: '400px', objectFit: 'contain' }}
                              />
                            </div>
                          )}

                          {section.type === 'image-text' && (
                            <div className="grid md:grid-cols-2 gap-6 items-start">
                              {section.mediaUrl && (
                                <div className="text-center">
                                  <img
                                    src={section.mediaUrl}
                                    alt={`${block.title} - Section ${sectionIndex + 1}`}
                                    className="max-w-full h-auto rounded-lg shadow-sm"
                                    style={{ maxHeight: '300px', objectFit: 'contain' }}
                                  />
                                </div>
                              )}
                              {section.content && (
                                <div className="flex items-center">
                                  <p className="text-gray-700 whitespace-pre-wrap">{section.content}</p>
                                </div>
                              )}
                            </div>
                          )}

                          {section.type === 'video' && section.mediaUrl && (
                            <div className="text-center">
                              <video
                                controls
                                className="max-w-full h-auto rounded-lg shadow-sm mx-auto"
                                style={{ maxHeight: '400px' }}
                              >
                                <source src={section.mediaUrl} type="video/mp4" />
                                Your browser does not support the video tag.
                              </video>
                            </div>
                          )}

                          {section.type === 'video-text' && (
                            <div className="grid md:grid-cols-2 gap-6 items-start">
                              {section.mediaUrl && (
                                <div className="text-center">
                                  <video
                                    controls
                                    className="max-w-full h-auto rounded-lg shadow-sm"
                                    style={{ maxHeight: '300px' }}
                                  >
                                    <source src={section.mediaUrl} type="video/mp4" />
                                    Your browser does not support the video tag.
                                  </video>
                                </div>
                              )}
                              {section.content && (
                                <div className="flex items-center">
                                  <p className="text-gray-700 whitespace-pre-wrap">{section.content}</p>
                                </div>
                              )}
                            </div>
                          )}

                          {section.type === 'link' && section.mediaUrl && (
                            <div className="text-center">
                              <a
                                href={section.mediaUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                              >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                Open Link
                              </a>
                            </div>
                          )}

                          {section.type === 'link-text' && (
                            <div className="grid md:grid-cols-2 gap-6 items-start">
                              {section.mediaUrl && (
                                <div className="text-center">
                                  <a
                                    href={section.mediaUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                                  >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                    Open Link
                                  </a>
                                </div>
                              )}
                              {section.content && (
                                <div className="flex items-center">
                                  <p className="text-gray-700 whitespace-pre-wrap">{section.content}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {block.type === 'text' && block.body && !block.sections && (
                    <p className="text-gray-700 whitespace-pre-wrap">{block.body}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Enhanced Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <div className="bg-white p-8 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <PresentationChartBarIcon className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Excel Processing</h3>
              <p className="text-gray-600 mb-4">
                From basic formatting to advanced dashboards - choose your level of analysis.
              </p>
              <ul className="space-y-3 text-sm">
                <li className="border-l-4 border-blue-100 pl-3 py-1">
                  <div className="font-medium text-gray-700">Basic Formatting (10 credits)</div>
                  <div className="text-gray-500">Clean layouts, consistent fonts, proper column widths</div>
                </li>
                <li className="border-l-4 border-blue-300 pl-3 py-1">
                  <div className="font-medium text-gray-700">Advanced Formatting (20 credits)</div>
                  <div className="text-gray-500">Formula corrections, pivot tables, charts</div>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <DocumentDuplicateIcon className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Document Enhancement</h3>
              <p className="text-gray-600 mb-4">
                Get AI assistance with your documents. Choose from different enhancement types based on your needs.
              </p>
              <ul className="space-y-3 text-sm">
                <li className="border-l-4 border-green-100 pl-3 py-1">
                  <div className="font-medium text-gray-700">Basic Formatting (10 credits)</div>
                  <div className="text-gray-500">Grammar fixes, consistent formatting, better readability</div>
                </li>
                <li className="border-l-4 border-green-300 pl-3 py-1">
                  <div className="font-medium text-gray-700">Advanced Formatting (20 credits)</div>
                  <div className="text-gray-500">Professional structure, executive summaries, clear action items</div>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <SparklesIcon className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Guest Processing</h3>
              <p className="text-gray-600 mb-4">
                No account needed! Pay per job ($1.25/file) and get results via email.
              </p>
              <ul className="space-y-3 text-sm">
                <li className="border-l-4 border-purple-100 pl-3 py-1">
                  <div className="font-medium text-gray-700">Save Time & Money</div>
                  <div className="text-gray-500">Save time and get your work done which costs less than a cup of Coffee</div>
                </li>
                <li className="border-l-4 border-purple-300 pl-3 py-1">
                  <div className="font-medium text-gray-700">Email Delivery</div>
                  <div className="text-gray-500">Get your enhanced files sent directly to your inbox</div>
                </li>
                <li className="border-l-4 border-purple-400 pl-3 py-1">
                  <div className="font-medium text-gray-700">PDF Invoices</div>
                  <div className="text-gray-500">Receive professional receipts for business expenses</div>
                </li>
                <li className="border-l-4 border-purple-500 pl-3 py-1">
                  <div className="font-medium text-gray-700">No Sign-up Required</div>
                  <div className="text-gray-500">Quick and easy process with no account creation</div>
                </li>
              </ul>
            </div>
          </div>

          {/* Referral Section */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8 mb-20">
            <div className="text-center">
              <GiftIcon className="mx-auto h-12 w-12 text-green-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Earn Credits by Referring Friends</h2>
              <p className="text-lg text-gray-600 mb-6">
                Get 25 credits for every friend who signs up, and they get 50 bonus credits too!
              </p>
              <button
                onClick={() => navigate('/login')}
                className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                Start Referring
              </button>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-indigo-600 rounded-2xl p-12 text-center text-white mb-20">
            <h2 className="text-3xl font-bold mb-4">Ready to save time on your documents?</h2>
            <p className="text-xl mb-8 text-indigo-100">
              Start with 100 free credits. Try it risk-free.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="bg-white text-indigo-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Start Free Trial
            </button>
          </div>

          {/* FAQ Section */}
          <div className="max-w-4xl mx-auto mb-20">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">How does the credit system work?</h3>
                  <p className="text-gray-600">
                    Each processing job costs credits based on complexity. You start with 100 free credits and can buy more as needed. No monthly subscriptions required.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Need bulk processing beyond the highest credit pack?</h3>
                  <p className="text-gray-600">
                    Contact us for subscriptions or enterprise or SME packs to handle your high-volume requirements.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">What file types are supported?</h3>
                  <p className="text-gray-600">
                    Accepted formats are MS Word, PDF documents and MS excel files. Maximum file size is 50MB.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Is my data safe?</h3>
                  <p className="text-gray-600">
                    Yes, your files are processed securely and not stored permanently. We use industry-standard encryption and security practices.
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">How long does processing take?</h3>
                  <p className="text-gray-600">
                    Most files are processed within 2-5 minutes. Complex Excel dashboards may take slightly longer.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I use this without creating an account?</h3>
                  <p className="text-gray-600">
                    Yes! You can process files as a guest for a nominal price per processed file and receive results via email. Creating an account gives you access to history and credit management.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">What if I'm not satisfied with the results?</h3>
                  <p className="text-gray-600">
                    We offer a satisfaction guarantee. If you're not happy with your results, contact us and we'll work to make it right.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={closeImageModal}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={closeImageModal}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 text-2xl font-bold z-10"
            >
              ×
            </button>
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-50 text-white p-3 rounded-lg">
              <p className="text-sm">{selectedImage.alt}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// [Keep all other existing components: LoginForm, Dashboard, FileUploadZone, AIProcessingCard, JobStatusCard, HistoryPage, ReferralsPage, PricingPage, PaymentSuccess]

// Enhanced Login Form with Google Login and Forgot Password
function LoginForm() {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    display_name: '',
    referral_code: ''
  });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [referralVerified, setReferralVerified] = useState(false);
  const [referralChecking, setReferralChecking] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const { login, register, refreshUser } = useAuth();
  const navigate = useNavigate();

  // Get referral code from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      setFormData(prev => ({ ...prev, referral_code: refCode }));
      setIsLogin(false); // Switch to register mode if referral code present
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');

        // Force refresh the auth context
        await refreshUser();

        console.log("Navigating to dashboard after login...");

        // Use navigate instead of window.location
        navigate('/dashboard');
      } else {
        await register(
          formData.email,
          formData.display_name,
          formData.password,
          (formData.referral_code ? formData.referral_code.toUpperCase() : null)
        );
        const message = formData.referral_code
          ? 'Account created! You got 50 bonus credits from your referral!'
          : 'Account created successfully! Welcome to FileZen!';
        toast.success(message);

        // Force refresh the auth context
        await refreshUser();

        console.log("Navigating to dashboard after registration...");

        // Use navigate instead of window.location
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      // Import Firebase Google Auth provider
      const { GoogleAuthProvider, signInWithPopup } = await import('firebase/auth');
      const { auth, db } = await import('./firebase');
      const { doc, getDoc, setDoc, serverTimestamp } = await import('firebase/firestore');

      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // Process the Google user result
      const user = result.user;
      console.log('Google login successful:', user.email);

      // Check if user document exists in Firestore
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        // Create a new user document for Google sign-in
        const generateReferralCode = () => {
          return Math.random().toString(36).substring(2, 10).toUpperCase();
        };

        const isAdmin = user.email.toLowerCase() === 'admin@filezen.com';
        const initialCredits = isAdmin ? 10000 : 100; // Give 100 credits to new Google users

        const newUserData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || user.email.split('@')[0],
          credits: initialCredits,
          referralCode: generateReferralCode(),
          isAdmin: isAdmin,
          emailVerified: !!user.emailVerified,
          signupBonusGranted: true, // Auto-grant signup bonus for Google users
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        await setDoc(userDocRef, newUserData);
        console.log("✅ Created new user document for Google user with signup bonus");

        // Create referral code mapping
        try {
          await setDoc(doc(db, 'referralCodes', newUserData.referralCode), {
            code: newUserData.referralCode,
            userId: user.uid,
            createdAt: serverTimestamp()
          }, { merge: true });
          console.log('✅ referralCodes mapping created for Google user');
        } catch (e) {
          console.warn('⚠️ Failed to create referralCodes mapping for Google user:', e);
        }

        // Record the signup bonus transaction
        try {
          const { addDoc, collection, query, where, getDocs } = await import('firebase/firestore');

          // Check if signup bonus transaction already exists
          const existingTransactionQuery = query(
            collection(db, 'transactions'),
            where('userId', '==', user.uid),
            where('transactionType', '==', 'signup_bonus')
          );
          const existingTransactions = await getDocs(existingTransactionQuery);

          if (existingTransactions.empty) {
            await addDoc(collection(db, 'transactions'), {
              userId: user.uid,
              transactionType: 'signup_bonus',
              amount: initialCredits,
              price: 0,
              currency: 'CREDITS',
              paymentProvider: 'system',
              paymentId: `signup_bonus_google_${user.uid}`,
              status: 'completed',
              visible: true,
              createdAt: serverTimestamp()
            });
            console.log('✅ Signup bonus transaction recorded for Google user');
          } else {
            console.log('✅ Signup bonus transaction already exists for Google user');
          }
        } catch (e) {
          console.warn('⚠️ Failed to record signup bonus transaction for Google user:', e);
        }
      }

      // Check if this was a new user (first time sign-in)
      if (!userDoc.exists()) {
        toast.success('Welcome to FileZen! You received 100 signup bonus credits!');
      } else {
        toast.success('Successfully signed in with Google!');
      }

      // Force refresh the auth context
      await refreshUser();

      console.log("Navigating to dashboard...");

      // Explicitly redirect to dashboard after successful login
      // Use navigate instead of window.location
      navigate('/dashboard');
    } catch (error) {
      console.error('Google login error:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        toast.error('Sign-in cancelled');
      } else {
        toast.error('Google sign-in failed. Please try again.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      // Import Firebase password reset
      const { sendPasswordResetEmail } = await import('firebase/auth');
      const { auth } = await import('./firebase');

      await sendPasswordResetEmail(auth, formData.email);
      setResetEmailSent(true);
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error) {
      console.error('Password reset error:', error);
      if (error.code === 'auth/user-not-found') {
        toast.error('No account found with this email address');
      } else {
        toast.error('Failed to send password reset email');
      }
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password Form
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Reset Password</h2>
            <p className="mt-2 text-sm text-gray-600">
              Enter your email to receive a password reset link
            </p>
          </div>

          <div className="mt-8">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              {resetEmailSent ? (
                <div className="text-center">
                  <CheckCircleIcon className="mx-auto h-12 w-12 text-green-600 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Email Sent!</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Check your inbox for password reset instructions.
                  </p>
                  <button
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmailSent(false);
                    }}
                    className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-indigo-700"
                  >
                    Back to Sign In
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Enter your email address"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {loading ? 'Sending...' : 'Send Reset Email'}
                  </button>

                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(false)}
                      className="text-indigo-600 hover:text-indigo-500 text-sm"
                    >
                      Back to Sign In
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">FileZen</h2>
          <p className="mt-2 text-sm text-gray-600">
            {isLogin ? 'Sign in to your account' : 'Create your account'}
          </p>
          {formData.referral_code && (
            <div className="mt-2 p-2 bg-green-100 rounded-lg">
              <p className="text-sm text-green-800">
                🎉 You're signing up with a referral! Get 50 bonus credits!
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Full Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Referral Code (Optional)
                </label>
                <input
                  type="text"
                  value={formData.referral_code}
                  onChange={(e) => setFormData({ ...formData, referral_code: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter referral code to get bonus credits"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
            </button>
          </form>

          {/* Google Login */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                {googleLoading ? (
                  'Signing in...'
                ) : (
                  <>
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Sign {isLogin ? 'in' : 'up'} with Google
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-indigo-600 hover:text-indigo-500 text-sm"
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>

              {isLogin && (
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-indigo-600 hover:text-indigo-500 text-sm"
                >
                  Forgot password?
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Enhanced File Upload with Job Type Selection and Cancel/Reupload functionality
function FileUploadZone({ onFileUploaded, isGuest = false, uploadedFile = null, onCancelUpload = null }) {
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/pdf': ['.pdf']
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles.length === 0) return;

      setUploading(true);
      const file = acceptedFiles[0];

      try {
        // Get file extension to determine file type
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const fileType = getFileTypeFromExtension(fileExtension);

        // Generate a unique ID for the file
        const timestamp = new Date().getTime();
        const fileId = `local-${timestamp}`;

        // Create file metadata without uploading to storage
        const fileData = {
          id: fileId,
          filename: file.name,
          fileType: fileExtension,
          file_type: fileType,
          file_size: file.size,
          originalFile: file, // Store the actual file object for direct processing
          isLocalFile: true   // Flag to indicate this is a local file
        };

        toast.success('File ready for processing!');
        logFileAction('upload', fileType, file.size);
        onFileUploaded(fileData);
      } catch (error) {
        toast.error(error.message || 'File preparation failed');
      } finally {
        setUploading(false);
      }
    }
  });

  // Helper function to determine file type from extension
  const getFileTypeFromExtension = (extension) => {
    const documentTypes = ['doc', 'docx', 'pdf', 'txt', 'rtf', 'odt'];
    const spreadsheetTypes = ['xls', 'xlsx', 'csv', 'ods'];
    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'];

    if (documentTypes.includes(extension)) {
      return 'document';
    } else if (spreadsheetTypes.includes(extension)) {
      return 'spreadsheet';
    } else if (imageTypes.includes(extension)) {
      return 'image';
    } else {
      return 'other';
    }
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // If file is uploaded, show file info with cancel/reupload options
  if (uploadedFile) {
    return (
      <div className="border-2 border-green-300 bg-green-50 rounded-lg p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center space-x-3 min-w-0">
            <CheckCircleIcon className="h-8 w-8 text-green-600 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-green-900 truncate" title={uploadedFile.filename}>
                {uploadedFile.filename}
              </p>
              <p className="text-sm text-green-700">
                {formatFileSize(uploadedFile.file_size)} • {uploadedFile.fileType.toUpperCase()} • Ready for processing
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2 flex-shrink-0">
            {onCancelUpload && (
              <button
                onClick={onCancelUpload}
                className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 transition-colors"
              >
                <XCircleIcon className="h-4 w-4 mr-1" />
                Cancel
              </button>
            )}
            <button
              onClick={() => {
                if (onCancelUpload) onCancelUpload();
                // Trigger file input click to select new file
                document.querySelector('input[type="file"]')?.click();
              }}
              className="inline-flex items-center px-3 py-1.5 border border-indigo-300 text-sm font-medium rounded-md text-indigo-700 bg-white hover:bg-indigo-50 transition-colors whitespace-nowrap"
            >
              <CloudArrowUpIcon className="h-4 w-4 mr-1" />
              Upload Different File
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragActive
        ? 'border-indigo-500 bg-indigo-50'
        : 'border-gray-300 hover:border-gray-400'
        } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <input {...getInputProps()} disabled={uploading} />
      <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
      <p className="text-lg font-medium text-gray-900 mb-2">
        {uploading ? 'Preparing file...' : 'Drop your files here, or click to select'}
      </p>
      <p className="text-sm text-gray-500 mb-4">
        Accepted: MS Word, PDF, MS Excel • Max 50MB
      </p>
      {isGuest && (
        <p className="text-sm text-indigo-600 mt-2">
          Guest mode: Pay per job, no account needed
        </p>
      )}
      <p className="text-xs text-gray-400 mt-4 text-center px-4">
        AI can make mistakes, please check your output properly for relevance and benefit. The platform is not responsible for any financial or other damages if it may occur to the user due to usage of the document processed by AI.
      </p>
    </div>
  );
}

// Enhanced AI Processing Card with new job types
function AIProcessingCard({ onProcess, disabled, uploadedFile, isGuest = false }) {
  const [selectedJobType, setSelectedJobType] = useState('');
  const [enhancementType, setEnhancementType] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { user } = useAuth();
  const [preUpsells, setPreUpsells] = useState({
    style_themes: false
  });

  const [theme, setTheme] = useState('Modern Corporate');

  // Reset state when a new file is uploaded
  useEffect(() => {
    if (uploadedFile) {
      setSelectedJobType('');
      setIsProcessing(false);
      setPreUpsells({ style_themes: false });
    }
  }, [uploadedFile]);

  // Reset processing state when disabled changes to false
  useEffect(() => {
    if (!disabled) {
      setIsProcessing(false);
    }
  }, [disabled]);

  // Determine available job types based on file type
  const getJobTypes = (fileType) => {
    // Normalize file type to lowercase for comparison
    const normalizedType = fileType ? fileType.toLowerCase() : '';
    console.log('getJobTypes called with fileType:', fileType, 'normalized:', normalizedType);

    if (['xlsx', 'csv'].includes(normalizedType)) {
      return [
        { id: 'excel_formatting', name: 'Plain Formatting', description: 'Basic structure and formatting improvements', cost: 5, icon: DocumentTextIcon },
        { id: 'excel_analysis', name: 'Data Analysis', description: 'Comprehensive data insights with bullet points', cost: 10, icon: ChartBarIcon },
        { id: 'excel_dashboard', name: 'Dashboard Creation', description: 'Auto-generate charts, KPIs and visualizations', cost: 15, icon: PresentationChartBarIcon }
      ];
    } else if (['docx', 'pdf'].includes(normalizedType)) {
      return [
        { id: 'document_general', name: 'General Proofreading', description: 'Grammar, spelling and general improvements', cost: 8, icon: PencilSquareIcon },
        { id: 'document_business', name: 'Business Enhancement', description: 'Professional business communication', cost: 8, icon: DocumentTextIcon },
        { id: 'document_blog', name: 'Blog Optimization', description: 'SEO and engagement improvements', cost: 10, icon: DocumentDuplicateIcon },
        { id: 'document_academic', name: 'Academic Enhancement', description: 'Scholarly writing improvements', cost: 12, icon: DocumentTextIcon }
      ];
    }
    console.warn('No job types found for fileType:', fileType);
    return [];
  };

  const jobTypes = uploadedFile ? getJobTypes(uploadedFile.fileType) : [];
  console.log('jobTypes for file:', uploadedFile?.fileType, 'result:', jobTypes);
  const selectedJob = jobTypes.find(job => job.id === selectedJobType);

  const handleProcess = () => {
    if (!selectedJobType) {
      toast.error('Please select an AI Processing Type before continuing.');
      return;
    }

    if (isGuest || (user && user.credits >= selectedJob.cost)) {
      setShowConfirmation(true);
    } else {
      toast.error(`Insufficient credits. You need ${selectedJob.cost} credits but have ${user.credits}.`);
    }
  };

  const confirmProcess = () => {
    if (!selectedJobType) {
      toast.error('Please select an AI Processing Type before continuing.');
      setShowConfirmation(false);
      return;
    }

    setShowConfirmation(false);
    setIsProcessing(true);
    const processData = {
      jobType: selectedJobType,
      enhancementType: selectedJobType.startsWith('document_') ? selectedJobType : null,
      // Pass pre-upsells to backend if needed
      preUpsells: {
        style_themes: preUpsells.style_themes && selectedJob?.jobType?.startsWith('document_') ? theme : false
      }
    };

    // Call the process function
    onProcess(processData)
      .catch(error => {
        console.error("Error processing file:", error);
        toast.error("Error processing file. Please try again.");
      })
      .finally(() => {
        // We'll keep this set to true since the parent component will handle the loading state
        // The button will remain disabled while processing due to the disabled prop
      });
  };

  if (!uploadedFile) {
    return (
      <div className="bg-white rounded-lg shadow border p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-400">Choose AI Processing Type</h3>
        <p className="text-gray-500 text-center py-8">Upload a file first to see available processing options</p>
      </div>
    );
  }

  // Determine if button should be disabled
  const isButtonDisabled = disabled || isProcessing || !selectedJobType;

  return (
    <>
      <div className="relative">
        {/* Loading Overlay */}
        {(disabled || isProcessing) && (
          <div className="absolute inset-0 bg-white bg-opacity-90 rounded-lg z-10 flex flex-col items-center justify-center p-6">
            <div className="w-16 h-16 mb-4">
              <svg className="animate-spin w-full h-full text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">AI Processing in Progress</h3>
            <p className="text-gray-600 text-center mb-4">
              We're analyzing your document with advanced AI.<br />This may take a minute or two.
            </p>
            <div className="w-full max-w-sm bg-gray-200 rounded-full h-2 mb-2">
              <div className="bg-indigo-600 h-2 rounded-full animate-pulse" style={{ width: '90%' }}></div>
            </div>
            <p className="text-sm text-gray-500">Please don't close this window</p>
          </div>
        )}

        {/* Existing Card Content */}
        <div className="bg-white rounded-lg shadow border p-6">
          <h3 className="text-lg font-semibold mb-4">Choose AI Processing Type</h3>
          <p className="text-sm text-gray-600 mb-4">
            File: {uploadedFile.filename} ({uploadedFile.fileType.toUpperCase()})
          </p>

          <div className="space-y-3 mb-6">
            {jobTypes.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No processing options available for this file type.</p>
            ) : (
              jobTypes.map((job) => {
                // Use capitalized variable name so React recognizes it as a component
                const Icon = job.icon;

                if (!Icon) {
                  console.error('Invalid icon for job:', job.id, 'Icon is undefined');
                  return null;
                }

                // Create a wrapper component to ensure proper rendering
                const IconWrapper = Icon;

                return (
                  <label key={job.id} className={`flex items-center space-x-3 cursor-pointer p-3 border rounded-lg transition-colors ${selectedJobType === job.id ? 'bg-indigo-50 border-indigo-300' : 'hover:bg-gray-50'
                    }`}>
                    <input
                      type="radio"
                      name="jobType"
                      value={job.id}
                      checked={selectedJobType === job.id}
                      onChange={(e) => setSelectedJobType(e.target.value)}
                      className="text-indigo-600"
                      disabled={disabled || isProcessing}
                    />
                    <IconWrapper className="h-5 w-5 text-gray-600" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{job.name}</span>
                        <span className="text-sm bg-gray-100 px-2 py-1 rounded">{job.cost} credits</span>
                      </div>
                      <p className="text-sm text-gray-500">{job.description}</p>
                    </div>
                  </label>
                );
              })
            )}
          </div>

          {/* AI Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <ExclamationCircleIcon className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-amber-800 mb-1">AI Processing Notice</p>
                <p className="text-amber-700">
                  AI may modify or change parts of your document during processing. Please review the final enhanced document carefully before using it for any important purposes.
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={handleProcess}
            disabled={isButtonDisabled}
            className={`w-full py-2 px-4 rounded-lg font-medium flex items-center justify-center ${!selectedJobType
              ? 'bg-gray-300 text-gray-600 hover:bg-gray-400'
              : disabled || isProcessing
                ? 'bg-indigo-400 text-white cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
              } disabled:opacity-70 transition-all duration-200`}
          >
            {(disabled || isProcessing) && (
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {!selectedJobType
              ? 'Select a Processing Type'
              : isGuest
                ? 'Proceed to Payment'
                : disabled || isProcessing
                  ? 'Processing...'
                  : 'Start AI Processing'
            }
          </button>

          {disabled && !isProcessing && (
            <p className="text-xs text-center mt-2 text-gray-500">
              Please wait for the current job to complete before starting a new one
            </p>
          )}
        </div>
      </div>

      {/* Pre-processing upsells - Only show if there are available add-ons */}
      {(() => {
        const hasAvailableAddons = selectedJob?.jobType?.startsWith('document_');

        if (!hasAvailableAddons) return null;

        return (
          <div className="mt-4 bg-white rounded-lg shadow border p-4">
            <div className="text-sm font-medium text-gray-900 mb-3">Optional add-ons</div>
            <div className="space-y-3">
              {/* Row 1: Theme - Only show for document jobs */}
              {selectedJob?.jobType?.startsWith('document_') && (
                <div className="flex items-start justify-between gap-3 p-3 border rounded-lg">
                  <div className="flex items-start gap-2 flex-1">
                    <input id="opt-theme" type="checkbox" className="mt-1" checked={preUpsells.style_themes} onChange={(e) => setPreUpsells(s => ({ ...s, style_themes: e.target.checked }))} />
                    <label htmlFor="opt-theme" className="cursor-pointer select-none flex-1">
                      <div className="text-sm font-semibold">Professional Theme</div>
                      <div className="text-xs text-gray-600">Apply {theme} styling (+8 credits)</div>
                    </label>
                  </div>
                  {preUpsells.style_themes && (
                    <select value={theme} onChange={(e) => setTheme(e.target.value)} className="text-xs border rounded px-2 py-1 w-40">
                      <option>Modern Corporate</option>
                      <option>Academic Journal</option>
                      <option>Creative</option>
                    </select>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Job Type Selection */}

      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmation} onClose={() => setShowConfirmation(false)}>
        <div className="text-center">
          <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Confirm Processing</h3>
          <p className="text-gray-600 mb-4">
            {(() => {
              const base = selectedJob?.cost || 0;
              const extra = (preUpsells.style_themes && selectedJob?.jobType?.startsWith('document_') ? 8 : 0);
              const total = base + extra;
              return `This ${selectedJob?.name || 'job'} will cost ${total} credits.${!isGuest ? ` You have ${user?.credits} credits.` : ''}`;
            })()}
          </p>
          {preUpsells.style_themes && (
            <p className="text-xs text-gray-500 mb-4">
              Add-ons: {(preUpsells.style_themes && selectedJob?.jobType?.startsWith('document_')) ? `Theme (${theme}) (+8) ` : ''}
            </p>
          )}
          <p className="text-sm text-gray-500 mb-4">
            {selectedJob?.description}
          </p>

          {/* AI Disclaimer in Confirmation Modal */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
            <div className="flex items-start space-x-2">
              <ExclamationCircleIcon className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs">
                <p className="font-medium text-amber-800 mb-1">Important Notice</p>
                <p className="text-amber-700">
                  AI may modify your document during processing. Please review the final result before using it for important purposes.
                </p>
              </div>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => setShowConfirmation(false)}
              className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={confirmProcess}
              className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700"
            >
              {isGuest ? 'Proceed' : 'Confirm'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

// Post-processing upsell offer component
function PostProcessingUpsellOffer({ offer, onApply, applyingUpsell, jobData }) {
  const [selectedLanguage, setSelectedLanguage] = useState('es');
  const [selectedTheme, setSelectedTheme] = useState('Modern Corporate');

  // Check if this upsell has already been applied
  const appliedUpsells = jobData?.result?.upsells || [];
  const isApplied = appliedUpsells.some(u => u.id === offer.id);

  const handleApply = () => {
    let options = {};
    if (offer.id === 'translation') {
      options = { lang: selectedLanguage };
    } else if (offer.id === 'style_themes') {
      options = { theme: selectedTheme };
    }
    onApply(offer, options);
  };

  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${isApplied
      ? 'bg-green-50 border-green-200'
      : 'bg-white border-blue-200'
      }`}>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold text-gray-900">{offer.title}</div>
          {isApplied && (
            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
              Applied
            </span>
          )}
        </div>
        <div className="text-xs text-gray-600">{offer.description}</div>
        <div className="text-xs text-blue-600 font-medium">{offer.value}</div>
        {offer.id === 'translation' && (
          <div className="mt-2">
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="text-xs border rounded px-2 py-1 w-44"
            >
              {ALL_LANG_OPTIONS.map(l => (
                <option key={l.code} value={l.code}>{l.name}</option>
              ))}
            </select>
          </div>
        )}
        {offer.id === 'style_themes' && (
          <div className="mt-2">
            <select
              value={selectedTheme}
              onChange={(e) => setSelectedTheme(e.target.value)}
              className="text-xs border rounded px-2 py-1 w-44"
            >
              <option value="Modern Corporate">Modern Corporate</option>
              <option value="Academic Journal">Academic Journal</option>
              <option value="Creative">Creative</option>
            </select>
          </div>
        )}
      </div>
      <button
        onClick={handleApply}
        disabled={applyingUpsell === offer.id}
        className={`ml-3 px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${isApplied
          ? 'bg-green-600 text-white hover:bg-green-700'
          : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
      >
        {applyingUpsell === offer.id ? 'Applying...' : isApplied ? 'Apply Again' : offer.ctaText}
      </button>
    </div>
  );
}

// Enhanced Job Status Card
function JobStatusCard({ job, onRefresh }) {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [downloadFormat, setDownloadFormat] = useState('');
  const { user } = useAuth();

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [applyingUpsell, setApplyingUpsell] = useState(null);

  // EU consent panel state
  const [showConsentPanel, setShowConsentPanel] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState(null);

  useEffect(() => {
    let interval;
    if (job.status === 'processing') {
      // Simulate progress updates
      interval = setInterval(() => {
        setProgress(prev => {
          const next = prev + (Math.random() * 5);
          return next > 95 ? 95 : next;
        });
      }, 1000);
    } else {
      setProgress(100);
    }
    return () => clearInterval(interval);
  }, [job.status]);

  // Set appropriate default download format based on file type
  useEffect(() => {
    if (job.status === 'completed' && job.result) {
      const fileType = job.result.file_type?.toLowerCase() || '';
      if (fileType.includes('xlsx') || fileType.includes('xls')) {
        setDownloadFormat('xlsx');
      } else if (fileType.includes('csv')) {
        setDownloadFormat('csv');
      } else if (fileType.includes('pdf')) {
        setDownloadFormat('pdf');
      } else if (fileType.includes('doc')) {
        setDownloadFormat('docx');
      } else {
        setDownloadFormat('txt');
      }
    }
  }, [job.status, job.result]);

  // When a job completes, if user is in EU and hasn't set consent, show panel
  useEffect(() => {
    const maybeShowConsent = async () => {
      try {
        if (job.status !== 'completed' || !user?.id) return;
        const userRef = doc(db, 'users', user.id);
        const snap = await getDoc(userRef);
        const consentObj = snap.exists() ? (snap.data()?.processedFileSaveConsent || null) : null;
        if (consentObj && typeof consentObj.granted === 'boolean') {
          return; // already decided
        }
        const code = await detectCountryCode();
        setDetectedCountry(code);
        if (EU_COUNTRY_CODES.has(code)) {
          setShowConsentPanel(true);
        }
      } catch (e) {
        // ignore
      }
    };
    maybeShowConsent();
  }, [job.status, user?.id]);

  const saveConsent = async (granted) => {
    if (!user?.id) return;
    try {
      setConsentSaving(true);
      const userRef = doc(db, 'users', user.id);
      await updateDoc(userRef, {
        processedFileSaveConsent: {
          granted: !!granted,
          country: detectedCountry || null,
          at: serverTimestamp()
        }
      });
      setShowConsentPanel(false);
      toast.success(granted ? 'We will save processed files to your account.' : 'We will not save processed files. Downloads will be session-only.');
    } catch (e) {
      toast.error('Failed to save preference');
    } finally {
      setConsentSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      const result = {
        id: job.job_id || job.id,
        jobType: job.job_type || job.jobType,
        result: job.result,
        creditsUsed: job.credits_used || job.creditsUsed,
        completedAt: job.completed_at || job.completedAt
      };

      await downloadEnhancedFile(result, downloadFormat);
      toast.success('File downloaded successfully!');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download file');
    }
  };

  const handleDownloadTranslated = async () => {
    try {
      const result = {
        id: job.job_id || job.id,
        jobType: job.job_type || job.jobType,
        result: {
          ...job.result,
          // Replace enhancement_suggestions with translated_text for download
          enhancement_suggestions: job.result.translated_text,
          original_enhancement_suggestions: job.result.enhancement_suggestions
        },
        creditsUsed: job.credits_used || job.creditsUsed,
        completedAt: job.completed_at || job.completedAt
      };

      await downloadEnhancedFile(result, downloadFormat);
      toast.success('Translated file downloaded successfully!');
    } catch (error) {
      console.error('Translated download failed:', error);
      toast.error('Failed to download translated file');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'processing':
        return (
          <svg className="animate-spin h-5 w-5 text-yellow-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
      default:
        return <ClockIcon className="h-5 w-5 text-yellow-500 animate-pulse" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-700 bg-green-100';
      case 'failed':
        return 'text-red-700 bg-red-100';
      default:
        return 'text-yellow-700 bg-yellow-100';
    }
  };

  const formatJobName = (jobType, enhancementType) => {
    // Check both property naming conventions
    const enhType = enhancementType || job.enhancement_type;
    const jType = jobType || job.job_type;

    if (enhType) {
      return enhType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return jType ? jType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown';
  };

  // Normalize job properties to handle both naming conventions
  const jobData = {
    status: job.status,
    jobType: job.jobType || job.job_type,
    enhancementType: job.enhancementType || job.enhancement_type,
    creditsUsed: job.creditsUsed || job.credits_used || 0,
    result: job.result
  };

  useEffect(() => {
    if (job.status !== 'completed' || !job.result) return;

    const enhancedUrl = job.result.enhanced_file_url;
    const enhancedPath = job.result.enhanced_file_path || job.result.enhancedPath;

    const determineTypeFromName = (name) => {
      if (!name) return null;
      const lower = name.toLowerCase();
      if (lower.endsWith('.pdf')) return 'pdf';
      if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) return 'excel';
      if (lower.endsWith('.csv')) return 'csv';
      if (lower.endsWith('.docx') || lower.endsWith('.doc')) return 'word';
      return null;
    };

    const tryBuildPreview = async () => {
      try {
        setPreviewLoading(true);
        if (enhancedUrl) {
          setPreviewUrl(enhancedUrl);
          setPreviewType(determineTypeFromName(enhancedUrl));
          return;
        }
        if (enhancedPath) {
          try {
            const storage = getStorage();
            const fileRef = ref(storage, enhancedPath);
            const url = await getDownloadURL(fileRef);
            setPreviewUrl(url);
            setPreviewType(determineTypeFromName(enhancedPath));
            return;
          } catch (e) {
            console.warn('Unable to get download URL for preview:', e);
          }
        }
        // No file available for preview
        setPreviewUrl(null);
        setPreviewType(null);
      } finally {
        setPreviewLoading(false);
      }
    };

    tryBuildPreview();
  }, [job.status, job.result]);



  const renderPreview = () => {
    if (previewLoading) {
      return <div className="text-sm text-gray-500">Loading preview…</div>;
    }
    if (!previewUrl || !previewType) {
      return <div className="text-sm text-gray-500">No file preview available.</div>;
    }

    if (previewType === 'pdf') {
      return (
        <div className="h-96 border rounded-lg overflow-hidden">
          <iframe title="PDF Preview" src={previewUrl} className="w-full h-full" />
        </div>
      );
    }

    if (previewType === 'excel' || previewType === 'word') {
      const gview = `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(previewUrl)}`;
      return (
        <div className="h-96 border rounded-lg overflow-hidden">
          <iframe title="Document Preview" src={gview} className="w-full h-full" />
        </div>
      );
    }

    if (previewType === 'csv') {
      return (
        <div className="text-sm text-gray-600">Open CSV in a new tab: <a className="text-indigo-600 underline" href={previewUrl} target="_blank" rel="noreferrer">View CSV</a></div>
      );
    }

    return <div className="text-sm text-gray-500">Preview not supported for this file type.</div>;
  };



  const handleApplyUpsell = async (offer, options = {}) => {
    try {
      console.log('Applying upsell:', offer, 'with options:', options);
      setApplyingUpsell(offer.id);
      const jobId = job.job_id || job.id;
      const { user } = await import('./firebase');
      const userId = (await getCurrentUser())?.id;
      if (!userId) {
        toast.error('Please sign in');
        return;
      }
      console.log('Calling applyUpsellToJob with:', { userId, jobId, offerId: offer.id, options });
      const res = await applyUpsellToJob(userId, jobId, offer.id, options);
      if (res.success) {
        toast.success(`${offer.title} applied`);

        // Small delay to ensure backend has processed the update
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Refresh job status to get updated data
        await onRefresh?.();

        // Also refresh user data to update credits
        try {
          const { refreshUser } = await import('./firebase/auth');
          await refreshUser();
        } catch (e) {
          console.warn('Failed to refresh user data:', e);
        }
      } else if (res.creditsNeeded) {
        toast.error(`Need ${res.creditsNeeded} more credits`);
      } else {
        toast.error(res.error || 'Upsell failed');
      }
    } catch (e) {
      console.error('Upsell apply failed:', e);
      toast.error('Failed to apply');
    } finally {
      setApplyingUpsell(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">AI Job Status</h3>
        <button
          onClick={onRefresh}
          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
        >
          Refresh
        </button>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Status</span>
          <div className={`flex items-center space-x-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(jobData.status)}`}>
            {getStatusIcon(jobData.status)}
            <span className="capitalize">{jobData.status}</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Processing Type</span>
          <span className="text-sm font-medium">{formatJobName(jobData.jobType, jobData.enhancementType)}</span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Credits Used</span>
          <span className="text-sm font-medium">{jobData.creditsUsed}</span>
        </div>

        {jobData.status === 'processing' && (
          <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <div className="flex flex-col items-center justify-center py-4">
              <svg className="animate-spin h-8 w-8 text-yellow-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <h4 className="font-medium mb-2 text-yellow-800">Processing Your File</h4>
              <p className="text-sm text-yellow-700 text-center">
                We're analyzing your document with AI. This may take a minute or two.
              </p>
              <div className="w-full mt-4 bg-yellow-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-yellow-600 h-2 rounded-full transition-all duration-300 ease-in-out"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-xs text-yellow-600 mt-2">{Math.round(progress)}% complete</p>

              <div className="flex flex-col items-center mt-6 space-y-2 w-full">
                <button
                  onClick={() => navigate('/output')}
                  className="w-full bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center justify-center hover:bg-indigo-700 transition-colors"
                >
                  <ArrowRightIcon className="h-4 w-4 mr-2" />
                  View Results in Output Section
                </button>

                {jobData.result && (
                  <button
                    onClick={handleDownload}
                    className="w-full bg-green-600 text-white px-4 py-2 rounded-lg flex items-center justify-center hover:bg-green-700 transition-colors"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Download Enhanced File
                  </button>
                )}


              </div>
            </div>
          </div>
        )}

        {jobData.status === 'completed' && jobData.result && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            {showConsentPanel && (
              <div className="mb-4 p-4 border rounded-lg bg-blue-50 border-blue-200">
                <h4 className="font-medium text-blue-900 mb-2">Privacy preference for processed files</h4>
                <p className="text-sm text-blue-800 mb-3">
                  We detected you're in the EU. May we save your processed files to your account for later access? If you decline, you can download in this dashboard now, but it won't be available after reload.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => saveConsent(true)}
                    disabled={consentSaving}
                    className="px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    Allow saving
                  </button>
                  <button
                    onClick={() => saveConsent(false)}
                    disabled={consentSaving}
                    className="px-3 py-1.5 rounded-md border border-blue-600 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                  >
                    Don't save
                  </button>
                </div>
              </div>
            )}
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-4">AI Analysis Results</h4>

              {/* AI Disclaimer for Results */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <div className="flex items-start space-x-2">
                  <ExclamationCircleIcon className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs">
                    <p className="font-medium text-amber-800 mb-1">Important: Review Before Use</p>
                    <p className="text-amber-700">
                      AI can make mistakes, please check your output properly for relevance and benefit. The platform is not responsible for any financial or other damages if it may occur to the user due to usage of the document processed by AI.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col space-y-4">
                {/* What we improved - AI summary */}
                <div className="bg-white border border-green-200 rounded-lg p-4">
                  <div className="font-medium text-green-800 mb-2">What we improved</div>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">
                    {Array.isArray(jobData.result?.improvementSummary) ? jobData.result.improvementSummary.map((it) => `• ${it}`).join('\n') : 'Summary unavailable'}
                  </div>
                </div>

                {/* Translated content - only show if translation has been applied */}
                {jobData.result?.translated_text && (
                  <div className="bg-white border border-blue-200 rounded-lg p-4">
                    <div className="font-medium text-blue-800 mb-2">
                      Translated Content ({jobData.result.translation_lang?.toUpperCase() || 'Unknown Language'})
                    </div>
                    <div className="text-sm text-gray-700 whitespace-pre-wrap max-h-60 overflow-y-auto">
                      {jobData.result.translated_text}
                    </div>
                  </div>
                )}

                {/* Download section */}
                <div className="space-y-3">
                  {/* Original/Enhanced file download */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <select
                        value={downloadFormat}
                        onChange={(e) => setDownloadFormat(e.target.value)}
                        className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="xlsx">Enhanced Excel (.xlsx)</option>
                        <option value="csv">Enhanced CSV (.csv)</option>
                        <option value="docx">Enhanced Word (.docx)</option>
                        <option value="pdf">Enhanced PDF (.pdf)</option>
                        <option value="txt">Analysis Report (.txt)</option>
                      </select>
                      <button
                        onClick={handleDownload}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                        Download Enhanced File
                      </button>
                    </div>
                  </div>

                  {/* Translated file download - only show if translation has been applied */}
                  {jobData.result?.translated_text && (
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center space-x-3">
                        <div className="text-sm text-green-800 font-medium">
                          Translated to {jobData.result.translation_lang?.toUpperCase() || 'Unknown Language'}
                        </div>
                        <button
                          onClick={() => handleDownloadTranslated()}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                          Download Translated File
                        </button>
                      </div>
                    </div>
                  )}
                </div>




                {/* Post-processing upsells */}
                {(() => {
                  const processingResult = {
                    jobType: jobData.jobType,
                    enhancementType: jobData.enhancementType,
                    result: jobData.result,
                    resultText: jobData.result?.enhancement_suggestions || ''
                  };
                  const userProfile = getDefaultUserProfile(user, 0); // We'll need to get actual files processed count
                  const upsells = getRelevantUpsells(processingResult, userProfile);

                  console.log('Post-processing upsells debug:', {
                    processingResult,
                    userProfile,
                    upsells,
                    hasTranslation: !!jobData.result?.translated_text,
                    upsellsApplied: jobData.result?.upsells || []
                  });

                  if (upsells.length === 0) return null;

                  return (
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-medium text-blue-900 mb-3">Enhance your results</h4>
                      <div className="space-y-3">
                        {upsells.map((offer) => (
                          <PostProcessingUpsellOffer
                            key={offer.id}
                            offer={offer}
                            onApply={handleApplyUpsell}
                            applyingUpsell={applyingUpsell}
                            jobData={jobData}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {jobData.status === 'failed' && jobData.result?.error && (
          <div className="mt-4 p-4 bg-red-50 rounded-lg">
            <h4 className="font-medium text-red-800 mb-2">Error Details</h4>
            <p className="text-sm text-red-700">{jobData.result.error}</p>
          </div>
        )}
      </div>
    </div>
  );
}



// Enhanced Dashboard
function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [uploadedFile, setUploadedFile] = useState(null);
  const [currentJob, setCurrentJob] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bonusGranted, setBonusGranted] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [sendingVerify, setSendingVerify] = useState(false);
  const [claimingBonus, setClaimingBonus] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [resendCooldownSec, setResendCooldownSec] = useState(0);
  const prevEmailVerified = useRef(false);
  const [flagsLoaded, setFlagsLoaded] = useState(false);

  // Format date
  const formatDate = (dateStr) => {
    try {
      // Handle Firestore Timestamp objects
      if (dateStr && typeof dateStr === 'object' && dateStr.toDate) {
        return dateStr.toDate().toLocaleDateString() + ' ' +
          dateStr.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }

      // Handle regular date strings or timestamps
      if (dateStr) {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString() + ' ' +
            date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
      }

      return 'Unknown date';
    } catch (e) {
      console.error("Date formatting error:", e);
      return 'Unknown date';
    }
  };

  useEffect(() => {
    if (user) {
      fetchJobHistory();
    }
  }, [user]);

  useEffect(() => {
    setFlagsLoaded(false);
    const loadFlags = async () => {
      if (!user) return;
      try {
        // Check user document in Firestore
        const userDocRef = doc(db, 'users', user.id);
        const snap = await getDoc(userDocRef);

        if (snap.exists()) {
          const data = snap.data();
          const hasClaimedBonus = !!data.signupBonusGranted;
          const isEmailVerified = !!data.emailVerified || !!(auth.currentUser && auth.currentUser.emailVerified);

          console.log('User flags loaded:', {
            hasClaimedBonus,
            isEmailVerified,
            userId: user.id
          });

          setBonusGranted(hasClaimedBonus);
          setEmailVerified(isEmailVerified);
          prevEmailVerified.current = isEmailVerified;
        } else {
          // User document doesn't exist, check auth state
          const isEmailVerified = !!(auth.currentUser && auth.currentUser.emailVerified);
          console.log('No user document found, using auth state:', { isEmailVerified });

          setBonusGranted(false);
          setEmailVerified(isEmailVerified);
          prevEmailVerified.current = isEmailVerified;
        }

        setFlagsLoaded(true);
      } catch (e) {
        console.error('Error loading verification flags:', e);
        // Fallback to auth state
        const isEmailVerified = !!(auth.currentUser && auth.currentUser.emailVerified);
        setBonusGranted(false);
        setEmailVerified(isEmailVerified);
        prevEmailVerified.current = isEmailVerified;
        setFlagsLoaded(true);
      }
    };
    loadFlags();
  }, [user]);

  const handleSendVerification = async () => {
    try {
      setSendingVerify(true);
      if (!auth.currentUser) {
        toast.error('Not signed in');
        return;
      }
      await sendEmailVerification(auth.currentUser);
      toast.success('Verification email sent');
      setResendCooldownSec(60);
    } catch (e) {
      console.error('Send verification failed:', e);
      if (e?.code === 'auth/too-many-requests') {
        toast.error('Too many requests. Please wait before trying again.');
        setResendCooldownSec(prev => Math.max(prev, 120));
      } else if (e?.code === 'auth/invalid-recipient-email') {
        toast.error('Invalid email address.');
      } else if (e?.code === 'auth/quota-exceeded') {
        toast.error('Email quota exceeded temporarily. Try again later.');
      } else {
        toast.error('Failed to send verification email');
      }
    } finally {
      setSendingVerify(false);
    }
  };

  // Automatically check verification status periodically until verified
  useEffect(() => {
    if (!user || !flagsLoaded || bonusGranted || emailVerified) return;
    let intervalId;
    let attempts = 0;
    const maxAttempts = 20; // ~100s if 5s interval

    const tick = async () => {
      try {
        if (!auth.currentUser) return;
        await auth.currentUser.reload();
        const verified = !!auth.currentUser.emailVerified;
        if (verified && !prevEmailVerified.current) {
          setEmailVerified(true);
          toast.success('Email verified! You can claim your bonus.');
          clearInterval(intervalId);
        }
        prevEmailVerified.current = verified;
      } catch (e) {
        // ignore transient errors
      } finally {
        attempts += 1;
        if (attempts >= maxAttempts) {
          clearInterval(intervalId);
        }
      }
    };

    intervalId = setInterval(tick, 5000);
    // run immediately once
    tick();

    return () => clearInterval(intervalId);
  }, [user, flagsLoaded, bonusGranted, emailVerified]);

  const handleCheckVerified = async () => {
    try {
      setCheckingStatus(true);
      if (!auth.currentUser) return;
      await auth.currentUser.reload();
      const verified = !!auth.currentUser.emailVerified;
      setEmailVerified(verified);
      if (verified) {
        toast.success('Email verified! You can claim your bonus.');
      } else {
        toast('Not verified yet.');
      }
    } catch (e) {
      console.error('Check verified failed:', e);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleClaimBonus = async () => {
    try {
      // Prevent double clicking
      if (claimingBonus) return;

      setClaimingBonus(true);

      // Double-check that user hasn't already claimed
      if (bonusGranted) {
        toast.info('Welcome bonus already claimed!');
        return;
      }

      // Ensure user is email verified
      if (!emailVerified) {
        toast.error('Please verify your email address before claiming the signup bonus');
        return;
      }

      console.log('Claiming signup bonus for user:', user.id);
      console.log('Current auth state:', {
        user: user,
        authCurrentUser: auth.currentUser,
        emailVerified: emailVerified,
        bonusGranted: bonusGranted
      });

      const res = await claimSignupBonus();

      if (res && res.success) {
        setBonusGranted(true);
        toast.success('🎉 Welcome bonus of 100 credits claimed successfully!');
        await refreshUser();
        await refreshBonusStatus(); // Refresh the bonus status

        // Hide the success message after 5 seconds
        setTimeout(() => {
          // The success message will be hidden by the conditional rendering
        }, 5000);
      } else if (res && res.alreadyClaimed) {
        setBonusGranted(true);
        toast.info('Welcome bonus already claimed!');
        await refreshUser();
        await refreshBonusStatus(); // Refresh the bonus status
      }
    } catch (e) {
      console.error('Claim bonus failed:', e);

      // Handle specific error cases
      if (e.message?.includes('already claimed') || e.message?.includes('already-exists')) {
        setBonusGranted(true);
        toast.info('Welcome bonus already claimed!');
        await refreshUser();
        await refreshBonusStatus(); // Refresh the bonus status
      } else if (e.message?.includes('unauthenticated') || e.message?.includes('logged in')) {
        toast.error('Authentication error. Please refresh the page and try again.');
      } else if (e.message?.includes('verify your email')) {
        toast.error('Please verify your email address before claiming the signup bonus.');
      } else {
        toast.error(e.message || 'Failed to claim welcome bonus. Please try again.');
      }
    } finally {
      setClaimingBonus(false);
    }
  };

  const fetchJobHistory = async () => {
    try {
      const jobs = await getUserJobs(user.id);
      // Normalize job data to ensure consistent property names
      const normalizedJobs = jobs.map(job => ({
        id: job.id,
        status: job.status,
        jobType: job.jobType || job.job_type,
        enhancementType: job.enhancementType || job.enhancement_type,
        creditsUsed: job.creditsUsed || job.credits_used || 0,
        createdAt: job.createdAt || job.created_at,
        completedAt: job.completedAt || job.completed_at,
        result: job.result,
        fileName: job.fileName || job.filename || (job.fileId ? `File ${job.fileId.replace('local-', '')}` : 'Unknown file')
      }));
      setJobHistory(normalizedJobs);
    } catch (error) {
      console.error('Failed to fetch job history:', error);
    }
  };

  // Function to refresh bonus status
  const refreshBonusStatus = async () => {
    if (!user) return;

    try {
      const userDocRef = doc(db, 'users', user.id);
      const snap = await getDoc(userDocRef);

      if (snap.exists()) {
        const data = snap.data();
        const hasClaimedBonus = !!data.signupBonusGranted;
        const isEmailVerified = !!data.emailVerified || !!(auth.currentUser && auth.currentUser.emailVerified);

        setBonusGranted(hasClaimedBonus);
        setEmailVerified(isEmailVerified);
        prevEmailVerified.current = isEmailVerified;

        console.log('Bonus status refreshed:', { hasClaimedBonus, isEmailVerified });
      }
    } catch (e) {
      console.error('Error refreshing bonus status:', e);
    }
  };

  const handleFileUploaded = (fileData) => {
    setUploadedFile(fileData);
    setCurrentJob(null);
    setLoading(false); // Reset loading state when a new file is uploaded
  };

  const handleProcessFile = async (processData) => {
    setLoading(true);
    setCurrentJob(null);

    // Show a toast to indicate processing has started
    toast.loading('Starting AI processing...', { id: 'process-start' });
    logJobAction('start', processData.jobType, null);

    // Determine EU and consent before processing to decide persistence behavior
    let isEU = false;
    let consentGranted = false;
    try {
      if (user?.id) {
        const userRef = doc(db, 'users', user.id);
        const snap = await getDoc(userRef);
        const consentObj = snap.exists() ? (snap.data()?.processedFileSaveConsent || null) : null;
        if (consentObj && typeof consentObj.granted === 'boolean') {
          consentGranted = !!consentObj.granted;
        }
      }
      const countryCode = await detectCountryCode();
      isEU = EU_COUNTRY_CODES.has(countryCode);
    } catch (e) {
      isEU = false;
    }

    try {
      // Get the file from the uploaded file data
      let file = null;

      if (uploadedFile.isLocalFile && uploadedFile.originalFile) {
        // For local files, use the original File object
        file = uploadedFile.originalFile;
        console.log("Using local file for client-side processing");
      } else {
        // For files already uploaded to Firebase Storage, fetch them
        try {
          console.log("Fetching file from Firebase Storage");

          // Check if we have a valid file path
          if (!uploadedFile.file_path) {
            throw new Error('Invalid file path');
          }

          // Create a reference to the file
          const storage = getStorage();
          const fileRef = ref(storage, uploadedFile.file_path);
          const downloadURL = await getDownloadURL(fileRef);

          // Fetch the file
          const response = await fetch(downloadURL);
          const blob = await response.blob();

          // Create a File object
          file = new File([blob], uploadedFile.filename, {
            type: uploadedFile.contentType || response.headers.get('content-type')
          });
        } catch (fetchError) {
          console.error("Error fetching file from Storage:", fetchError);
          throw new Error("Could not fetch file from storage: " + fetchError.message);
        }
      }

      // Process with Gemini based on file type
      let result;
      if (processData.jobType.startsWith('excel_')) {
        result = await processExcelWithGemini(file, processData.jobType, processData.preUpsells || null);
      } else if (processData.jobType.startsWith('document_')) {
        result = await processDocumentWithGemini(file, processData.jobType, processData.enhancementType, processData.preUpsells || null);
      } else {
        throw new Error('Unsupported job type');
      }

      console.log("Client-side processing completed:", result);

      // For local files, we need to create a record in Firestore for the file
      let fileId = uploadedFile.id;

      if (uploadedFile.isLocalFile) {
        try {
          // Only save metadata about the file, not the actual file
          const fileMetadata = {
            filename: uploadedFile.filename,
            fileType: uploadedFile.fileType,
            fileSize: uploadedFile.file_size,
            userId: user.id,
            uploadedAt: serverTimestamp(),
            isLocalProcessed: true // Flag indicating this is a metadata-only entry
          };

          // Add to Firestore
          const docRef = await addDoc(collection(db, "files"), fileMetadata);
          fileId = docRef.id;
          console.log("File metadata saved to Firestore without storage upload");
        } catch (firestoreError) {
          console.error("Error saving metadata to Firestore:", firestoreError);
          // Continue with local ID if Firestore fails
        }
      }

      // Create the job in Firestore now that we have the result
      const jobData = await createAIJob(
        fileId,
        processData.jobType,
        processData.enhancementType,
        user.id,
        null,
        processData.preUpsells || null
      );

      // Clean the result to remove non-serializable objects before storing in Firestore
      const cleanResult = { ...result };

      // Handle enhanced file blob if it exists — only persist if allowed
      if ((!isEU || consentGranted) && cleanResult.enhanced_file && cleanResult.enhanced_file instanceof Blob) {
        try {
          // Upload the enhanced file to Firebase Storage
          const storage = getStorage();
          const enhancedFilePath = `enhanced/${user.id}/${jobData.id}_enhanced.${uploadedFile.fileType || 'xlsx'}`;
          const enhancedFileRef = ref(storage, enhancedFilePath);

          // Upload the blob to Firebase Storage
          const { uploadBytes: uploadBytesFunc } = await import('firebase/storage');
          await uploadBytesFunc(enhancedFileRef, cleanResult.enhanced_file);

          // Get download URL
          const downloadURL = await getDownloadURL(enhancedFileRef);

          // Replace the Blob with the download URL in the result
          cleanResult.enhanced_file_url = downloadURL;
          cleanResult.enhanced_file_path = enhancedFilePath;

          // Remove the Blob object
          delete cleanResult.enhanced_file;

          console.log("Enhanced file uploaded to Storage:", enhancedFilePath);
        } catch (uploadError) {
          console.error("Error uploading enhanced file:", uploadError);
          delete cleanResult.enhanced_file;
        }
      } else if (cleanResult.enhanced_file) {
        // Do not persist file in EU without consent
        delete cleanResult.enhanced_file;
      }

      // Remove any other potential Blob objects
      if (cleanResult._enhancedFileBlob) {
        delete cleanResult._enhancedFileBlob;
      }

      // Update the job with the cleaned result — persist only if allowed
      await updateDoc(doc(db, "jobs", jobData.id), {
        status: 'completed',
        result: (!isEU || consentGranted) ? cleanResult : null,
        completedAt: serverTimestamp()
      });

      // Update the UI with the completed job (keep full result in-session for download)
      const formattedJob = {
        job_id: jobData.id,
        status: 'completed',
        file_id: fileId,
        job_type: processData.jobType,
        enhancement_type: processData.enhancementType,
        credits_used: jobData.creditsUsed || calculateCreditsRequired(processData.jobType),
        result: result,
        created_at: new Date().toISOString(),
        completed_at: new Date().toISOString()
      };

      setCurrentJob(formattedJob);
      refreshUser(); // Update credits
      fetchJobHistory(); // Refresh job history

      // Dismiss the loading toast and show success
      toast.dismiss('process-start');
      toast.success('AI processing completed!');
      logJobAction('complete', processData.jobType, jobData.id);

      // Reset loading state
      setLoading(false);

      // Return success
      return true;

    } catch (clientError) {
      console.error("❌❌❌ DETAILED Client-side processing failed:", clientError);
      console.error("❌❌❌ Error message:", clientError.message);
      console.error("❌❌❌ Error stack:", clientError.stack);
      // Dismiss the loading toast and show error
      toast.dismiss('process-start');
      toast.error(`Client-side processing failed: ${clientError.message}. Falling back to server...`);

      // Don't reset loading state here as we're falling back to server processing

      // For local files that failed client-side processing, we need to upload them first
      let fileId = uploadedFile.id;

      if (uploadedFile.isLocalFile && uploadedFile.originalFile) {
        try {
          // Upload the file to Firebase Storage
          const fileData = await uploadFile(uploadedFile.originalFile, user.id);
          fileId = fileData.id;
          console.log("Local file uploaded to Firebase Storage for server processing");
        } catch (uploadError) {
          console.error("Error uploading local file:", uploadError);
          toast.error("Failed to upload file for server processing");
          setLoading(false);
          throw new Error("Failed to upload file for server processing");
        }
      }

      // Fall back to server-side processing
      const jobData = await createAIJob(
        fileId,
        processData.jobType,
        processData.enhancementType,
        user.id
      );

      console.log("Job created:", jobData);

      // Ensure consistent property names
      const formattedJob = {
        job_id: jobData.id,
        status: jobData.status || 'processing',
        file_id: fileId,
        job_type: processData.jobType,
        enhancement_type: processData.enhancementType,
        credits_used: jobData.creditsUsed || calculateCreditsRequired(processData.jobType),
        created_at: new Date().toISOString()
      };

      setCurrentJob(formattedJob);
      refreshUser(); // Update credits

      // Update toast to indicate server processing
      toast.dismiss('process-start');
      toast.loading('Processing on server...', { id: 'server-process' });

      // Poll for job status
      pollJobStatus(jobData.id);

      // Return success
      return true;
    }
  };

  // Store the active polling interval
  const [activePollingInterval, setActivePollingInterval] = useState(null);

  // Clean up polling when component unmounts or when a new file is uploaded
  useEffect(() => {
    return () => {
      if (activePollingInterval) {
        clearInterval(activePollingInterval);
      }
    };
  }, [activePollingInterval]);

  // Reset polling when a new file is uploaded
  useEffect(() => {
    if (activePollingInterval) {
      clearInterval(activePollingInterval);
      setActivePollingInterval(null);
    }
  }, [uploadedFile]);

  const pollJobStatus = async (jobId) => {
    // Clear any existing polling interval
    if (activePollingInterval) {
      clearInterval(activePollingInterval);
    }

    // Use a shorter polling interval (2 seconds instead of 3)
    const newPollInterval = setInterval(async () => {
      try {
        console.log("Polling job status for:", jobId);
        const job = await getJobStatus(jobId);

        if (job) {
          // Ensure the job object has consistent property names
          const formattedJob = {
            job_id: job.id,
            status: job.status,
            file_id: job.fileId || currentJob?.file_id,
            job_type: job.jobType || currentJob?.job_type,
            enhancement_type: job.enhancementType || currentJob?.enhancement_type,
            credits_used: job.creditsUsed || currentJob?.credits_used,
            result: job.result,
            created_at: job.createdAt || currentJob?.created_at,
            completed_at: job.completedAt
          };

          setCurrentJob(formattedJob);

          if (job.status === 'completed' || job.status === 'failed') {
            console.log(`Job ${jobId} is ${job.status}, stopping polling`);
            clearInterval(newPollInterval);
            setActivePollingInterval(null);
            fetchJobHistory(); // Refresh history

            // Dismiss the server processing toast
            toast.dismiss('server-process');

            if (job.status === 'completed') {
              // If EU and no consent, remove persisted result but keep in-session currentJob
              try {
                const userRef = doc(db, 'users', user.id);
                const snap = await getDoc(userRef);
                const consentObj = snap.exists() ? (snap.data()?.processedFileSaveConsent || null) : null;
                const consentGranted = typeof consentObj?.granted === 'boolean' ? !!consentObj.granted : false;
                const code = await detectCountryCode();
                const isEU = EU_COUNTRY_CODES.has(code);
                if (isEU && !consentGranted) {
                  try {
                    await updateDoc(doc(db, 'jobs', job.id), { result: null });
                  } catch (e) {
                    // ignore
                  }
                }
              } catch (e) {
                // ignore
              }
              toast.success('AI processing completed!');
            } else {
              toast.error('AI processing failed');
            }
          }
        }
      } catch (error) {
        console.error('Failed to poll job status:', error);
        // Don't clear the interval on error, just log and continue polling
      }
    }, 2000); // Poll every 2 seconds instead of 3

    // Store the interval for cleanup
    setActivePollingInterval(newPollInterval);

    // Stop polling after 30 seconds (reduced from 60 seconds)
    setTimeout(() => {
      if (activePollingInterval === newPollInterval) {
        clearInterval(newPollInterval);
        setActivePollingInterval(null);
        console.log(`Polling timeout reached for job ${jobId}, forcing completion`);
      }

      // Auto-complete the job if it's still processing after timeout
      setCurrentJob(prev => {
        if (prev && prev.status === 'processing') {
          // Force one final check of the job status
          getJobStatus(jobId).then(finalJob => {
            if (finalJob && finalJob.status === 'completed') {
              // If the job completed during our final check, use that data
              const formattedFinalJob = {
                job_id: finalJob.id,
                status: finalJob.status,
                file_id: finalJob.fileId || prev.file_id,
                job_type: finalJob.jobType || prev.job_type,
                enhancement_type: finalJob.enhancementType || prev.enhancement_type,
                credits_used: finalJob.creditsUsed || prev.credits_used,
                result: finalJob.result,
                created_at: finalJob.createdAt || prev.created_at,
                completed_at: finalJob.completedAt
              };
              setCurrentJob(formattedFinalJob);
              toast.success('AI processing completed!');
            }
          }).catch(err => {
            console.error('Error in final job status check:', err);
          });

          // Dismiss any processing toasts
          toast.dismiss('process-start');
          toast.dismiss('server-process');
          toast.info('Processing completed');
          fetchJobHistory(); // Refresh history
          return {
            ...prev,
            status: 'completed',
            result: {
              analysis: "Processing completed successfully.",
              processingTime: "30+ seconds"
            },
            completed_at: new Date().toISOString()
          };
        }
        return prev;
      });
    }, 30000); // 30 seconds timeout (reduced from 60 seconds)
  };

  const refreshJobStatus = async () => {
    if (!currentJob) return;

    try {
      // Handle either job_id or id property
      const jobId = currentJob.job_id || currentJob.id;
      console.log("Refreshing job status for:", jobId);

      const job = await getJobStatus(jobId);
      if (job) {
        // Ensure the job object has consistent property names
        const updatedJob = {
          job_id: job.id,
          status: job.status,
          file_id: job.fileId || currentJob.file_id,
          job_type: job.jobType || currentJob.job_type,
          enhancement_type: job.enhancementType || currentJob.enhancement_type,
          credits_used: job.creditsUsed || currentJob.credits_used,
          result: job.result,
          created_at: job.createdAt || currentJob.created_at,
          completed_at: job.completedAt
        };

        console.log("Updated job data:", updatedJob);
        setCurrentJob(updatedJob);

        // Also refresh job history to update the list
        await fetchJobHistory();
      }
    } catch (error) {
      console.error('Failed to refresh job status:', error);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-3 sm:py-8">
      <div className="mb-4 sm:mb-8">
        <h1 className="text-xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">Dashboard</h1>
        <p className="text-sm sm:text-base text-gray-600">Upload and process your documents with AI</p>

        {/* Loading State */}
        {user && !flagsLoaded && (
          <div className="mt-3 sm:mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-center">
              <ArrowPathIcon className="h-5 w-5 text-gray-400 mr-2 animate-spin" />
              <p className="text-sm text-gray-600">Loading account status...</p>
            </div>
          </div>
        )}

        {/* Signup Bonus Section - Show when not claimed yet */}
        {user && flagsLoaded && !bonusGranted && (
          <div className="mt-3 sm:mt-4 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <GiftIcon className="h-5 w-5 text-yellow-600 mr-2" />
                  <p className="text-sm sm:text-base font-medium text-yellow-800">🎉 Welcome Bonus: 100 Free Credits!</p>
                </div>
                {!emailVerified ? (
                  <div>
                    <p className="text-xs sm:text-sm text-yellow-700 mb-2">Verify your email address to claim your welcome bonus of 100 credits.</p>
                    <p className="text-xs text-yellow-600">Check your email (including Spam/Junk folder) for the verification link.</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs sm:text-sm text-yellow-700 mb-2">🎯 Your email is verified! Click the button to claim your 100 free credits.</p>
                    <p className="text-xs text-yellow-600">This bonus can only be claimed once per account.</p>
                  </div>
                )}
              </div>
              <div className="mt-2 sm:mt-0 flex gap-2 flex-shrink-0">
                {!emailVerified ? (
                  <>
                    <button
                      onClick={handleSendVerification}
                      disabled={sendingVerify || resendCooldownSec > 0}
                      className="px-3 py-1.5 text-xs sm:text-sm rounded-md bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-50 transition-colors"
                    >
                      {sendingVerify ? 'Sending…' : resendCooldownSec > 0 ? `Resend in ${resendCooldownSec}s` : 'Send verification'}
                    </button>
                    <button
                      onClick={handleCheckVerified}
                      disabled={checkingStatus}
                      className="px-3 py-1.5 text-xs sm:text-sm rounded-md border border-yellow-600 text-yellow-700 hover:bg-yellow-100 disabled:opacity-50 transition-colors"
                    >
                      {checkingStatus ? 'Checking…' : 'I verified, refresh'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleClaimBonus}
                    disabled={claimingBonus}
                    className="px-4 py-2 text-sm sm:text-base rounded-md bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 disabled:opacity-50 transition-all transform hover:scale-105 font-medium shadow-sm"
                  >
                    {claimingBonus ? (
                      <div className="flex items-center">
                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                        Claiming...
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <GiftIcon className="h-4 w-4 mr-2" />
                        Claim 100 Credits
                      </div>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}



        {/* Quick Stats */}
        <div className="mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
          <div className="bg-white p-3 sm:p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow card-hover">
            <div className="flex items-center">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CreditCardIcon className="h-4 w-4 sm:h-6 sm:w-6 text-green-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-500">Available Credits</p>
                <div className="flex items-baseline">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">{user?.credits || 0}</p>
                  <Link to="/pricing" className="ml-2 text-xs text-green-600 hover:text-green-800 font-medium">
                    + Add More
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-3 sm:p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow card-hover">
            <div className="flex items-center">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <DocumentTextIcon className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-500">Jobs Completed</p>
                <div className="flex items-baseline">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">
                    {jobHistory.filter(job => job.status === 'completed').length}
                  </p>
                  <Link to="/history" className="ml-2 text-xs text-blue-600 hover:text-blue-800 font-medium">
                    View All
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-3 sm:p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow card-hover">
            <div className="flex items-center">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <GiftIcon className="h-4 w-4 sm:h-6 sm:w-6 text-purple-600" />
              </div>
              <div className="ml-3 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-500">Referrals</p>
                <div className="flex items-baseline">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">
                    {user?.referral_stats?.referred_count || 0}
                  </p>
                  <Link to="/referrals" className="ml-2 text-xs text-purple-600 hover:text-purple-800 font-medium">
                    Invite Friends
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-3 sm:gap-6 lg:gap-8">
        {/* Left Column */}
        <div className="space-y-3 sm:space-y-6 lg:space-y-8">
          {/* File Upload */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center mb-2 sm:mb-4">
              <div className="w-7 h-7 sm:w-10 sm:h-10 bg-indigo-100 rounded-full flex items-center justify-center mr-2 sm:mr-3">
                <CloudArrowUpIcon className="h-3 w-3 sm:h-5 sm:w-5 text-indigo-600" />
              </div>
              <h2 className="text-base sm:text-xl font-semibold text-gray-900">Upload Document</h2>
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-4">
              Upload your file to get started. Accepted formats are MS Word, PDF documents and MS excel files.
            </p>

            {/* AI Disclaimer */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <div className="flex items-start space-x-2">
                <ExclamationCircleIcon className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs">
                  <p className="font-medium text-amber-800 mb-1">AI Processing Notice</p>
                  <p className="text-amber-700">
                    AI may modify or change parts of your document during processing. Please review the final enhanced document carefully before using it for any important purposes.
                  </p>
                </div>
              </div>
            </div>

            <FileUploadZone
              onFileUploaded={handleFileUploaded}
              uploadedFile={uploadedFile}
              onCancelUpload={() => {
                setUploadedFile(null);
                setCurrentJob(null);
                toast('File upload cancelled', { icon: '❌' });
              }}
            />
          </div>

          {/* AI Processing */}
          <div className={`transition-all duration-300 ${uploadedFile ? 'opacity-100 transform translate-y-0' : 'opacity-70 transform translate-y-4'}`}>
            <AIProcessingCard
              onProcess={handleProcessFile}
              disabled={loading || (currentJob && currentJob.status === 'processing')}
              uploadedFile={uploadedFile}
            />
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
          {/* Current Job Status */}
          {currentJob && (
            <div className="transition-all duration-500 transform slide-in-right">
              <JobStatusCard job={currentJob} onRefresh={refreshJobStatus} />
            </div>
          )}

          {/* Job History */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3 sm:mb-6">
              <div className="flex items-center">
                <div className="w-7 h-7 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center mr-2 sm:mr-3">
                  <ClockIcon className="h-3 w-3 sm:h-5 sm:w-5 text-blue-600" />
                </div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900">Recent Jobs</h3>
              </div>
              <Link to="/history" className="text-xs sm:text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center">
                View All
                <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
              </Link>
            </div>

            {jobHistory.length === 0 ? (
              <div className="text-center py-6 sm:py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <DocumentTextIcon className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
                <h3 className="mt-2 text-sm sm:text-base font-medium text-gray-900">No jobs yet</h3>
                <p className="mt-1 text-xs sm:text-sm text-gray-500">
                  Process your first file to see your job history here.
                </p>
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-4">
                {jobHistory.slice(0, 5).map((job) => (
                  <div key={job.id} className="flex items-center justify-between p-2 sm:p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center min-w-0">
                      <div className={`w-6 h-6 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${job.status === 'completed' ? 'bg-green-100' :
                        job.status === 'failed' ? 'bg-red-100' :
                          'bg-yellow-100'
                        }`}>
                        {job.status === 'completed' ? (
                          <CheckCircleIcon className="h-3 w-3 sm:h-5 sm:w-5 text-green-600" />
                        ) : job.status === 'failed' ? (
                          <XCircleIcon className="h-3 w-3 sm:h-5 sm:w-5 text-red-600" />
                        ) : (
                          <ClockIcon className="h-3 w-3 sm:h-5 sm:w-5 text-yellow-600" />
                        )}
                      </div>
                      <div className="ml-2 sm:ml-3 min-w-0 max-w-[100px] sm:max-w-[200px]">
                        <p className="font-medium text-xs sm:text-sm text-gray-900 truncate">
                          {job.enhancementType ?
                            job.enhancementType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) :
                            job.jobType ? job.jobType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) :
                              job.enhancement_type ?
                                job.enhancement_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) :
                                job.job_type ? job.job_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown'
                          }
                        </p>
                        <p className="text-xs text-gray-500 truncate" title={job.fileName}>
                          {job.fileName || 'Unknown file'}
                        </p>
                        <p className="text-xs text-gray-500 flex items-center mt-1 hidden sm:flex">
                          <CalendarIcon className="h-3 w-3 mr-1" />
                          {formatDate(job.createdAt || job.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-end sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 flex-shrink-0">
                      <span className={`px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-xs font-medium whitespace-nowrap ${job.status === 'completed' ? 'bg-green-100 text-green-800' :
                        job.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                        {job.status}
                      </span>
                      <span className="text-xs text-gray-700 font-medium whitespace-nowrap bg-gray-100 px-2 py-0.5 sm:py-1 rounded">
                        {job.creditsUsed || job.credits_used || 0} cr
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Enhanced Pricing Page
function PricingPage() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPackages, setProcessingPackages] = useState({});
  const [showCreditModal, setShowCreditModal] = useState(false);
  const { user, refreshUser } = useAuth();
  const [jobTypes, setJobTypes] = useState({});
  const [paymentProvider, setPaymentProvider] = useState('stripe');
  const [userCountry, setUserCountry] = useState('US');
  const [currency, setCurrency] = useState('USD');
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const [promoInput, setPromoInput] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);

      const regionData = await detectUserRegion();
      const { country: detectedCountry, currency: detectedCurrency, symbol: detectedSymbol, provider: detectedProvider } = regionData;

      setUserCountry(detectedCountry);
      setCurrency(detectedCurrency);
      setCurrencySymbol(detectedSymbol);
      setPaymentProvider(detectedProvider);

      // Create packages based on detected country
      const isIndia = detectedCountry === 'IN';
      const finalCurrency = isIndia ? 'INR' : 'USD';
      const finalSymbol = isIndia ? '₹' : '$';
      const finalProvider = isIndia ? 'razorpay' : 'stripe';

      const packagesData = {
        packages: [
          {
            package_id: "starter",
            credits: 50,
            price: isIndia ? 249 : 5.00,
            currency: finalCurrency,
            symbol: finalSymbol,
            description: "Starter Pack",
            validity: "3 months",
            recommended: false
          },
          {
            package_id: "small",
            credits: 100,
            price: isIndia ? 500 : 10.00,
            currency: finalCurrency,
            symbol: finalSymbol,
            description: "Small Pack",
            validity: "3 months",
            recommended: false
          },
          {
            package_id: "medium",
            credits: 250,
            price: isIndia ? 1000 : 22.00,
            currency: finalCurrency,
            symbol: finalSymbol,
            description: "Popular Pack",
            validity: "3 months",
            recommended: true
          },
          {
            package_id: "large",
            credits: 500,
            price: isIndia ? 1800 : 40.00,
            currency: finalCurrency,
            symbol: finalSymbol,
            description: "Pro Pack",
            validity: "6 months",
            recommended: false
          },
          {
            package_id: "enterprise",
            credits: 1000,
            price: isIndia ? 3200 : 75.00,
            currency: finalCurrency,
            symbol: finalSymbol,
            description: "Enterprise Pack",
            validity: "6 months",
            recommended: false
          },
          {
            package_id: "mega",
            credits: 2500,
            price: isIndia ? 7499 : 150.00,
            currency: finalCurrency,
            symbol: finalSymbol,
            description: "Mega Pack",
            validity: "6 months",
            recommended: false
          }
        ],
        country: detectedCountry,
        currency: finalCurrency,
        symbol: finalSymbol,
        payment_provider: finalProvider,
        job_types: {
          // Excel processing options
          "excel_formatting": { "cost": 5, "description": "Plain formatting and structure" },
          "excel_analysis": { "cost": 10, "description": "Data analysis with bullet-point insights" },
          "excel_dashboard": { "cost": 15, "description": "Auto-generated charts and KPIs" },

          // Document enhancement types with varying costs
          "document_business": { "cost": 8, "description": "Business letter enhancement" },
          "document_blog": { "cost": 10, "description": "Blog post optimization" },
          "document_academic": { "cost": 12, "description": "Academic paper improvement" },
          "document_general": { "cost": 8, "description": "General proofreading" },

          // Legacy support
          "excel_summary": { "cost": 10, "description": "Excel summary (legacy)" },
          "document_enhancement": { "cost": 10, "description": "Document enhancement (legacy)" }
        }
      };

      console.log("Created packages based on frontend detection:", packagesData);

      setPackages(packagesData.packages);
      setJobTypes(packagesData.job_types);
      setPaymentProvider(finalProvider);
      setUserCountry(detectedCountry);
      setCurrency(finalCurrency);
      setCurrencySymbol(finalSymbol);
    } catch (error) {
      console.error("Error fetching packages:", error);

      // Fallback to hardcoded packages based on detected country
      console.log("Using hardcoded packages for country:", userCountry);

      const isIndia = userCountry === 'IN';
      const fallbackCurrency = isIndia ? 'INR' : 'USD';
      const fallbackSymbol = isIndia ? '₹' : '$';
      const fallbackProvider = isIndia ? 'razorpay' : 'stripe';

      const fallbackPackages = [
        {
          package_id: "starter",
          credits: 50,
          price: isIndia ? 249 : 2.99,
          currency: fallbackCurrency,
          symbol: fallbackSymbol,
          description: "Starter Pack",
          validity: "3 months",
          recommended: false
        },
        {
          package_id: "small",
          credits: 100,
          price: isIndia ? 500 : 5.99,
          currency: fallbackCurrency,
          symbol: fallbackSymbol,
          description: "Small Pack",
          validity: "3 months",
          recommended: false
        },
        {
          package_id: "medium",
          credits: 250,
          price: isIndia ? 1000 : 11.99,
          currency: fallbackCurrency,
          symbol: fallbackSymbol,
          description: "Popular Pack",
          validity: "3 months",
          recommended: true
        },
        {
          package_id: "large",
          credits: 500,
          price: isIndia ? 1800 : 21.99,
          currency: fallbackCurrency,
          symbol: fallbackSymbol,
          description: "Pro Pack",
          validity: "6 months",
          recommended: false
        },
        {
          package_id: "enterprise",
          credits: 1000,
          price: isIndia ? 3200 : 39.99,
          currency: fallbackCurrency,
          symbol: fallbackSymbol,
          description: "Enterprise Pack",
          validity: "6 months",
          recommended: false
        },
        {
          package_id: "mega",
          credits: 2500,
          price: isIndia ? 7499 : 89.99,
          currency: fallbackCurrency,
          symbol: fallbackSymbol,
          description: "Mega Pack",
          validity: "6 months",
          recommended: false
        }
      ];

      // Define job types
      const jobTypesData = {
        // Excel processing options
        "excel_formatting": { "cost": 5, "description": "Plain formatting and structure" },
        "excel_analysis": { "cost": 10, "description": "Data analysis with bullet-point insights" },
        "excel_dashboard": { "cost": 15, "description": "Auto-generated charts and KPIs" },

        // Document enhancement types with varying costs
        "document_business": { "cost": 8, "description": "Business letter enhancement" },
        "document_blog": { "cost": 10, "description": "Blog post optimization" },
        "document_academic": { "cost": 12, "description": "Academic paper improvement" },
        "document_general": { "cost": 8, "description": "General proofreading" },

        // Legacy support
        "excel_summary": { "cost": 10, "description": "Excel summary (legacy)" },
        "document_enhancement": { "cost": 10, "description": "Document enhancement (legacy)" }
      };

      setPackages(fallbackPackages);
      setJobTypes(jobTypesData);
      setPaymentProvider(fallbackProvider);
      setCurrency(fallbackCurrency);
      setCurrencySymbol(fallbackSymbol);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packageId) => {
    setProcessingPackages(prev => ({ ...prev, [packageId]: true }));

    try {
      console.log(`Initiating purchase for package: ${packageId}`);

      if (!user) {
        toast.error('You must be logged in to purchase credits');
        return;
      }

      // Find the selected package
      const selectedPackage = packages.find(pkg => pkg.package_id === packageId);
      if (!selectedPackage) {
        throw new Error('Package not found');
      }

      // Import the appropriate function based on user's country
      if (userCountry.trim().toUpperCase() === 'IN' || paymentProvider === 'razorpay') {
        console.log("Using Razorpay for payment");
        const { createRazorpayOrder, verifyRazorpayPayment } = await import('./firebase/functions');

        // Get the user's IP address for Razorpay order
        let ip = '';
        try {
          const ipResponse = await fetch('https://api.ipify.org?format=json');
          const ipData = await ipResponse.json();
          ip = ipData.ip;
          console.log("User IP detected for Razorpay order:", ip);
        } catch (ipError) {
          console.error("Error detecting IP:", ipError);
        }

        // Create Razorpay order
        const orderData = await createRazorpayOrder(
          selectedPackage.price,
          selectedPackage.currency || 'INR',
          user.id,
          selectedPackage.credits,
          packageId,
          ip
        );

        console.log("Created Razorpay order:", orderData);

        if (!orderData || !orderData.key) {
          throw new Error("Failed to create payment order");
        }

        // Load Razorpay script if not already loaded
        if (!window.Razorpay) {
          return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => {
              initializeRazorpay();
              resolve();
            };
            document.body.appendChild(script);
          });
        } else {
          initializeRazorpay();
        }

        // Initialize Razorpay in a separate function
        function initializeRazorpay() {
          const options = {
            key: orderData.key,
            amount: orderData.amount,
            currency: orderData.currency || 'INR',
            name: "FileZen",
            description: `Purchase ${selectedPackage.credits} credits`,
            order_id: orderData.orderId,
            image: "https://filezen.app/logo192.png", // Add your logo URL here
            handler: async function (response) {
              try {
                console.log("Razorpay payment successful:", response);

                // Verify the payment
                const result = await verifyRazorpayPayment(
                  response.razorpay_payment_id,
                  response.razorpay_order_id,
                  response.razorpay_signature,
                  user.id
                );

                toast.success(`Payment successful! ${result.credits} credits added to your account.`);
                refreshUser();
              } catch (error) {
                console.error("Error processing payment:", error);
                toast.error('Payment processing failed');
              } finally {
                setProcessingPackages(prev => ({ ...prev, [packageId]: false }));
              }
            },
            prefill: {
              name: user.display_name || '',
              email: user.email || '',
              contact: user.phone || '' // Optional phone number
            },
            modal: {
              ondismiss: function () {
                console.log('Payment modal closed');
                setProcessingPackages(prev => ({ ...prev, [packageId]: false }));
              },
              animation: true
            },
            theme: {
              color: "#4F46E5"
            },
            // Event callbacks for debugging
            notes: {
              address: "FileZen Headquarters"
            },
            retry: {
              enabled: true,
              max_count: 3
            }
          };

          try {
            const razorpay = new window.Razorpay(options);

            // Register callback events for debugging
            razorpay.on('payment.failed', function (response) {
              console.error('Payment failed:', response.error);
              toast.error(`Payment failed: ${response.error.description}`);
              setProcessingPackages(prev => ({ ...prev, [packageId]: false }));
            });

            razorpay.open();
          } catch (err) {
            console.error("Error creating Razorpay instance:", err);
            toast.error("Could not initialize payment system");
            setProcessingPackages(prev => ({ ...prev, [packageId]: false }));
          }
        }
      } else {
        console.log("Using Stripe for payment");
        const { createStripeCheckoutSession } = await import('./firebase/functions');

        // Get the user's IP address for Stripe session
        let ip = '';
        try {
          const ipResponse = await fetch('https://api.ipify.org?format=json');
          const ipData = await ipResponse.json();
          ip = ipData.ip;
          console.log("User IP detected for Stripe session:", ip);
        } catch (ipError) {
          console.error("Error detecting IP:", ipError);
        }

        // Ensure user is properly authenticated
        if (!user || !user.id) {
          throw new Error('User not properly authenticated');
        }

        console.log('Creating Stripe session with user ID:', user.id);

        // Create simplified Stripe checkout session
        const sessionData = await createSimpleStripePayment(
          selectedPackage.price,
          selectedPackage.currency || 'USD',
          user.id,
          selectedPackage.credits,
          packageId
        );

        console.log("Created simplified Stripe session:", sessionData);

        if (!sessionData || !sessionData.sessionUrl) {
          throw new Error("Failed to create checkout session");
        }

        // Redirect to Stripe Checkout
        window.location.href = sessionData.sessionUrl;
      }
    } catch (error) {
      console.error("Error processing purchase:", error);
      toast.error(error.message || 'Failed to process purchase');
      setProcessingPackages(prev => ({ ...prev, [packageId]: false }));
    }
  };

  const onRedeem = async () => {
    if (!user) {
      toast.error('Sign in to redeem a code');
      return;
    }
    const code = (promoInput || '').trim();
    if (!code) {
      toast.error('Enter a promo code');
      return;
    }
    try {
      setRedeemLoading(true);
      const result = await redeemPromoCode(user.id, code);
      toast.success(`Added ${result.creditsAdded} credits`);
      setPromoInput('');
      await refreshUser();
    } catch (e) {
      toast.error(e.message || 'Redemption failed');
    } finally {
      setRedeemLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">Loading pricing...</div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Flexible Pricing</h1>
          <p className="text-xl text-gray-600 mb-4">Pay as you go with our credit system</p>
          {user && (
            <button
              onClick={() => setShowCreditModal(true)}
              className="mt-4 text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Or buy custom credits →
            </button>
          )}
        </div>

        {/* Promo code redemption */}
        <div className="bg-white rounded-lg shadow border p-6 mb-8">
          <h3 className="text-lg font-semibold mb-2">Have a promo code?</h3>
          <p className="text-sm text-gray-600 mb-4">Enter a code to add free credits to your account.</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={promoInput}
              onChange={(e) => setPromoInput(e.target.value)}
              placeholder="PROMO2025"
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
            <button
              onClick={onRedeem}
              disabled={redeemLoading}
              className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {redeemLoading ? 'Redeeming...' : 'Redeem'}
            </button>
          </div>
        </div>

        {/* Preset Packages */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {packages.map((pkg) => (
            <div
              key={pkg.package_id}
              className={`bg-white rounded-lg shadow border p-6 relative hover:shadow-lg transition-shadow ${pkg.recommended ? 'border-indigo-500 ring-2 ring-indigo-500' : ''
                }`}
            >
              {pkg.recommended && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-indigo-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center">
                <h3 className="text-xl font-semibold mb-2">{pkg.description}</h3>
                <div className="mb-4">
                  <span className="text-3xl font-bold">{pkg.symbol || '$'}{pkg.price}</span>
                </div>
                <div className="mb-6">
                  <p className="text-gray-600 font-medium">{pkg.credits} Credits</p>
                  {pkg.validity && (
                    <p className="text-xs text-green-600 mt-1">Valid for {pkg.validity}</p>
                  )}
                </div>

                {user ? (
                  <button
                    onClick={() => handlePurchase(pkg.package_id)}
                    disabled={Object.values(processingPackages).some(Boolean)}
                    className={`w-full py-2 px-4 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed ${processingPackages[pkg.package_id]
                      ? 'bg-indigo-400 text-white'
                      : Object.values(processingPackages).some(Boolean)
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }`}
                  >
                    {processingPackages[pkg.package_id] ? 'Processing...' : 'Purchase'}
                  </button>
                ) : (
                  <Link
                    to="/login"
                    className="block w-full bg-indigo-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-indigo-700 text-center"
                  >
                    Sign In to Purchase
                  </Link>
                )}
                <div className="text-xs text-gray-500 mt-2">
                  Taxes as applicable will be charged subject to jurisdiction and applicable Tax laws
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Per-Job Pricing */}
        <div className="bg-gray-50 rounded-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-center mb-8">Per-Job Pricing</h2>

          {/* Excel Jobs */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <PresentationChartBarIcon className="h-5 w-5 mr-2 text-blue-600" />
              Excel Processing
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg border">
                <h4 className="font-medium">Plain Formatting</h4>
                <p className="text-2xl font-bold text-blue-600 my-2">
                  {jobTypes.excel_formatting?.cost || 5} Credits
                </p>
                <p className="text-sm text-gray-600">
                  {jobTypes.excel_formatting?.description || "Basic structure and formatting improvements"}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border">
                <h4 className="font-medium">Data Analysis</h4>
                <p className="text-2xl font-bold text-blue-600 my-2">
                  {jobTypes.excel_analysis?.cost || 10} Credits
                </p>
                <p className="text-sm text-gray-600">
                  {jobTypes.excel_analysis?.description || "Comprehensive insights with bullet points"}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border">
                <h4 className="font-medium">Dashboard Creation</h4>
                <p className="text-2xl font-bold text-blue-600 my-2">
                  {jobTypes.excel_dashboard?.cost || 15} Credits
                </p>
                <p className="text-sm text-gray-600">
                  {jobTypes.excel_dashboard?.description || "Charts, KPIs and visualizations"}
                </p>
              </div>
            </div>
          </div>

          {/* Document Jobs */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <DocumentDuplicateIcon className="h-5 w-5 mr-2 text-green-600" />
              Document Enhancement
            </h3>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg border">
                <h4 className="font-medium">General Proofreading</h4>
                <p className="text-2xl font-bold text-green-600 my-2">
                  {jobTypes.document_general?.cost || 8} Credits
                </p>
                <p className="text-sm text-gray-600">
                  {jobTypes.document_general?.description || "Grammar and spelling fixes"}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border">
                <h4 className="font-medium">Business Enhancement</h4>
                <p className="text-2xl font-bold text-green-600 my-2">
                  {jobTypes.document_business?.cost || 8} Credits
                </p>
                <p className="text-sm text-gray-600">
                  {jobTypes.document_business?.description || "Professional communication"}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border">
                <h4 className="font-medium">Blog Optimization</h4>
                <p className="text-2xl font-bold text-green-600 my-2">
                  {jobTypes.document_blog?.cost || 10} Credits
                </p>
                <p className="text-sm text-gray-600">
                  {jobTypes.document_blog?.description || "Engaging content creation"}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border">
                <h4 className="font-medium">Academic Improvement</h4>
                <p className="text-2xl font-bold text-green-600 my-2">
                  {jobTypes.document_academic?.cost || 12} Credits
                </p>
                <p className="text-sm text-gray-600">
                  {jobTypes.document_academic?.description || "Scholarly writing enhancement"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div >

      {/* Credit Purchase Modal */}
      {
        showCreditModal && (
          <CreditPurchaseModal
            isOpen={showCreditModal}
            onClose={() => setShowCreditModal(false)}
            onPurchase={refreshUser}
          />
        )
      }
    </>
  );
}

// Enhanced Output Section Component
function OutputSection() {
  const { user } = useAuth();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedResult, setSelectedResult] = useState(null);
  const [downloadFormats, setDownloadFormats] = useState({});

  useEffect(() => {
    if (user) {
      fetchResults();
    }
  }, [user]);

  const fetchResults = async () => {
    try {
      const userJobs = await getUserJobs(user.id);
      const completedJobs = userJobs.filter(job =>
        job.status === 'completed' && job.result
      );
      setResults(completedJobs);

      // Set appropriate default download formats based on original file type
      const formats = {};
      completedJobs.forEach(job => {
        // Use the original file type if available
        if (job.result?.file_type) {
          // Map the file type to supported download formats
          const fileType = job.result.file_type.toLowerCase();
          if (fileType === 'xlsx' || fileType === 'xls') {
            formats[job.id] = 'xlsx';
          } else if (fileType === 'csv') {
            formats[job.id] = 'csv';
          } else if (fileType === 'pdf') {
            formats[job.id] = 'pdf';
          } else if (fileType === 'docx' || fileType === 'doc' || fileType === 'txt') {
            formats[job.id] = 'docx';
          } else {
            formats[job.id] = 'txt';
          }
        } else {
          // Fallback to job type if file type is not available
          if (job.jobType?.includes('document')) {
            formats[job.id] = 'docx';
          } else if (job.jobType?.startsWith('excel_')) {
            formats[job.id] = 'xlsx';
          } else {
            formats[job.id] = 'txt';
          }
        }
      });
      setDownloadFormats(formats);
    } catch (error) {
      console.error('Failed to load results:', error);
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = (result) => {
    try {
      // Create basic PDF without external library first
      const content = typeof result.result === 'object'
        ? result.result.analysis || result.result.enhancement_suggestions || JSON.stringify(result.result, null, 2)
        : result.result;

      const blob = new Blob([
        `FileZen AI Analysis Report\n\n`,
        `Job Type: ${result.jobType}\n`,
        `Credits Used: ${result.creditsUsed}\n`,
        `Completed: ${new Date(result.completedAt).toLocaleString()}\n\n`,
        `Analysis Results:\n`,
        `${'-'.repeat(50)}\n`,
        content
      ], { type: 'text/plain;charset=utf-8' });

      return blob;
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw new Error('Failed to generate PDF');
    }
  };



  const downloadEnhancedResult = async (result, format) => {
    try {
      if (!result || !result.id) {
        throw new Error('Invalid result data');
      }

      // If no format is specified, determine the appropriate format based on job type
      let downloadFormat = format;
      if (!downloadFormat) {
        if (result.jobType?.includes('document') || result.result?.file_type === 'docx' || result.result?.file_type === 'txt') {
          downloadFormat = 'docx';
        } else if (result.jobType?.startsWith('excel_') || result.result?.file_type === 'xlsx') {
          downloadFormat = 'xlsx';
        } else if (result.result?.file_type === 'csv') {
          downloadFormat = 'csv';
        } else if (result.result?.file_type === 'pdf') {
          downloadFormat = 'pdf';
        } else {
          downloadFormat = 'txt';
        }
      }

      const message = await downloadEnhancedFile(result, downloadFormat);
      toast.success(message);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error(`Download failed: ${error.message}`);
    }
  };

  const formatJobType = (jobType) => {
    return jobType.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-2">Loading your results...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Output & Downloads</h1>
        <p className="text-gray-600">View and download your AI analysis results</p>
      </div>

      {results.length === 0 ? (
        <div className="text-center py-12">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No results yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Process some files to see your results here.
          </p>
          <div className="mt-6">
            <Link
              to="/dashboard"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Upload Files
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid gap-6">
          {results
            .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt)) // Sort by completion date, newest first
            .map((result) => (
              <div key={result.id} className="bg-white shadow border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {formatJobType(result.jobType)}
                    </h3>
                    <div className="text-sm text-gray-500 space-y-1">
                      <p>Completed: {new Date(result.completedAt).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</p>
                      <p>Credits Used: {result.creditsUsed} • Job ID: {result.id.slice(-8)}</p>
                      {result.result?.file_type && <p>File Type: {result.result.file_type.toUpperCase()}</p>}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <select
                      value={downloadFormats[result.id]}
                      onChange={(e) => setDownloadFormats({ ...downloadFormats, [result.id]: e.target.value })}
                      className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="xlsx">Enhanced Excel (.xlsx)</option>
                      <option value="csv">Enhanced CSV (.csv)</option>
                      <option value="txt">Analysis Report (.txt)</option>
                      <option value="pdf">Enhanced PDF (.pdf)</option>
                      <option value="docx">Enhanced Word (.docx)</option>
                    </select>
                    <button
                      onClick={() => downloadEnhancedResult(result, downloadFormats[result.id])}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                      Download
                    </button>
                  </div>
                </div>

                {/* Preview Section */}
                <div className="border-t pt-4">
                  <button
                    onClick={() => setSelectedResult(selectedResult === result.id ? null : result.id)}
                    className="flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800 mb-3"
                  >
                    {selectedResult === result.id ? (
                      <ChevronDownIcon className="h-4 w-4 mr-1" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4 mr-1" />
                    )}
                    {selectedResult === result.id ? 'Hide Preview' : 'Show Preview'}
                  </button>

                  {selectedResult === result.id && (
                    <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto border border-gray-200">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-medium text-gray-900">Analysis Results</h4>
                        {result.result && (result.result.enhanced_file || result.result.enhanced_file_url) && (
                          <button
                            onClick={() => downloadEnhancedResult(result, downloadFormats[result.id] || 'xlsx')}
                            className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded flex items-center"
                          >
                            <ArrowDownTrayIcon className="h-3 w-3 mr-1" />
                            Download Enhanced File
                          </button>
                        )}

                      </div>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap custom-scrollbar border-t border-gray-200 pt-3">
                        {typeof result.result === 'object'
                          ? (result.result.analysis ||
                            result.result.enhancement_suggestions ||
                            (result.result.summary && (
                              <>
                                <div className="font-medium mb-2">Summary:</div>
                                <div className="mb-4">{result.result.summary}</div>
                                {result.result.details && (
                                  <>
                                    <div className="font-medium mb-2">Details:</div>
                                    <div>{result.result.details}</div>
                                  </>
                                )}
                              </>
                            )) ||
                            JSON.stringify(result.result, null, 2)
                          )
                          : result.result}
                      </div>
                      {result.result && result.result.data_shape && (
                        <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500 flex flex-wrap gap-2">
                          {result.result.data_shape && (
                            <span className="bg-gray-100 px-2 py-1 rounded">Data: {result.result.data_shape}</span>
                          )}
                          {result.result.file_type && (
                            <span className="bg-gray-100 px-2 py-1 rounded">Type: {result.result.file_type}</span>
                          )}
                          {result.result.model_used && (
                            <span className="bg-gray-100 px-2 py-1 rounded">Model: {result.result.model_used}</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Additional Info */}
                {result.result && typeof result.result === 'object' && (
                  <div className="mt-4 text-xs text-gray-500">
                    {result.result.data_shape && (
                      <span className="mr-4">Data: {result.result.data_shape}</span>
                    )}
                    {result.result.file_type && (
                      <span className="mr-4">Type: {result.result.file_type}</span>
                    )}
                    {result.result.model_used && (
                      <span>Model: {result.result.model_used}</span>
                    )}
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

// New History Page
function HistoryPage() {
  const { user } = useAuth();
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('jobs');
  const [currentCredits, setCurrentCredits] = useState(0);

  useEffect(() => {
    if (user) {
      fetchHistory();
    }
  }, [user]);

  const fetchHistory = async () => {
    try {
      console.log("Fetching user history data...");

      if (!user || !user.id) {
        console.error("No user ID available for fetching history");
        setLoading(false);
        return;
      }

      // Use Firebase getUserHistory function
      const historyData = await getUserHistory(user.id);
      console.log("Received history data:", historyData);

      // Normalize job data to ensure consistent property names
      if (historyData && historyData.jobs) {
        const normalizedJobs = historyData.jobs.map(job => ({
          id: job.id,
          status: job.status,
          jobType: job.jobType || job.job_type,
          enhancementType: job.enhancementType || job.enhancement_type,
          creditsUsed: job.creditsUsed || job.credits_used || 0,
          createdAt: job.createdAt || job.created_at,
          completedAt: job.completedAt || job.completed_at,
          result: job.result,
          fileName: job.fileName || job.fileId ? `File ${job.fileId.replace('local-', '')}` : 'Unknown file'
        }));

        // Sort jobs by date (newest first)
        normalizedJobs.sort((a, b) => {
          const dateA = a.createdAt && typeof a.createdAt === 'object' && a.createdAt.toDate ?
            a.createdAt.toDate().getTime() :
            a.createdAt ? new Date(a.createdAt).getTime() : 0;

          const dateB = b.createdAt && typeof b.createdAt === 'object' && b.createdAt.toDate ?
            b.createdAt.toDate().getTime() :
            b.createdAt ? new Date(b.createdAt).getTime() : 0;

          return dateB - dateA; // Descending order (newest first)
        });

        console.log(`Successfully normalized and sorted ${normalizedJobs.length} jobs`);
        setHistory({
          ...historyData,
          jobs: normalizedJobs
        });

        // Set current credits
        if (historyData.currentCredits !== undefined) {
          setCurrentCredits(historyData.currentCredits);
        }
      } else {
        console.log("No jobs data found in response");
        setHistory(historyData || { jobs: [], transactions: [] });
      }
    } catch (error) {
      console.error('Failed to load history:', error);
      toast.error('Failed to load history');
      setHistory({ jobs: [], transactions: [] });
    } finally {
      setLoading(false);
    }
  };

  // Format job type for display
  const formatJobType = (jobType, enhancementType) => {
    const type = enhancementType || jobType || '';
    return type
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format transaction type for display
  const formatTransactionType = (type, description) => {
    if (!type) return 'Unknown';

    // Special handling for credit_usage
    if (type === 'credit_usage') {
      return 'AI Processing';
    }

    return type
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format date
  const formatDate = (dateStr) => {
    try {
      // Handle Firestore Timestamp objects
      if (dateStr && typeof dateStr === 'object' && dateStr.toDate) {
        return dateStr.toDate().toLocaleDateString() + ' ' +
          dateStr.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }

      // Handle regular date strings or timestamps
      if (dateStr) {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString() + ' ' +
            date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
      }

      return 'Unknown date';
    } catch (e) {
      console.error("Date formatting error:", e);
      return 'Unknown date';
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-lg text-gray-700">Loading history...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Transaction History</h1>
        <p className="mt-2 text-gray-600">View all your AI jobs and credit transactions</p>
      </div>

      {/* Credit Summary */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow mb-8 p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold">Current Credit Balance</h2>
            <p className="text-indigo-100 text-sm mt-1">Use credits to process files with AI</p>
          </div>
          <div className="mt-4 md:mt-0">
            <span className="text-4xl font-bold">{currentCredits}</span>
            <span className="ml-2 text-lg">credits</span>
            <div className="mt-2">
              <Link to="/pricing" className="inline-flex items-center px-4 py-2 bg-white text-indigo-600 rounded-md font-medium text-sm hover:bg-indigo-50 transition-colors">
                <PlusCircleIcon className="h-5 w-5 mr-1" />
                Buy More Credits
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('jobs')}
            className={`${activeTab === 'jobs'
              ? 'border-indigo-500 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            AI Jobs ({history?.jobs?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('transactions')}
            className={`${activeTab === 'transactions'
              ? 'border-indigo-500 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Credit Transactions ({history?.transactions?.length || 0})
          </button>
        </nav>
      </div>

      {/* AI Jobs History */}
      {activeTab === 'jobs' && (
        <div className="bg-white rounded-lg shadow border p-6">
          {!history?.jobs?.length ? (
            <div className="text-center py-12">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-lg font-medium text-gray-900">No AI jobs yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Process your first file to see your job history here.
              </p>
              <div className="mt-6">
                <Link to="/dashboard" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
                  <CloudArrowUpIcon className="h-5 w-5 mr-2" />
                  Upload a File
                </Link>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Job Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Credits
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {history.jobs.map((job) => (
                    <tr key={job.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatJobType(job.jobType, job.enhancementType)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 truncate max-w-xs" title={job.fileName || job.fileId || 'Unknown file'}>
                          {job.fileName || (job.fileId && job.fileId.replace('local-', '')) || 'Unknown file'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {formatDate(job.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${job.status === 'completed' ? 'bg-green-100 text-green-800' :
                          job.status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                          {job.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {job.creditsUsed || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Credit Transactions */}
      {activeTab === 'transactions' && (
        <div className="bg-white rounded-lg shadow border p-6">
          {!history?.transactions?.length ? (
            <div className="text-center py-12">
              <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-lg font-medium text-gray-900">No transactions yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Purchase credits or process files to see your transaction history.
              </p>
              <div className="mt-6">
                <Link to="/pricing" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
                  <CreditCardIcon className="h-5 w-5 mr-2" />
                  Buy Credits
                </Link>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Transaction Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {history.transactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatTransactionType(transaction.transactionType || transaction.transaction_type, transaction.description)}
                        </div>
                        {transaction.description && (
                          <div className="text-xs text-gray-500">
                            {transaction.transactionType === 'credit_usage' && transaction.description.includes('for')
                              ? transaction.description.replace(/Used \d+ credits for /, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                              : transaction.description}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {formatDate(transaction.createdAt || transaction.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                          transaction.status === 'failed' ? 'bg-red-100 text-red-800' :
                            transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                          }`}>
                          {transaction.status || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                        <span className={`${(transaction.amount || 0) > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                          {(transaction.amount || 0) > 0 ? '+' : ''}{transaction.amount || 0} credits
                        </span>
                        {transaction.price && (
                          <div className="text-xs text-gray-500">
                            {transaction.currency || '$'} {transaction.price}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Referrals Page (code + who used it)
function ReferralsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState('');
  const [referredUsers, setReferredUsers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        if (user?.id) {
          const userSnap = await getDoc(doc(db, 'users', user.id));
          const code = userSnap.exists() ? (userSnap.data().referralCode || userSnap.data().referral_code || '') : '';
          setReferralCode(code || '');
          // Query only users that were referred by the current user
          const referredQuery = query(collection(db, 'users'), where('referredBy', '==', user.id));
          const referredSnap = await getDocs(referredQuery);
          const matches = [];
          referredSnap.forEach(u => {
            if (u.id === user.id) return;
            const data = u.data();
            matches.push({ id: u.id, email: data.email, displayName: data.displayName || data.display_name || data.email, createdAt: data.createdAt?.toDate?.() || new Date() });
          });
          matches.sort((a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0));
          setReferredUsers(matches);
        }
      } catch (e) {
        toast.error('Failed to load referrals');
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center text-gray-600">Loading referrals...</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Your Referrals</h1>

      <div className="bg-white rounded-lg shadow border p-6 mb-8">
        <div className="text-sm text-gray-600 mb-1">Your referral code</div>
        <div className="font-mono text-lg">{referralCode || '—'}</div>

      </div>

      <div className="bg-white rounded-lg shadow border p-6">
        <h2 className="text-lg font-semibold mb-4">People who used your code</h2>
        {referredUsers.length === 0 ? (
          <div className="text-gray-500 text-center py-8">No referrals yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Joined</th>
                </tr>
              </thead>
              <tbody>
                {referredUsers.map(u => (
                  <tr key={u.id} className="border-t">
                    <td className="py-2 pr-4">{u.displayName}</td>
                    <td className="py-2 pr-4">{u.email}</td>
                    <td className="py-2 pr-4">{u.createdAt?.toLocaleDateString?.() || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Payment Success Page
function PaymentSuccess() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setIsProcessing(false);
    }
  }, []);

  const checkPaymentStatus = async (sessionId) => {
    try {
      setIsProcessing(true);

      console.log('Checking payment status for session:', sessionId);

      // Check if user is authenticated
      if (!user || !user.id) {
        console.log('User not authenticated, cannot verify payment');
        toast.error('Please sign in to verify your payment');
        setIsProcessing(false);
        return;
      }

      // Use simplified payment verification
      const result = await checkPaymentAndAddCredits(sessionId, user.id);

      if (result.success) {
        toast.success(result.message);
        refreshUser();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Failed to verify payment status. Please contact support.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
      {isProcessing ? (
        <div className="flex flex-col items-center">
          <ClockIcon className="h-16 w-16 text-indigo-500 mb-4 animate-pulse" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Processing Payment...</h1>
          <p className="text-lg text-gray-600">Please wait while we confirm your payment.</p>
        </div>
      ) : (
        <>
          <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500 mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Payment Successful!</h1>
          <p className="text-lg text-gray-600 mb-8">
            Your credits have been added to your account. You can now start processing documents with AI.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 mr-4"
          >
            Go to Dashboard
          </button>
          <button
            onClick={() => navigate('/history')}
            className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50"
          >
            View History
          </button>
        </>
      )}
    </div>
  );
}

// Payment Cancel Page
function PaymentCancel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const sessionId = queryParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      handleCancelPayment(sessionId);
    }
  }, [sessionId]);

  const handleCancelPayment = async (sessionId) => {
    try {
      // Try to cancel the payment
      let result;

      if (user && user.id) {
        // User is authenticated, use the full cancellation
        result = await cancelPayment(sessionId, user.id);
      } else {
        // User is not authenticated, try simple cancellation by session ID only
        console.log("User not authenticated, attempting simple cancellation");
        const response = await fetch('https://us-central1-filezenai.cloudfunctions.net/cancelPaymentApi', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sessionId
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `API endpoint failed with status ${response.status}`);
        }

        result = await response.json();
      }

      console.log("Payment canceled:", result);
      toast.success("Payment was canceled successfully");
    } catch (error) {
      console.error('Error canceling payment:', error);
      // Don't show error toast for cancellation failures as it's not critical
      console.log("Payment cancellation failed, but this is not critical");
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
      <XCircleIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
      <h1 className="text-3xl font-bold text-gray-900 mb-4">Payment Canceled</h1>
      <p className="text-lg text-gray-600 mb-8">
        Your payment was canceled and no credits were added to your account.
      </p>
      <button
        onClick={() => navigate('/dashboard')}
        className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 mr-4"
      >
        Go to Dashboard
      </button>
      <button
        onClick={() => navigate('/pricing')}
        className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50"
      >
        View Pricing
      </button>
    </div>
  );
}

// Email Verification Panel Component
function EmailVerificationPanel() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [sendingVerify, setSendingVerify] = useState(false);
  const [resendCooldownSec, setResendCooldownSec] = useState(0);
  const [emailVerified, setEmailVerified] = useState(false);
  const [checking, setChecking] = useState(false);
  const [claimingCredits, setClaimingCredits] = useState(false);
  const [creditsClaimed, setCreditsClaimed] = useState(false);

  useEffect(() => {
    // Check email verification status on component mount
    checkEmailVerification();
  }, []);

  const checkEmailVerification = async () => {
    try {
      setChecking(true);
      if (!auth.currentUser) return;

      await auth.currentUser.reload();
      const verified = !!auth.currentUser.emailVerified;
      setEmailVerified(verified);

      // If email is verified, refresh the user context
      if (verified) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Error checking email verification:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleSendVerification = async () => {
    try {
      setSendingVerify(true);
      if (!auth.currentUser) {
        toast.error('Not signed in');
        return;
      }
      await sendEmailVerification(auth.currentUser);
      toast.success('Verification email sent! Check your inbox (and spam folder)');
      setResendCooldownSec(60);
    } catch (error) {
      console.error('Send verification failed:', error);
      if (error?.code === 'auth/too-many-requests') {
        toast.error('Too many requests. Please wait before trying again.');
        setResendCooldownSec(prev => Math.max(prev, 120));
      } else if (error?.code === 'auth/invalid-recipient-email') {
        toast.error('Invalid email address.');
      } else if (error?.code === 'auth/quota-exceeded') {
        toast.error('Email quota exceeded temporarily. Try again later.');
      } else {
        toast.error('Failed to send verification email');
      }
    } finally {
      setSendingVerify(false);
    }
  };

  const handleClaimCredits = async () => {
    try {
      // Check if user is authenticated
      if (!auth.currentUser) {
        throw new Error('You must be logged in to claim the signup bonus');
      }

      // Force reload user to ensure we have the latest auth state
      await auth.currentUser.reload();

      // Ensure user is email verified
      if (!auth.currentUser.emailVerified) {
        throw new Error('Please verify your email address before claiming the signup bonus');
      }

      // Force refresh the user's ID token to ensure it's valid
      try {
        const idToken = await auth.currentUser.getIdToken(true);
        console.log('ID token refreshed successfully, length:', idToken.length);

        // Additional check: verify the token is valid by making a test call
        if (idToken.length < 100) {
          throw new Error('Invalid token length');
        }
      } catch (tokenError) {
        console.error('Failed to refresh ID token:', tokenError);
        throw new Error('Authentication token expired. Please refresh the page and try again.');
      }

      setClaimingCredits(true);

      // Add a small delay to ensure auth state is fully established
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Add retry mechanism with exponential backoff
      let lastError;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          console.log(`Attempt ${attempt} to claim credits...`);
          const res = await claimSignupBonus();
          console.log('Credits claimed:', res);
          setCreditsClaimed(true);
          toast.success('🎉 Welcome bonus of 100 credits claimed!');
          await refreshUser(); // Refresh user data to show updated credits
          return; // Success, exit the function
        } catch (error) {
          console.error(`Attempt ${attempt} failed:`, error);
          lastError = error;

          if (error.message?.includes('already claimed')) {
            setCreditsClaimed(true);
            toast.info('Welcome bonus already claimed!');
            return; // Exit on already claimed
          }

          // If this is the last attempt, throw the error
          if (attempt === 3) {
            throw error;
          }

          // Wait before retrying (exponential backoff)
          const delay = Math.pow(2, attempt) * 1000; // 2s, 4s
          console.log(`Waiting ${delay}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    } catch (error) {
      console.error('Error claiming credits:', error);
      if (error.message?.includes('already claimed')) {
        setCreditsClaimed(true);
        toast.info('Welcome bonus already claimed!');
      } else if (error.message?.includes('unauthenticated') || error.message?.includes('logged in') || error.message?.includes('token expired')) {
        toast.error('Authentication error. Please refresh the page and try again.');
      } else if (error.message?.includes('verify your email')) {
        toast.error('Please verify your email address before claiming the signup bonus.');
      } else {
        toast.error('Failed to claim welcome bonus. Please try again.');
      }
    } finally {
      setClaimingCredits(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to logout');
    }
  };



  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldownSec <= 0) return;

    const timer = setTimeout(() => {
      setResendCooldownSec(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearTimeout(timer);
  }, [resendCooldownSec]);

  // Auto-check verification status every 5 seconds
  useEffect(() => {
    if (emailVerified) return;

    const interval = setInterval(async () => {
      try {
        if (!auth.currentUser) return;

        await auth.currentUser.reload();
        const verified = !!auth.currentUser.emailVerified;
        if (verified && !emailVerified) {
          setEmailVerified(true);
          await refreshUser();

          // Check if user has already claimed the bonus by checking their user document
          try {
            const { doc, getDoc } = await import('firebase/firestore');
            const { db } = await import('./firebase/config');
            const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
            if (userDoc.exists() && userDoc.data().signupBonusGranted) {
              console.log('User has already claimed signup bonus');
              setCreditsClaimed(true);
              return;
            }
          } catch (docError) {
            console.error('Error checking user document:', docError);
          }

          // Automatically claim credits when email is verified
          // Add a longer delay to ensure auth state is completely stable
          setTimeout(async () => {
            if (!creditsClaimed && auth.currentUser && auth.currentUser.emailVerified) {
              try {
                // Double-check auth state before claiming
                await auth.currentUser.reload();
                if (auth.currentUser && auth.currentUser.emailVerified) {
                  console.log('Auto-claiming credits for verified user:', auth.currentUser.uid);
                  await handleClaimCredits();
                }
              } catch (error) {
                console.error('Auto-claim credits error:', error);
                // Don't show error toast for auto-claim, let user claim manually
                // The user can still claim manually using the button
              }
            }
          }, 5000); // Increased delay to ensure auth state is completely stable
        }
      } catch (error) {
        console.error('Auto-check verification error:', error);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [emailVerified, refreshUser, creditsClaimed]);

  // Additional auto-claim retry mechanism
  useEffect(() => {
    if (!emailVerified || creditsClaimed) return;

    // If email is verified but credits haven't been claimed after 15 seconds, try again
    const retryTimer = setTimeout(async () => {
      if (emailVerified && !creditsClaimed && auth.currentUser && auth.currentUser.emailVerified) {
        console.log('Retrying credit claim after delay...');
        try {
          // Double-check auth state before retry
          await auth.currentUser.reload();
          if (auth.currentUser && auth.currentUser.emailVerified) {
            await handleClaimCredits();
          }
        } catch (error) {
          console.error('Retry claim credits error:', error);
          // Still don't show error toast, let user claim manually
        }
      }
    }, 15000); // 15 second retry with more robust auth check

    return () => clearTimeout(retryTimer);
  }, [emailVerified, creditsClaimed]);

  // Redirect to dashboard when email is verified and credits are claimed
  useEffect(() => {
    if (emailVerified && creditsClaimed) {
      // Small delay to show the success message
      const timer = setTimeout(() => {
        navigate('/dashboard');
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [emailVerified, creditsClaimed, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
            <EnvelopeIcon className="h-6 w-6 text-yellow-600" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Verify Your Email
          </h2>

          <p className="text-gray-600 mb-6">
            To use FileZen, please verify your email address first.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Email:</strong> {user?.email}
            </p>
          </div>

          {emailVerified ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                <p className="text-sm text-green-800 font-medium">
                  Email verified successfully!
                </p>
              </div>
              {creditsClaimed ? (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <GiftIcon className="h-4 w-4 text-blue-600 mr-2" />
                    <p className="text-xs text-blue-800 font-medium">
                      🎉 Welcome bonus of 100 credits claimed!
                    </p>
                  </div>
                </div>
              ) : claimingCredits ? (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <ArrowPathIcon className="h-4 w-4 text-blue-600 mr-2 animate-spin" />
                    <p className="text-xs text-blue-800">
                      Claiming your welcome bonus...
                    </p>
                  </div>
                </div>
              ) : (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <GiftIcon className="h-4 w-4 text-blue-600 mr-2" />
                      <p className="text-xs text-blue-800">
                        Claim your welcome bonus of 100 credits!
                      </p>
                    </div>
                    <button
                      onClick={handleClaimCredits}
                      disabled={claimingCredits}
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {claimingCredits ? 'Claiming...' : 'Claim'}
                    </button>
                  </div>
                  <div className="mt-2 text-xs text-gray-600">
                    If auto-claim fails, click the button above to claim manually. The system will automatically retry in the background.
                  </div>
                </div>
              )}
              <p className="text-xs text-green-700 mt-2">
                Redirecting you to the dashboard...
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  We've sent a verification email to your inbox. Please check your email and click the verification link.
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Don't see it? Check your spam/junk folder.
                </p>
              </div>

              <div className="flex flex-col space-y-3">
                <button
                  onClick={handleSendVerification}
                  disabled={sendingVerify || resendCooldownSec > 0}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {sendingVerify ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : resendCooldownSec > 0 ? (
                    `Resend in ${resendCooldownSec}s`
                  ) : (
                    <>
                      <EnvelopeIcon className="h-4 w-4 mr-2" />
                      Resend Verification Email
                    </>
                  )}
                </button>

                <button
                  onClick={checkEmailVerification}
                  disabled={checking}
                  className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 flex items-center justify-center"
                >
                  {checking ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-2" />
                      Check Verification Status
                    </>
                  )}
                </button>


              </div>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center mx-auto"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4 mr-1" />
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main App Component with enhanced routing
function AppContent() {
  const { user, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    logPageView(location.pathname);
  }, [location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  // Check if user is logged in but email is not verified (admin accounts bypass verification)
  if (user && !user.emailVerified && !user.isAdmin) {
    return <EmailVerificationPanel />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <NavigationBreadcrumb />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={user ? <Navigate to="/dashboard" /> : <HomePage />} />
          <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <LoginForm />} />
          <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/login" />} />
          <Route path="/output" element={user ? <OutputSection /> : <Navigate to="/login" />} />
          <Route path="/history" element={user ? <HistoryPage /> : <Navigate to="/login" />} />
          <Route path="/referrals" element={user ? <ReferralsPage /> : <Navigate to="/login" />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/admin" element={user && user.isAdmin ? <AdminPanel /> : <Navigate to="/" />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/refund" element={<RefundPolicyPage />} />
          <Route path="/cookies" element={<CookiePolicyPage />} />
          <Route path="/payment-success" element={user ? <PaymentSuccess /> : <Navigate to="/login" />} />
          <Route path="/payment-cancel" element={<PaymentCancel />} />
          <Route path="/verify-email" element={user && !user.isAdmin ? <EmailVerificationPanel /> : <Navigate to="/dashboard" />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router basename="/">
        <div className="min-h-screen bg-gray-50">
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                theme: {
                  primary: 'green',
                  secondary: 'black',
                },
              },
            }}
          />
          <AppContent />
          <Chatbot />
        </div>
      </Router>
    </AuthProvider>
  );
}

function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('promos');
  const [landingTab, setLandingTab] = useState('content');
  const [form, setForm] = useState({ code: '', credits: 50, count: 1, expiresAt: '', active: true });
  const [grantEmail, setGrantEmail] = useState('');
  const [grantAmount, setGrantAmount] = useState('');
  const [creating, setCreating] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [list, setList] = useState([]);
  const [managed, setManaged] = useState([]);
  const [newManagedEmail, setNewManagedEmail] = useState('');
  const [newManagedName, setNewManagedName] = useState('');
  const [newManagedRole, setNewManagedRole] = useState('user');
  const [newManagedCredits, setNewManagedCredits] = useState('0');
  const [newManagedPassword, setNewManagedPassword] = useState('');
  const [userLookupEmail, setUserLookupEmail] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  // Landing content state (admin)
  const [landingTitle, setLandingTitle] = useState('');
  const [landingItems, setLandingItems] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [contentSections, setContentSections] = useState([{ type: 'text', content: '', mediaUrl: '', pendingFile: null }]);
  const [editingLanding, setEditingLanding] = useState(null);
  const [editingLandingId, setEditingLandingId] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedSection, setDraggedSection] = useState(null);
  const [allFeedback, setAllFeedback] = useState([]);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackAuthor, setFeedbackAuthor] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [editFeedbackText, setEditFeedbackText] = useState('');
  const [editFeedbackAuthor, setEditFeedbackAuthor] = useState('');
  const [editFeedbackRating, setEditFeedbackRating] = useState(0);
  const [topJobs, setTopJobs] = useState([]);
  const [topRef, setTopRef] = useState([]);
  const [analyticsTab, setAnalyticsTab] = useState('jobs');
  const [jobsLimit] = useState(10);
  const [refLimit, setRefLimit] = useState(10);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [refLoading, setRefLoading] = useState(false);
  const [globalAnalytics, setGlobalAnalytics] = useState({ totalCreditsUsed: 0, totalRevenue: 0, jobTypeBreakdown: {} });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // User search/pagination state
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userCurrentPage, setUserCurrentPage] = useState(1);
  const [userPagination, setUserPagination] = useState({ totalUsers: 0, totalPages: 1 });

  const refreshLanding = async () => {
    try {
      const items = await listLandingContent();
      setLandingItems(items);
      const fb = await listAllFeedback(50);
      setAllFeedback(fb);
    } catch { }
  };



  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const addContentSection = () => {
    setContentSections([...contentSections, { type: 'text', content: '', mediaUrl: '', pendingFile: null }]);
  };

  const removeContentSection = (index) => {
    if (contentSections.length > 1) {
      // Clean up object URL if this section has a pending file
      const section = contentSections[index];
      if (section.pendingFile && section.mediaUrl) {
        URL.revokeObjectURL(section.mediaUrl);
      }

      const newSections = contentSections.filter((_, i) => i !== index);
      setContentSections(newSections);
    }
  };

  const updateContentSection = (index, field, value) => {
    const newSections = [...contentSections];
    newSections[index] = { ...newSections[index], [field]: value };
    setContentSections(newSections);
  };

  const removeFileFromSection = (index) => {
    const section = contentSections[index];
    if (section.pendingFile && section.mediaUrl) {
      // Clean up the object URL
      URL.revokeObjectURL(section.mediaUrl);
    }
    updateContentSection(index, 'mediaUrl', '');
    updateContentSection(index, 'pendingFile', null);
  };

  const handleSectionFileUpload = (file, index) => {
    // Store file locally for preview, upload will happen on form submission
    const fileUrl = URL.createObjectURL(file);
    updateContentSection(index, 'mediaUrl', fileUrl);
    updateContentSection(index, 'pendingFile', file);
    toast.success('File selected! Upload will happen when you post the content.');
  };

  const handleSectionVideoUpload = (file, index) => {
    // Store file locally for preview, upload will happen on form submission
    const fileUrl = URL.createObjectURL(file);
    updateContentSection(index, 'mediaUrl', fileUrl);
    updateContentSection(index, 'pendingFile', file);
    toast.success('Video selected! Upload will happen when you post the content.');
  };

  const uploadPendingFiles = async (sections) => {
    const updatedSections = [...sections];

    for (let i = 0; i < updatedSections.length; i++) {
      const section = updatedSections[i];
      if (section.pendingFile) {
        try {
          setUploadingFile(true);
          const result = await uploadFile(section.pendingFile, user?.uid);
          updatedSections[i] = {
            ...section,
            mediaUrl: result.download_url,
            pendingFile: null
          };
          // Clean up the object URL
          URL.revokeObjectURL(section.mediaUrl);
        } catch (error) {
          console.error('Upload error:', error);
          throw new Error(`Failed to upload file in section ${i + 1}: ${error.message}`);
        } finally {
          setUploadingFile(false);
        }
      }
    }

    return updatedSections;
  };

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleLandingDrop = async (e, targetItem) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetItem.id) return;

    try {
      const draggedIndex = landingItems.findIndex(item => item.id === draggedItem.id);
      const targetIndex = landingItems.findIndex(item => item.id === targetItem.id);

      // Create new array with reordered items
      const newItems = [...landingItems];
      const [removed] = newItems.splice(draggedIndex, 1);
      newItems.splice(targetIndex, 0, removed);

      // Update order numbers
      const updatedItems = newItems.map((item, index) => ({
        ...item,
        order: index + 1 // Start from 1
      }));

      // Update all items in Firestore
      const updatePromises = updatedItems.map(item =>
        updateLandingContent(item.id, { ...item, order: item.order })
      );

      await Promise.all(updatePromises);
      setLandingItems(updatedItems);
      toast.success('Order updated successfully');
    } catch (error) {
      toast.error('Failed to update order');
    } finally {
      setDraggedItem(null);
    }
  };

  const handleSectionDragStart = (e, sectionIndex) => {
    setDraggedSection(sectionIndex);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSectionDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSectionDrop = (e, targetSectionIndex) => {
    e.preventDefault();
    if (draggedSection === null || draggedSection === targetSectionIndex) return;

    const newSections = [...contentSections];
    const [removed] = newSections.splice(draggedSection, 1);
    newSections.splice(targetSectionIndex, 0, removed);

    setContentSections(newSections);
    setDraggedSection(null);
    toast.success('Section order updated');
  };

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/');
      return;
    }
    refresh();
    refreshUsers();
    refreshLanding();
  }, [user]);

  // Auto-load analytics when Analytics tab is opened
  // Auto-load analytics when Analytics tab is opened
  useEffect(() => {
    if (!user?.isAdmin) return;
    if (activeTab !== 'analytics') return;
    (async () => {
      try {
        setAnalyticsLoading(true);
        const globalStats = await getGlobalAnalytics();
        setGlobalAnalytics(globalStats);
      } catch (e) { console.error(e); } finally { setAnalyticsLoading(false); }

      try {
        setJobsLoading(true);
        const jobsData = await getTopUsersByJobs(jobsLimit, userCurrentPage, userSearchTerm);
        setTopJobs(jobsData.users);
        setUserPagination({ totalUsers: jobsData.totalUsers, totalPages: jobsData.totalPages });
      } catch (e) { } finally { setJobsLoading(false); }

      try {
        setRefLoading(true);
        const refData = await getTopReferrers(refLimit);
        setTopRef(refData);
      } catch (e) { } finally { setRefLoading(false); }
    })();
  }, [activeTab, user, jobsLimit, refLimit, userCurrentPage, userSearchTerm]);

  const refresh = async () => {
    try {
      setLoadingList(true);
      const items = await listPromoCodes(100);
      setList(items);
    } finally {
      setLoadingList(false);
    }
  };

  const refreshUsers = async () => {
    try {
      const items = await listManagedUsers(user.id);
      setManaged(items);
    } catch { }
  };

  const onCreate = async () => {
    try {
      setCreating(true);
      const payload = {
        code: form.code?.trim() || undefined,
        credits: Number(form.credits) || 0,
        count: Number(form.count) || 0,
        active: !!form.active,
      };
      if (form.expiresAt) {
        const dt = new Date(form.expiresAt);
        if (!isNaN(dt.getTime())) payload.expiresAt = dt;
      }
      await createPromoCode(payload, user.id);
      setForm({ code: '', credits: 50, count: 1, expiresAt: '', active: true });
      await refresh();
      toast.success('Promo code saved');
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setCreating(false);
    }
  };

  const onGrant = async () => {
    try {
      if (!grantEmail || !grantAmount) return;
      await grantCreditsToUserByEmail(user.email, grantEmail.trim(), Number(grantAmount));
      toast.success('Credits granted');
      setGrantEmail('');
      setGrantAmount('');
    } catch (e) {
      toast.error(e.message || 'Grant failed');
    }
  };

  const onCreateManaged = async () => {
    try {
      if (!newManagedEmail) return;
      if (!newManagedPassword) { toast.error('Password is required'); return; }
      // Create Auth user via Cloud Function
      await adminCreateUserWithPassword({
        email: newManagedEmail,
        password: newManagedPassword,
        displayName: newManagedName,
        role: newManagedRole,
        initialCredits: Number(newManagedCredits)
      });
      // Track as managed user record for owner scoping
      await createManagedUser({ email: newManagedEmail, displayName: newManagedName, role: newManagedRole, initialCredits: Number(newManagedCredits), createdBy: user.id });
      setNewManagedEmail('');
      setNewManagedName('');
      setNewManagedRole('user');
      setNewManagedCredits('0');
      setNewManagedPassword('');
      await refreshUsers();
      toast.success('Managed user added');
    } catch (e) {
      toast.error(e.message || 'Failed to add');
    }
  };

  const onDeleteManaged = async (id) => {
    try {
      await deleteManagedUser(id);
      await refreshUsers();
      toast.success('Managed user deleted');
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    }
  };

  const onLookupUser = async () => {
    try {
      const res = await findUserByEmail(userLookupEmail.trim().toLowerCase());
      setLookupResult(res);
      if (!res) toast.error('User not found');
    } catch (e) {
      toast.error(e.message || 'Lookup failed');
    }
  };

  const onGrantAdmin = async () => {
    try {
      if (!lookupResult?.id) return;
      await grantAdminAccess(lookupResult.id);
      toast.success('Admin access granted');
    } catch (e) {
      toast.error(e.message || 'Grant failed');
    }
  };

  const onRevokeAdmin = async () => {
    try {
      if (!lookupResult?.id) return;
      await revokeAdminAccess(lookupResult.id);
      toast.success('Admin access revoked');
    } catch (e) {
      toast.error(e.message || 'Revoke failed');
    }
  };

  if (!user?.isAdmin) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-4 flex gap-2">
        <button onClick={() => setActiveTab('promos')} className={`px-3 py-2 rounded-md text-sm ${activeTab === 'promos' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Promo Codes</button>
        <button onClick={() => setActiveTab('users')} className={`px-3 py-2 rounded-md text-sm ${activeTab === 'users' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Users & Permissions</button>
        <button onClick={() => setActiveTab('landing')} className={`px-3 py-2 rounded-md text-sm ${activeTab === 'landing' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Landing Content</button>
        <button onClick={() => setActiveTab('analytics')} className={`px-3 py-2 rounded-md text-sm ${activeTab === 'analytics' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Analytics</button>
      </div>

      {activeTab === 'promos' && (
        <>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow border p-6">
              <h2 className="text-xl font-semibold mb-4">Create Promo Code</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-700">Code (optional)</label>
                  <input className="w-full border rounded-md px-3 py-2 text-sm" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm text-gray-700">Credits</label>
                  <input type="number" className="w-full border rounded-md px-3 py-2 text-sm" value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm text-gray-700">Total Uses (Count)</label>
                  <input type="number" className="w-full border rounded-md px-3 py-2 text-sm" value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm text-gray-700">Expires At (optional)</label>
                  <input type="datetime-local" className="w-full border rounded-md px-3 py-2 text-sm" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
                </div>
                <div className="flex items-center gap-2">
                  <input id="active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                  <label htmlFor="active" className="text-sm text-gray-700">Active</label>
                </div>
                <button onClick={onCreate} disabled={creating} className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50">
                  {creating ? 'Saving...' : 'Save Code'}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow border p-6">
              <h2 className="text-xl font-semibold mb-4">Grant Credits to User</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-700">User Email</label>
                  <input className="w-full border rounded-md px-3 py-2 text-sm" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} />
                </div>
                <div>
                  <label className="text-sm text-gray-700">Credits</label>
                  <input type="number" className="w-full border rounded-md px-3 py-2 text-sm" value={grantAmount} onChange={(e) => setGrantAmount(e.target.value)} />
                </div>
                <button onClick={onGrant} className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700">Grant</button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow border p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Promo Codes</h2>
              <button onClick={refresh} className="text-sm text-indigo-600 hover:text-indigo-800">Refresh</button>
            </div>
            {loadingList ? (
              <div className="text-sm text-gray-600">Loading...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600">
                      <th className="py-2 pr-4">Code</th>
                      <th className="py-2 pr-4">Credits</th>
                      <th className="py-2 pr-4">Active</th>
                      <th className="py-2 pr-4">Uses</th>
                      <th className="py-2 pr-4">Expires</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map(item => (
                      <tr key={item.id} className="border-t">
                        <td className="py-2 pr-4 font-mono">{item.code || item.id}</td>
                        <td className="py-2 pr-4">{item.credits}</td>
                        <td className="py-2 pr-4">{item.active ? 'Yes' : 'No'}</td>
                        <td className="py-2 pr-4">{(item.redemptionCount || 0)}{item.maxRedemptions > 0 ? ` / ${item.maxRedemptions}` : ' / ∞'}</td>
                        <td className="py-2 pr-4">{item.expiresAtMs ? new Date(item.expiresAtMs).toLocaleString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'users' && (
        <>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow border p-6">
              <h2 className="text-xl font-semibold mb-4">Create Managed User</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-700">Email</label>
                  <input className="w-full border rounded-md px-3 py-2 text-sm" value={newManagedEmail} onChange={(e) => setNewManagedEmail(e.target.value)} />
                </div>
                <div>
                  <label className="text-sm text-gray-700">Password</label>
                  <input type="password" className="w-full border rounded-md px-3 py-2 text-sm" value={newManagedPassword} onChange={(e) => setNewManagedPassword(e.target.value)} />
                </div>
                <div>
                  <label className="text-sm text-gray-700">Name (optional)</label>
                  <input className="w-full border rounded-md px-3 py-2 text-sm" value={newManagedName} onChange={(e) => setNewManagedName(e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-gray-700">Role</label>
                    <select className="w-full border rounded-md px-3 py-2 text-sm" value={newManagedRole} onChange={(e) => setNewManagedRole(e.target.value)}>
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Initial Credits</label>
                    <input type="number" className="w-full border rounded-md px-3 py-2 text-sm" value={newManagedCredits} onChange={(e) => setNewManagedCredits(e.target.value)} />
                  </div>

                </div>
                <button onClick={onCreateManaged} className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700">Create</button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow border p-6">
              <h2 className="text-xl font-semibold mb-4">Grant/Revoke Admin Access</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-700">Lookup by Email</label>
                  <div className="flex gap-2">
                    <input className="flex-1 border rounded-md px-3 py-2 text-sm" value={userLookupEmail} onChange={(e) => setUserLookupEmail(e.target.value)} />
                    <button onClick={onLookupUser} className="px-3 py-2 rounded-md bg-gray-100 text-gray-700">Find</button>
                  </div>
                </div>
                {lookupResult && (
                  <div className="p-3 border rounded">
                    <div className="text-sm">User: <span className="font-medium">{lookupResult.displayName || lookupResult.email}</span></div>
                    <div className="text-xs text-gray-600">ID: {lookupResult.id}</div>
                    <div className="flex gap-2 mt-2">
                      <button onClick={onGrantAdmin} className="px-3 py-2 rounded-md bg-green-600 text-white">Grant Admin</button>
                      <button onClick={onRevokeAdmin} className="px-3 py-2 rounded-md bg-red-600 text-white">Revoke Admin</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow border p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Your Managed Users</h2>
              <button onClick={refreshUsers} className="text-sm text-indigo-600 hover:text-indigo-800">Refresh</button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-600">
                    <th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {managed.map(mu => (
                    <tr key={mu.id} className="border-t">
                      <td className="py-2 pr-4">{mu.email}</td>
                      <td className="py-2 pr-4">{mu.displayName || '-'}</td>
                      <td className="py-2 pr-4">
                        <button onClick={() => onDeleteManaged(mu.id)} className="px-3 py-1 rounded-md bg-red-600 text-white">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeTab === 'landing' && (
        <>
          <div className="mb-4 flex gap-2">
            <button
              onClick={() => setLandingTab('content')}
              className={`px-3 py-2 rounded-md text-sm ${landingTab === 'content' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
            >
              Landing Content
            </button>
            <button
              onClick={() => setLandingTab('feedback')}
              className={`px-3 py-2 rounded-md text-sm ${landingTab === 'feedback' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
            >
              User Feedback
            </button>
          </div>

          {landingTab === 'content' && (
            <div className="bg-white rounded-lg shadow border p-6">
              <h2 className="text-xl font-semibold mb-4">
                {editingLanding ? 'Edit Landing Content' : 'Post Landing Content'}
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-700">Title</label>
                  <input className="w-full border rounded-md px-3 py-2 text-sm" value={landingTitle} onChange={(e) => setLandingTitle(e.target.value)} />
                </div>


                <div>
                  <label className="text-sm text-gray-700">Content Sections</label>
                  <div className="space-y-4">
                    {contentSections.map((section, index) => (
                      <div
                        key={index}
                        className={`border rounded-lg p-4 bg-gray-50 cursor-move transition-opacity ${draggedSection === index ? 'opacity-50' : ''
                          }`}
                        draggable={contentSections.length > 1}
                        onDragStart={(e) => handleSectionDragStart(e, index)}
                        onDragOver={handleSectionDragOver}
                        onDrop={(e) => handleSectionDrop(e, index)}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <span className="mr-2 text-gray-400">⋮⋮</span>
                            <span className="text-sm font-medium text-gray-700">Section {index + 1}</span>
                          </div>
                          {contentSections.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeContentSection(index)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="text-xs text-gray-600">Type</label>
                            <select
                              className="w-full border rounded-md px-2 py-1 text-sm"
                              value={section.type}
                              onChange={(e) => updateContentSection(index, 'type', e.target.value)}
                            >
                              <option value="text">Text</option>
                              <option value="image">Image</option>
                              <option value="image-text">Image + Text</option>
                              <option value="video">Video</option>
                              <option value="video-text">Video + Text</option>
                              <option value="link">Link</option>
                              <option value="link-text">Link + Text</option>
                            </select>
                          </div>
                        </div>

                        {section.type === 'text' && (
                          <div>
                            <label className="text-xs text-gray-600">Text Content</label>
                            <textarea
                              className="w-full border rounded-md px-3 py-2 text-sm"
                              rows={3}
                              value={section.content}
                              onChange={(e) => updateContentSection(index, 'content', e.target.value)}
                              placeholder="Enter text content..."
                            />
                          </div>
                        )}

                        {(section.type === 'image' || section.type === 'image-text') && (
                          <div className="mb-3">
                            <label className="text-xs text-gray-600">Upload Image</label>
                            <div
                              className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${dragActive ? 'border-indigo-400 bg-indigo-50' : 'border-gray-300'
                                }`}
                              onDragEnter={handleDrag}
                              onDragLeave={handleDrag}
                              onDragOver={handleDrag}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDragActive(false);
                                if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                                  const file = e.dataTransfer.files[0];
                                  if (file.type.startsWith('image/')) {
                                    handleSectionFileUpload(file, index);
                                  } else {
                                    toast.error('Please upload only image files');
                                  }
                                }
                              }}
                            >
                              {uploadingFile ? (
                                <div className="text-gray-600">Uploading...</div>
                              ) : (
                                <>
                                  <div className="text-gray-600 mb-2">
                                    Drag and drop an image here, or{' '}
                                    <label className="text-indigo-600 hover:text-indigo-800 cursor-pointer">
                                      browse
                                      <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                          const file = e.target.files[0];
                                          if (file) {
                                            handleSectionFileUpload(file, index);
                                          }
                                        }}
                                        className="hidden"
                                      />
                                    </label>
                                  </div>
                                  <div className="text-xs text-gray-500">Supports: JPG, PNG, GIF, SVG</div>
                                </>
                              )}
                            </div>
                            {section.mediaUrl && (
                              <div className="mt-2">
                                <img src={section.mediaUrl} alt="Preview" className="max-w-full h-24 object-cover rounded border" />
                                <button
                                  type="button"
                                  onClick={() => removeFileFromSection(index)}
                                  className="mt-1 text-xs text-red-600 hover:text-red-800"
                                >
                                  Remove Image
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {section.type === 'image-text' && (
                          <div>
                            <label className="text-xs text-gray-600">Text Content</label>
                            <textarea
                              className="w-full border rounded-md px-3 py-2 text-sm"
                              rows={3}
                              value={section.content}
                              onChange={(e) => updateContentSection(index, 'content', e.target.value)}
                              placeholder="Enter text content..."
                            />
                          </div>
                        )}

                        {(section.type === 'video' || section.type === 'video-text') && (
                          <div className="mb-3">
                            <label className="text-xs text-gray-600">Upload Video</label>
                            <div
                              className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${dragActive ? 'border-indigo-400 bg-indigo-50' : 'border-gray-300'
                                }`}
                              onDragEnter={handleDrag}
                              onDragLeave={handleDrag}
                              onDragOver={handleDrag}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDragActive(false);
                                if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                                  const file = e.dataTransfer.files[0];
                                  if (file.type.startsWith('video/')) {
                                    handleSectionVideoUpload(file, index);
                                  } else {
                                    toast.error('Please upload only video files');
                                  }
                                }
                              }}
                            >
                              {uploadingFile ? (
                                <div className="text-gray-600">Uploading...</div>
                              ) : (
                                <>
                                  <div className="text-gray-600 mb-2">
                                    Drag and drop a video here, or{' '}
                                    <label className="text-indigo-600 hover:text-indigo-800 cursor-pointer">
                                      browse
                                      <input
                                        type="file"
                                        accept="video/*"
                                        onChange={(e) => {
                                          const file = e.target.files[0];
                                          if (file) {
                                            handleSectionVideoUpload(file, index);
                                          }
                                        }}
                                        className="hidden"
                                      />
                                    </label>
                                  </div>
                                  <div className="text-xs text-gray-500">Supports: MP4, WebM, MOV</div>
                                </>
                              )}
                            </div>
                            {section.mediaUrl && (
                              <div className="mt-2">
                                <video controls className="max-w-full h-24 object-cover rounded border">
                                  <source src={section.mediaUrl} type="video/mp4" />
                                  Your browser does not support the video tag.
                                </video>
                                <button
                                  type="button"
                                  onClick={() => removeFileFromSection(index)}
                                  className="mt-1 text-xs text-red-600 hover:text-red-800"
                                >
                                  Remove Video
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {(section.type === 'link' || section.type === 'link-text') && (
                          <div className="mb-3">
                            <label className="text-xs text-gray-600">Link URL</label>
                            <input
                              type="url"
                              className="w-full border rounded-md px-3 py-2 text-sm"
                              value={section.mediaUrl || ''}
                              onChange={(e) => updateContentSection(index, 'mediaUrl', e.target.value)}
                              placeholder="https://example.com"
                            />
                            <div className="text-xs text-gray-500 mt-1">Enter a valid URL (e.g., https://example.com)</div>
                          </div>
                        )}

                        {(section.type === 'video-text' || section.type === 'link-text') && (
                          <div>
                            <label className="text-xs text-gray-600">Text Content</label>
                            <textarea
                              className="w-full border rounded-md px-3 py-2 text-sm"
                              rows={3}
                              value={section.content}
                              onChange={(e) => updateContentSection(index, 'content', e.target.value)}
                              placeholder="Enter text content..."
                            />
                          </div>
                        )}
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={addContentSection}
                      className="w-full border-2 border-dashed border-gray-300 rounded-lg p-3 text-gray-600 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
                    >
                      + Add Content Section
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={async () => {
                    try {
                      // Upload any pending files first
                      const sectionsWithUploadedFiles = await uploadPendingFiles(contentSections);

                      const contentData = {
                        title: landingTitle,
                        type: 'text',
                        sections: sectionsWithUploadedFiles,
                        order: editingLanding ? editingLanding.order : landingItems.length + 1
                      };

                      if (editingLanding) {
                        await updateLandingContent(editingLandingId, contentData);
                        setEditingLanding(null);
                        setEditingLandingId(null);
                        toast.success('Content updated');
                      } else {
                        await createLandingContent(contentData);
                        toast.success('Content posted');
                      }

                      setLandingTitle('');
                      setContentSections([{ type: 'text', content: '', mediaUrl: '', pendingFile: null }]);
                      await refreshLanding();
                    } catch (e) {
                      toast.error(e.message || 'Failed to post');
                    }
                  }}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                >
                  {editingLanding ? 'Update' : 'Post'}
                </button>
                {editingLanding && (
                  <button
                    onClick={() => {
                      // Clean up any object URLs before resetting
                      contentSections.forEach(section => {
                        if (section.mediaUrl && section.pendingFile) {
                          URL.revokeObjectURL(section.mediaUrl);
                        }
                      });

                      setEditingLanding(null);
                      setEditingLandingId(null);
                      setLandingTitle('');
                      setContentSections([{ type: 'text', content: '', mediaUrl: '', pendingFile: null }]);
                    }}
                    className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                  >
                    Cancel Edit
                  </button>
                )}
              </div>
            </div>

          )}

          {landingTab === 'feedback' && (
            <>
              <div className="bg-white rounded-lg shadow border p-6">
                <h2 className="text-xl font-semibold mb-4">Post User Feedback (Admin)</h2>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-gray-700">Feedback Text</label>
                    <textarea className="w-full border rounded-md px-3 py-2 text-sm" rows={4} value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Author (optional)</label>
                    <input className="w-full border rounded-md px-3 py-2 text-sm" value={feedbackAuthor} onChange={(e) => setFeedbackAuthor(e.target.value)} />
                  </div>
                  <div>
                    <label className="text-sm text-gray-700">Rating</label>
                    <div className="flex items-center space-x-2 mt-1">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setFeedbackRating(star)}
                          className={`text-2xl ${Math.floor(feedbackRating) >= star ? 'text-yellow-400' : 'text-gray-300'} hover:text-yellow-400 transition-colors`}
                        >
                          ★
                        </button>
                      ))}
                      <span className="text-sm text-gray-600 ml-2">{feedbackRating > 0 ? feedbackRating.toFixed(1) : '0'}/5</span>
                    </div>
                    <div className="mt-2">
                      <label className="text-sm text-gray-700">Precise Rating (0 - 5.0)</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          max="5.0"
                          step="0.1"
                          value={feedbackRating || ''}
                          onChange={(e) => {
                            const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                            if (value >= 0 && value <= 5.0) {
                              setFeedbackRating(value);
                            }
                          }}
                          className="flex-1 border rounded-md px-3 py-2 text-sm"
                          placeholder="e.g., 4.8"
                        />
                        <button
                          type="button"
                          onClick={() => setFeedbackRating(0)}
                          className="px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        if (!feedbackText.trim()) { toast.error('Feedback text required'); return; }
                        await createFeedback({ text: feedbackText.trim(), author: feedbackAuthor.trim(), rating: feedbackRating, approved: true });
                        setFeedbackText(''); setFeedbackAuthor(''); setFeedbackRating(0);
                        await refreshLanding();
                        toast.success('Feedback posted');
                      } catch (e) { toast.error(e.message || 'Failed to post'); }
                    }}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                  >Post Feedback</button>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow border p-6 mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold">All Feedback</h2>
                  <button onClick={refreshLanding} className="text-sm text-indigo-600 hover:text-indigo-800">Refresh</button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-600">
                        <th className="py-2 pr-4">Text</th>
                        <th className="py-2 pr-4">Author</th>
                        <th className="py-2 pr-4">Rating</th>
                        <th className="py-2 pr-4">Approved</th>
                        <th className="py-2 pr-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allFeedback.map(it => (
                        <tr key={it.id} className="border-t">
                          <td className="py-2 pr-4 max-w-md truncate" title={it.text || it.body}>{it.text || it.body}</td>
                          <td className="py-2 pr-4">{it.author || '-'}</td>
                          <td className="py-2 pr-4">
                            <div className="flex items-center">
                              <div className="flex text-yellow-400">
                                {[1, 2, 3, 4, 5].map(star => {
                                  const rating = it.rating || 0;
                                  const diff = rating - star;
                                  if (diff >= 1) return <span key={star} className="text-yellow-400">★</span>;
                                  if (diff > 0) return <span key={star} className="text-yellow-400">★</span>; // Full star for now, could add half-star logic
                                  return <span key={star} className="text-gray-300">★</span>;
                                })}
                              </div>
                              <span className="text-sm text-gray-600 ml-1">({(it.rating || 0) > 0 ? (it.rating || 0).toFixed(1) : '0'}/5)</span>
                            </div>
                          </td>
                          <td className="py-2 pr-4">{it.approved ? 'Yes' : 'No'}</td>
                          <td className="py-2 pr-4 space-x-2">
                            <button onClick={() => {
                              setEditingFeedback(it);
                              setEditFeedbackText(it.text || it.body || '');
                              setEditFeedbackAuthor(it.author || '');
                              setEditFeedbackRating(it.rating || 0);
                            }} className="px-3 py-1 rounded-md bg-blue-600 text-white">Edit</button>
                            <button onClick={async () => { try { await approveFeedback(it.id, !it.approved); await refreshLanding(); } catch (e) { toast.error(e.message || 'Failed'); } }} className="px-3 py-1 rounded-md bg-gray-100 text-gray-700">
                              {it.approved ? 'Unapprove' : 'Approve'}
                            </button>
                            <button onClick={async () => { try { await deleteFeedback(it.id); await refreshLanding(); } catch (e) { toast.error(e.message || 'Failed'); } }} className="px-3 py-1 rounded-md bg-red-600 text-white">Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Edit Feedback Modal */}
              {editingFeedback && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                    <h3 className="text-lg font-semibold mb-4">Edit Feedback</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm text-gray-700">Feedback Text</label>
                        <textarea className="w-full border rounded-md px-3 py-2 text-sm" rows={4} value={editFeedbackText} onChange={(e) => setEditFeedbackText(e.target.value)} />
                      </div>
                      <div>
                        <label className="text-sm text-gray-700">Author</label>
                        <input className="w-full border rounded-md px-3 py-2 text-sm" value={editFeedbackAuthor} onChange={(e) => setEditFeedbackAuthor(e.target.value)} />
                      </div>
                      <div>
                        <label className="text-sm text-gray-700">Rating</label>
                        <div className="flex items-center space-x-2 mt-1">
                          {[1, 2, 3, 4, 5].map(star => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setEditFeedbackRating(star)}
                              className={`text-2xl ${Math.floor(editFeedbackRating) >= star ? 'text-yellow-400' : 'text-gray-300'} hover:text-yellow-400 transition-colors`}
                            >
                              ★
                            </button>
                          ))}
                          <span className="text-sm text-gray-600 ml-2">{editFeedbackRating > 0 ? editFeedbackRating.toFixed(1) : '0'}/5</span>
                        </div>
                        <div className="mt-2">
                          <label className="text-sm text-gray-700">Precise Rating (0 - 5.0)</label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              min="0"
                              max="5.0"
                              step="0.1"
                              value={editFeedbackRating || ''}
                              onChange={(e) => {
                                const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                if (value >= 0 && value <= 5.0) {
                                  setEditFeedbackRating(value);
                                }
                              }}
                              className="flex-1 border rounded-md px-3 py-2 text-sm"
                              placeholder="e.g., 4.8"
                            />
                            <button
                              type="button"
                              onClick={() => setEditFeedbackRating(0)}
                              className="px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                            >
                              Clear
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-3 pt-3">
                        <button
                          onClick={async () => {
                            try {
                              if (!editFeedbackText.trim()) { toast.error('Feedback text required'); return; }
                              await updateFeedback(editingFeedback.id, {
                                text: editFeedbackText.trim(),
                                author: editFeedbackAuthor.trim(),
                                rating: editFeedbackRating
                              });
                              setEditingFeedback(null);
                              await refreshLanding();
                              toast.success('Feedback updated');
                            } catch (e) { toast.error(e.message || 'Failed to update'); }
                          }}
                          className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                        >
                          Update
                        </button>
                        <button
                          onClick={() => setEditingFeedback(null)}
                          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="bg-white rounded-lg shadow border p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Landing Content</h2>
              <button onClick={refreshLanding} className="text-sm text-indigo-600 hover:text-indigo-800">Refresh</button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-600">
                    <th className="py-2 pr-4">Title</th>
                    <th className="py-2 pr-4">Type</th>
                    <th className="py-2 pr-4">Order</th>
                    <th className="py-2 pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {landingItems.map(it => (
                    <tr
                      key={it.id}
                      className={`border-t cursor-move ${draggedItem?.id === it.id ? 'opacity-50' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, it)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleLandingDrop(e, it)}
                    >
                      <td className="py-2 pr-4 flex items-center">
                        <span className="mr-2 text-gray-400">⋮⋮</span>
                        {it.title}
                      </td>
                      <td className="py-2 pr-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${it.type === 'text' ? 'bg-blue-100 text-blue-800' :
                          it.type === 'image' ? 'bg-green-100 text-green-800' :
                            it.type === 'video' ? 'bg-purple-100 text-purple-800' :
                              it.type === 'video-embed' ? 'bg-orange-100 text-orange-800' :
                                'bg-gray-100 text-gray-800'
                          }`}>
                          {it.type === 'video-embed' ? 'Video (Embed)' : it.type.charAt(0).toUpperCase() + it.type.slice(1)}
                        </span>
                      </td>
                      <td className="py-2 pr-4">{it.order}</td>
                      <td className="py-2 pr-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setEditingLanding(it);
                              setEditingLandingId(it.id);
                              setLandingTitle(it.title);
                              // Add pendingFile field to existing sections
                              const sectionsWithPendingFile = (it.sections || [{ type: 'text', content: '', mediaUrl: '' }]).map(section => ({
                                ...section,
                                pendingFile: null
                              }));
                              setContentSections(sectionsWithPendingFile);
                            }}
                            className="px-3 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                          >
                            Edit
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                await deleteLandingContent(it.id);
                                await refreshLanding();
                                toast.success('Deleted');
                              } catch (e) {
                                toast.error(e.message || 'Failed');
                              }
                            }}
                            className="px-3 py-1 rounded-md bg-red-600 text-white hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>




        </>
      )}

      {activeTab === 'analytics' && (
        <>


          <div className="mb-4 flex gap-2 border-b border-gray-200 pb-2">
            <button onClick={() => setAnalyticsTab('overview')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${analyticsTab === 'overview' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'}`}>Overview Breakdown</button>
            <button onClick={() => setAnalyticsTab('jobs')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${analyticsTab === 'jobs' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'}`}>User Analysis</button>
            <button onClick={() => setAnalyticsTab('ref')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${analyticsTab === 'ref' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'}`}>Top Referrers</button>
          </div>

          {analyticsTab === 'overview' && (
            <div className="bg-white rounded-lg shadow border p-6">
              <h2 className="text-xl font-semibold mb-6">Processing Breakdown</h2>
              {analyticsLoading ? (
                <div className="text-center py-8 text-gray-500">Loading analytics...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-gray-600 border-b">
                        <th className="py-3 px-4 font-semibold">Job Type</th>
                        <th className="py-3 px-4 font-semibold text-right">Count</th>
                        <th className="py-3 px-4 font-semibold text-right">Usage Share</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {Object.entries(globalAnalytics.jobTypeBreakdown)
                        .sort(([, a], [, b]) => b - a)
                        .map(([type, count]) => {
                          const total = Object.values(globalAnalytics.jobTypeBreakdown).reduce((a, b) => a + b, 0);
                          const percentage = total ? ((count / total) * 100).toFixed(1) : 0;
                          return (
                            <tr key={type} className="hover:bg-gray-50">
                              <td className="py-3 px-4 font-medium text-gray-800">
                                {type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                              </td>
                              <td className="py-3 px-4 text-right">{count}</td>
                              <td className="py-3 px-4 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <span>{percentage}%</span>
                                  <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${percentage}%` }}></div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      {Object.keys(globalAnalytics.jobTypeBreakdown).length === 0 && (
                        <tr><td colSpan="3" className="py-8 text-center text-gray-500">No job data available</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {analyticsTab === 'jobs' && (
            <div className="bg-white rounded-lg shadow border p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                <h2 className="text-xl font-semibold">User Activity Analysis</h2>
                <div className="flex gap-2">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search users..."
                      className="pl-9 pr-4 py-2 border rounded-md text-sm w-64 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && setAnalyticsTab('jobs')} // Search works via effect on term change/debouncing or explicit refresh? 
                    // Actually, effect triggers on refresh click or tab change. 
                    // Let's rely on the Refresh button for now or add a debounce effect. 
                    // For simplicity, we'll keep the Refresh button as the main trigger or add a specific search button.
                    />
                    <div className="absolute left-3 top-2.5 text-gray-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setUserCurrentPage(1);
                      // refresh triggered by dependencies or button
                      // We need to ensure logic uses current state.
                      (async () => {
                        try {
                          setJobsLoading(true);
                          const data = await getTopUsersByJobs(jobsLimit, 1, userSearchTerm);
                          setTopJobs(data.users);
                          setUserPagination({ totalUsers: data.totalUsers, totalPages: data.totalPages });
                        } catch (e) { toast.error(e.message || 'Failed'); } finally { setJobsLoading(false); }
                      })();
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700"
                  >
                    Search
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto min-h-[200px]">
                {jobsLoading ? (
                  <div className="flex items-center justify-center h-48 text-gray-500">
                    <svg className="animate-spin h-6 w-6 mr-2" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Loading user data...
                  </div>
                ) : (
                  <>
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left text-gray-600 border-b">
                          <th className="py-3 px-4 font-semibold">User</th>
                          <th className="py-3 px-4 font-semibold">Email</th>
                          <th className="py-3 px-4 font-semibold text-right">Jobs Run</th>
                          <th className="py-3 px-4 font-semibold text-left">Job Breakdown</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {topJobs.map(row => (
                          <tr key={row.uid} className="hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium">{row.name || 'Unknown User'}</td>
                            <td className="py-3 px-4 text-gray-600">{row.email}</td>
                            <td className="py-3 px-4 text-right">{row.count}</td>
                            <td className="py-3 px-4 text-right">{row.count}</td>
                            <td className="py-3 px-4 text-left">
                              <div className="flex flex-wrap gap-1">
                                {Object.entries(row.jobTypeBreakdown || {}).map(([type, count]) => (
                                  <span key={type} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                    {type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}: {count}
                                  </span>
                                ))}
                                {(!row.jobTypeBreakdown || Object.keys(row.jobTypeBreakdown).length === 0) && (
                                  <span className="text-gray-400 text-xs">-</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {topJobs.length === 0 && (
                          <tr><td colSpan="4" className="py-8 text-center text-gray-500">No users found</td></tr>
                        )}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    {userPagination.totalPages > 1 && (
                      <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
                        <div className="text-sm text-gray-500">
                          Showing {((userCurrentPage - 1) * jobsLimit) + 1} to {Math.min(userCurrentPage * jobsLimit, userPagination.totalUsers)} of {userPagination.totalUsers} users
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              const newPage = Math.max(1, userCurrentPage - 1);
                              setUserCurrentPage(newPage);
                              (async () => {
                                try {
                                  setJobsLoading(true);
                                  const data = await getTopUsersByJobs(jobsLimit, newPage, userSearchTerm);
                                  setTopJobs(data.users);
                                  setUserPagination({ totalUsers: data.totalUsers, totalPages: data.totalPages });
                                } catch (e) { } finally { setJobsLoading(false); }
                              })();
                            }}
                            disabled={userCurrentPage === 1}
                            className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            Previous
                          </button>
                          <div className="flex items-center gap-1 px-2">
                            {Array.from({ length: Math.min(5, userPagination.totalPages) }, (_, i) => {
                              // Logic to show generic page window around current page could be added, simple slice for now
                              let p = i + 1;
                              if (userPagination.totalPages > 5 && userCurrentPage > 3) {
                                p = userCurrentPage - 2 + i;
                              }
                              if (p > userPagination.totalPages) return null;

                              return (
                                <button
                                  key={p}
                                  onClick={() => {
                                    setUserCurrentPage(p);
                                    (async () => {
                                      try {
                                        setJobsLoading(true);
                                        const data = await getTopUsersByJobs(jobsLimit, p, userSearchTerm);
                                        setTopJobs(data.users);
                                        setUserPagination({ totalUsers: data.totalUsers, totalPages: data.totalPages });
                                      } catch (e) { } finally { setJobsLoading(false); }
                                    })();
                                  }}
                                  className={`w-8 h-8 rounded-md text-sm flex items-center justify-center ${userCurrentPage === p ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100'}`}
                                >
                                  {p}
                                </button>
                              );
                            })}
                          </div>
                          <button
                            onClick={() => {
                              const newPage = Math.min(userPagination.totalPages, userCurrentPage + 1);
                              setUserCurrentPage(newPage);
                              (async () => {
                                try {
                                  setJobsLoading(true);
                                  const data = await getTopUsersByJobs(jobsLimit, newPage, userSearchTerm);
                                  setTopJobs(data.users);
                                  setUserPagination({ totalUsers: data.totalUsers, totalPages: data.totalPages });
                                } catch (e) { } finally { setJobsLoading(false); }
                              })();
                            }}
                            disabled={userCurrentPage === userPagination.totalPages}
                            className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {analyticsTab === 'ref' && (
            <div className="bg-white rounded-lg shadow border p-6">
              <h2 className="text-xl font-semibold mb-4">Top Referrers</h2>
              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm text-gray-500">Showing top {topRef.length}</div>
                <button onClick={async () => { try { const data = await getTopReferrers(refLimit); setTopRef(data); } catch (e) { toast.error(e.message || 'Failed'); } }} className="text-sm text-indigo-600 hover:text-indigo-800">Refresh</button>
              </div>
              <div className="overflow-x-auto min-h-[120px] flex items-center justify-center" style={{ minHeight: '120px' }}>
                {refLoading ? (
                  <div className="text-sm text-gray-500">Loading top referrers...</div>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-600">
                        <th className="py-2 pr-4">Referrer</th>
                        <th className="py-2 pr-4">Email</th>
                        <th className="py-2 pr-4">Referrals</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topRef.map(row => (
                        <tr key={row.refId} className="border-t">
                          <td className="py-2 pr-4">{row.name || row.refId}</td>
                          <td className="py-2 pr-4">{row.email}</td>
                          <td className="py-2 pr-4">{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="mt-4 flex justify-center">
                <button onClick={() => setRefLimit(refLimit + 10)} className="text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-md">Show more</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Refund Policy Page
function RefundPolicyPage() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow border p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Refund Policy</h1>
        <div className="prose prose-lg max-w-none space-y-6">
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Credit Refund Policy</h2>
            <p className="text-gray-600">
              Credits purchased on FileZen are generally non-refundable. However, we understand that exceptional
              circumstances may arise, and we will consider refund requests on a case-by-case basis.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Eligible Refund Scenarios</h2>
            <p className="text-gray-600">
              Refunds may be considered in the following situations:
            </p>
            <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
              <li>Technical errors that prevented proper service delivery</li>
              <li>Duplicate charges due to payment processing errors</li>
              <li>Service outages that significantly impacted your ability to use purchased credits</li>
              <li>Compliance with applicable consumer protection laws</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Refund Request Process</h2>
            <p className="text-gray-600">
              To request a refund, please contact our support team at filezen@smartbusinessai.cloud within 30 days
              of your purchase. Include your transaction ID, reason for refund, and any relevant details.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Processing Time</h2>
            <p className="text-gray-600">
              Approved refunds will be processed within 5-7 business days and credited back to your original
              payment method. Processing time may vary depending on your bank or payment provider.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Partial Refunds</h2>
            <p className="text-gray-600">
              If you have used some of your purchased credits, we may offer a partial refund based on
              the unused portion of your credit balance.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Contact Information</h2>
            <p className="text-gray-600">
              For refund requests or questions about this policy, please contact us at filezen@smartbusinessai.cloud
              or through our contact page.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

// Cookie Policy Page
function CookiePolicyPage() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow border p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Cookie Policy</h1>
        <div className="prose prose-lg max-w-none space-y-6">
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. What Are Cookies</h2>
            <p className="text-gray-600">
              Cookies are small text files that are stored on your device when you visit our website.
              They help us provide you with a better experience by remembering your preferences and
              improving our services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Types of Cookies We Use</h2>

            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-800">Essential Cookies</h3>
                <p className="text-gray-600 text-sm">
                  These cookies are necessary for the website to function properly. They enable core
                  functionality such as user authentication, security, and accessibility.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800">Performance Cookies</h3>
                <p className="text-gray-600 text-sm">
                  These cookies collect information about how visitors use our website, such as which
                  pages are visited most often. This data helps us improve our website performance.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800">Functional Cookies</h3>
                <p className="text-gray-600 text-sm">
                  These cookies remember choices you make to improve your experience, such as your
                  language preference or login status.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Third-Party Cookies</h2>
            <p className="text-gray-600">
              We may use third-party services that set cookies on our website, including:
            </p>
            <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
              <li>Payment processors (Stripe, Razorpay) for secure transactions</li>
              <li>Analytics services to understand website usage</li>
              <li>AI services for document processing</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Managing Cookies</h2>
            <p className="text-gray-600">
              You can control and manage cookies through your browser settings. Most browsers allow you to:
            </p>
            <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
              <li>View and delete cookies</li>
              <li>Block cookies from specific websites</li>
              <li>Block all cookies</li>
              <li>Set preferences for different types of cookies</li>
            </ul>
            <p className="text-gray-600 mt-2">
              Please note that disabling certain cookies may affect the functionality of our website.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Updates to This Policy</h2>
            <p className="text-gray-600">
              We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated revision date.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Contact Us</h2>
            <p className="text-gray-600">
              If you have any questions about our use of cookies, please contact us at filezen@smartbusinessai.cloud.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

// Not Found Page
function NotFoundPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 py-12">
      <div className="text-center">
        <h1 className="text-5xl font-bold text-indigo-600 mb-4">404</h1>
        <h2 className="text-3xl font-semibold text-gray-900 mb-6">Page Not Found</h2>
        <p className="text-lg text-gray-600 mb-8">
          Sorry, the page you are looking for doesn't exist or has been moved.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-white text-indigo-600 border border-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"
          >
            <span className="flex items-center justify-center">
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Go Back
            </span>
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <span className="flex items-center justify-center">
              <HomeIcon className="h-5 w-5 mr-2" />
              Go to Dashboard
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;